import * as THREE from 'three';
import {
  CameraController,
  DISTANCE as CAMERA_DISTANCE,
  PITCH as CAMERA_PITCH,
} from './game/camera/CameraController';
import { Daylight, type SkyState } from './game/sky/Daylight';
import { SunLight } from './game/sky/SunLight';
import { createWaterMaterial } from './game/map/WaterSurface';
import { createRiverMaterial } from './game/map/RiverSurface';
import { createDepositMaterial } from './game/map/DepositMarks';
import { createBridgeMaterial, createDigMaterial, createRoadMaterial, deckY } from './game/map/RoadSurface';
import { RoadWorks, type WorkSite } from './game/map/RoadWorks';
import { applyGrassDetail } from './game/map/GrassDetail';
import { createBlockedMaterial } from './game/debug/BlockedOverlay';
import { buildBridge, buildRoad, loadRoads, type RoadCell } from './network/Roads';
import { rest, wake } from './network/Rest';
import { fellTrees, loadFelled, stopFelling } from './network/Felling';
import {
  cancelShipment,
  loadBoard,
  loadConvoys,
  orderShipment,
} from './network/Haulage';
import { CORNERS, HEX_SIZE, elevationToY, setBiomePalette } from './game/map/HexMetrics';
import { ChunkStreamer } from './game/map/ChunkStreamer';
import { loadProps, type Props } from './game/props/PropModels';
import { FellingTrees } from './game/props/FellingTrees';
import { bannerClock, bannerTexture } from './game/banners/BannerCloth';
import { loadStaff, raiseBanner, type Staff } from './game/banners/BannerStaff';
import { loadBanner, loadBannerSpec, loadMyBanner } from './network/Banners';
import type { Anchors } from './game/buildings/Anchors';
import { loadBridgeModel, spanMatrix, type BridgeModel } from './game/map/BridgeModel';
import { loadAnchors, loadCompositions } from './game/buildings/Anchors';
import { loadBuildingModels } from './game/buildings/BuildingModels';
import { adoptModels } from './game/buildings/BuildingShapes';
import { WorldSource } from './game/map/WorldSource';
import { StatsHud, type Cell } from './game/debug/StatsHud';
import { HexHighlight } from './game/interaction/HexHighlight';
import { HexPicker } from './game/interaction/HexPicker';
import {
  Axial,
  axialKey,
  axialToWorld,
  distance,
  neighbor,
  roundAxial,
  worldToAxial,
} from './game/map/HexCoordinates';
import { HexCellData, isWater, waterLevel } from './game/map/HexSurface';
import {
  MAX_STEP,
  crossingSeconds,
  findRoute,
  habitable,
  hexDistance,
  levelOf,
  mirrorOf,
} from './game/map/Movement';
import { Connection, type Holding, type Mark, type Presence } from './network/Connection';
import { forget, logOut, type Joined, type Session } from './network/Session';
import { PlayerMarkers } from './game/players/PlayerMarkers';
import { loadPawnModel } from './game/players/PawnModel';
import { InteractionPanel } from './ui/InteractionPanel';
import { Fact, SpotPanel } from './ui/SpotPanel';
import { StorePanel, type StoreTab } from './ui/StorePanel';
import { SideSheet, type SidePlace } from './ui/SideSheet';
import { ConvoyMarkers } from './game/convoys/ConvoyMarkers';
import { ShipMarkers } from './game/ships/ShipMarkers';
import { boardShip, buildShip, leaveShip, loadShips } from './network/Ships';
import { RouteLine } from './game/players/RouteLine';
import { TargetMark } from './game/players/TargetMark';
import { NameTags } from './ui/NameTags';
import { StatusBar } from './ui/StatusBar';
import { HaulPanel } from './ui/HaulPanel';
import { noteForPlayerPage } from './ui/PlayerPage';
import { Compass } from './ui/Compass';
import { BiomeInfo } from './game/map/WorldSource';
import { BuildPanel } from './ui/BuildPanel';
import { HudBar } from './ui/HudBar';
import { PurseBar } from './ui/PurseBar';
import { BuildProgress } from './ui/BuildProgress';
import { BuildingTooltip, countdown, madeSince, withMade } from './ui/BuildingTooltip';
import { YieldPops } from './ui/YieldPops';
import { BuildingMenu, MenuEntry } from './ui/BuildingMenu';
import { screenPlacer } from './ui/ScreenPoint';
import { Credit, GameMenu } from './ui/GameMenu';
import { Clouds, applyCloudShadow, createCloudUniforms } from './game/sky/Clouds';
import { Rain } from './game/sky/Rain';
import { Smoke } from './game/buildings/Smoke';
import { Hearths, LIT } from './game/buildings/Hearths';
import { Weather } from './game/sky/Weather';
import { storedVolumes } from './ui/SoundSettings';
import { cloudsWanted } from './ui/ViewSettings';
import { snapWanted } from './ui/CameraSettings';
import { AudioEngine } from './game/audio/AudioEngine';
import { AudioManifest, loadAudioManifest } from './game/audio/Manifest';
import { nearness } from './game/audio/Nearness';
import { hideLoader } from './ui/Loader';
import { PathTracer } from './game/debug/pathtrace/PathTracer';
import {
  BuildingKind,
  Catalogue,
  loadCatalogue,
  missingFor,
  nameOf,
  unmet,
} from './game/buildings/Catalogue';
import { Materials, countOf, formOf, iconOf, labelOf, loadMaterials } from './game/buildings/Materials';
import { loadObjects } from './game/buildings/Objects';
import { BuildingField, turnFor } from './game/buildings/BuildingField';
import { BuildGhost } from './game/buildings/BuildGhost';
import {
  Placed,
  harvestBuilding,
  loadPlaced,
  placeBuilding,
  renameBuilding,
  stockBuilding,
  withdrawBuilding,
  upgradeBuilding,
} from './game/buildings/Placed';
import {
  Refusal,
  Standing,
  Vocabulary,
  checkSite,
  footprint,
  inBuildReach,
  inCollectReach,
  hintFor,
  waterFacing,
} from './game/buildings/Site';

/**
 * Where the terrain comes from: the game server, a neighbourhood of chunks at a
 * time. The client no longer holds a world — it holds what is on screen plus a
 * margin, which is what makes a world larger than a thousand chunks possible.
 */
/**
 * Where the server is.
 *
 * **Relative by default**, because production serves the app and proxies the
 * API from the same origin: a same-origin request has no preflight and no CORS
 * to get wrong, which is a class of failure this project has already paid for
 * once. `VITE_API_URL` stays as an override for pointing a dev build at a
 * server somewhere else.
 */
const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Ile liter zmieści nazwa osady. Bliźniak `names::MAX_LETTERS`.
 *
 * Tu po to, żeby pole tekstowe **nie pozwoliło** wpisać nazwy, którą serwer
 * i tak odrzuci — a nie po to, żeby o niej rozstrzygać. Rozstrzyga serwer, jak
 * zawsze; to jest uprzejmość wobec palców.
 */
const NAME_LETTERS = 24;

/**
 * Debug overlays are built per chunk as it is meshed.
 *
 * They used to be skipped above a chunk count, because the client held the
 * whole world and building them for a thousand chunks measured the debug tool
 * instead of the terrain. Streaming keeps the drawn set to a few dozen, so the
 * limit has nothing left to protect against — `?overlays=off` remains for
 * measuring the terrain build on its own.
 */
function overlaysEnabled(): boolean {
  return new URLSearchParams(window.location.search).get('overlays') !== 'off';
}

/**
 * Depth range of the orthographic view box.
 *
 * `near` is deliberately negative. An orthographic camera's position only fixes
 * where the view box sits — it plays no part in how large anything looks, which
 * is the zoom's job — so CameraController parks the camera a mere 35 units from
 * its focus. Zoom out far enough and the visible strip of ground grows longer
 * than that, putting the near half of it physically behind the camera, where a
 * positive near plane slices it off. Letting the box extend backwards removes
 * the limit without moving the camera, which matters because moving it would
 * push the whole scene past the fog range.
 *
 * The range is generous on purpose: orthographic depth is linear, so widening
 * it costs precision evenly and there is plenty to spare.
 */
const CAMERA_NEAR = -500;
const CAMERA_FAR = 1000;

/**
 * How many physical screen pixels one game pixel covers.
 *
 * The render buffer is sized from the window rather than fixed at 480p, which
 * is what lets this stay a whole number: every game pixel then lands on exactly
 * the same square of screen. A fixed buffer forces a fractional upscale — 854
 * pixels across a 3024-pixel-wide display is 3.54 — and nearest-neighbour
 * spreads that over blocks of three and four in an uneven pattern. On an
 * isometric view, where almost every silhouette runs diagonally, that unevenness
 * is what reads as ragged edges.
 */
/**
 * Where the darkness begins and ends, as a share of the loaded radius.
 *
 * It starts well inside the last ring so the edge is never a visible line, and
 * is complete by the time the ground ends. The far number is the cosine of the
 * camera's pitch, not one: fog is measured along the line of sight, and a point
 * that is `edge` units away across the map is only 0,82 of that further from
 * the camera. Setting it to one left the last ring at 42% of the fog and the
 * terrain visibly stopping in daylight.
 */
const FOG_NEAR_EDGE = 0.55;
const FOG_FAR_EDGE = 0.82;

/**
 * How long the fog takes to part once the world is ready.
 *
 * Long enough to read as weather lifting rather than as a curtain being pulled,
 * short enough that nobody waits for it after having already waited for the
 * terrain.
 */
const REVEAL_SECONDS = 1.6;

/**
 * How long to stay behind the fog before giving up on the terrain.
 *
 * A fog that never lifts is a black screen with no explanation, and the terrain
 * can fail to arrive for reasons this side cannot fix — no world on the server,
 * a request that never comes back. After this it parts anyway and whatever is
 * there is what gets shown.
 */
const REVEAL_TIMEOUT_SECONDS = 8;

/** How far the fog has to have parted before the interface joins it. */
const HUD_AT_REVEAL = 0.55;

/** Eased at both ends, so the fog neither snaps open nor crawls at the finish. */
const smoothstep = (t: number) => t * t * (3 - 2 * t);

/** How much of the sky's colour survives in that darkness. */
const EDGE_SKY = 0.12;

/**
 * Zooms between which the sea's waves fade out.
 *
 * At 1,0 a hex is 31 game pixels across and a crest is a few of them; at 0,5 it
 * is 15 and the crests are down to a pixel apiece, which is where a moving
 * pattern turns into noise.
 */
const WAVES_FULL = 1.0;
const WAVES_GONE = 0.5;

const PIXEL_SCALE = 2;

/**
 * Buffer pixels per world unit at zoom 1.
 *
 * This is the knob for how coarse the picture looks, and it is what stays fixed
 * across window sizes: the frustum is derived from the buffer instead of being
 * a constant, so a hex is always the same number of pixels across and a larger
 * window shows more world rather than the same world in finer pixels.
 *
 * **24 with a scale of 2, where it used to be 16 with a scale of 3.** The two
 * move together and their product is unchanged, so a hex is the same size on
 * the glass and the frame holds the same amount of world at the same zoom —
 * what changes is that the hex is drawn with half as many pixels again. Zoomed
 * in, blocks of three physical pixels were coarse enough to read as blur rather
 * than as pixel art. The buffer costs 2,25 times as many pixels for it.
 */
const PIXELS_PER_UNIT = 24;

/**
 * How see-through the water goes when the sea is switched to look-underneath
 * mode. Enough blue left to read where the coastline is, enough gone to see
 * the shape of the seabed under it.
 */
const WATER_SEE_THROUGH_OPACITY = 0.35;

/** How many chunks to load. The file stores them nearest-first. */
/**
 * `?radius=N` pins how many rings of chunks are drawn, instead of letting the
 * zoom decide. The successor to `?chunks=N`: same purpose — bound the work for
 * a measurement — expressed in the unit that now controls it.
 */
function requestedDrawRadius(): number | null {
  const raw = new URLSearchParams(window.location.search).get('radius');
  if (raw === null) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * Main Game class
 * Manages Three.js renderer, scene, camera, and game loop
 */
/** Green where a building would go up, red where it would not. */
const BUILD_OK = 0x5fd47a;
const BUILD_NO = 0xff2d2d;

/**
 * Wycinka: ile trwa i ile daje jeden poziom zasobności.
 *
 * **Bliźniaki `SECONDS_PER_LEVEL` i `LOGS_PER_LEVEL` w `felling.rs`.** Tu są
 * tylko po to, żeby menu mogło powiedzieć, co się stanie; o tym, co się stanie,
 * rozstrzyga serwer i to jego liczby przychodzą w odpowiedzi.
 */
const FELL_SECONDS_PER_LEVEL = 30;
const FELL_LOGS_PER_LEVEL = 10;
/** Ile czekać po terminie wycinki na `treesFelled`, zanim licznik zgaśnie. */
const FELL_GRACE = 5000;
/** Z ilu pól jeszcze słychać topór. */
const CHOP_HEARING = 10;

/** Odległość dwóch pól w krokach. */
function cellDistance(a: Axial, b: Axial): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

export class Game {
  private container: HTMLElement;
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private cameraController!: CameraController;
  private gridOverlay = new THREE.Group();
  private chunkBorders = new THREE.Group();
  private hud?: StatsHud;
  private picker?: HexPicker;
  private highlight?: HexHighlight;
  private world = new Map<number, HexCellData>();
  private hovered: number | null = null;
  /** The cell under the cursor, kept so a click does not raycast a second time. */
  private hoveredCell: Axial | null = null;
  private debugMode = false;

  /**
   * The path traced view, on its own WebGPU canvas over everything.
   *
   * Made on first use rather than at startup: it asks the browser for a GPU
   * device, which is asynchronous and allowed to fail, and a game that waited
   * for that would be a game that starts more slowly for a key nobody pressed.
   */
  private tracer?: PathTracer;
  private tracing = false;
  /** Materials that trace as water. The tracer cannot guess them from a name. */
  private waterMaterials = new Set<THREE.Material>();

  /** Everything the mouse can hit. The highlight itself is deliberately absent. */
  private pickTargets: THREE.Object3D[] = [];

  /** One material for every stretch of sea, so the key has one thing to flip. */
  private waterMaterial?: THREE.MeshStandardMaterial;

  private streamer?: ChunkStreamer;

  /**
   * Whether the camera is keeping up with the player.
   *
   * Turned on by ordering a walk, and off by the first drag or WASD key — the
   * camera belongs to whoever last reached for it. Rotating and zooming leave
   * it on, because neither moves what the camera is over.
   */
  private following = false;

  /**
   * The camera as the server last heard it, and how long it has been still.
   *
   * The view is worth storing but not worth streaming: it is reported once it
   * has stopped moving, which is the only moment it means anything.
   */
  private reportedView?: { yaw: number; zoom: number };

  /**
   * The world starts behind fog and comes out of it when it is ready.
   *
   * Loading is not a blank screen and it is not a progress bar either: the game
   * already draws a near-black fog at the edge of what has been streamed, so
   * the same fog pulled in over everything is a world that has not arrived yet.
   * When it has, the fog goes back out to where it belongs and the opening move
   * begins — which is why the arrival is held here rather than started at boot.
   */
  private reveal = 0;
  private waitingForWorld = 0;
  private worldReady = false;
  /**
   * What is allowed on screen: nothing, the world, or everything.
   *
   * Three states rather than one flag, because the canvas and the interface
   * come in at different moments. The canvas appears as soon as there is a
   * frame to show — which, with the fog closed, is a dark rectangle — and the
   * HUD waits for the fog to be most of the way out, so it does not hang over
   * the weather naming a player nobody can see yet.
   */
  private shown: 'blank' | 'world' | 'ready' = 'blank';
  private arrival?: { yaw: number; zoom: number };
  private seenView?: { yaw: number; zoom: number };
  private stillFor = 0;

  /**
   * Biome names, for the debug line. The palette takes the colours out of the
   * same answer; this keeps the labels, which are the half a person reads.
   */
  /**
   * What to call a cell, in both readings.
   *
   * A biome is a climate; whether it reads as meadow or wood is the forest
   * layer's answer, so the HUD needs both and picks the one that matches what
   * is drawn.
   */
  private biomeNames = new Map<number, { bare: string; wooded?: string }>();

  /**
   * The biome dictionary as it arrived, keyed the way the rules name it.
   *
   * Kept beside the palette because they answer different questions: the
   * palette draws a cell, this is what a rule saying `BIO_BEACH` is checked
   * against.
   */
  private biomes = new Map<string, BiomeInfo>();

  /**
   * Biomes whose woodland is broadleaf rather than pine, by id.
   *
   * Filled from the legend by **key**, not by number: the ids are a wire format
   * and safe to append to, so writing 12 and 13 here would be correct until
   * somebody reorders the enum, and wrong silently afterwards. An empty set —
   * a server too old to have these biomes — simply means pines everywhere.
   */
  private broadleafBiomes = new Set<number>();

  /**
   * What the last streaming pass was made for, so a still camera costs nothing.
   * The radius is part of it: zooming out widens the view without the focus
   * moving an inch, and the ground at the new edge still has to be fetched.
   */
  private streamedFocus?: { q: number; r: number; radius: number };
  private waterSeeThrough = false;
  /** Whether the deposit circles are up instead of the props. Debug, key 5. */
  private depositView = false;
  /** Czy płatki nieprzechodności są na mapie. Debug, klawisz 6. */
  private blockedView = false;

  private isRunning = false;
  private lastFrameTime = 0;

  // --- the player, and everybody else ------------------------------------

  private connection?: Connection;
  private markers?: PlayerMarkers;
  /** Wozy w drodze — pionki, nie liczby. Patrz `ConvoyMarkers`. */
  private convoys?: ConvoyMarkers;
  /** Statki na wodzie. Bliźniak wozów — patrz `ShipMarkers`. */
  private ships?: ShipMarkers;
  /**
   * Którym statkiem płynę, albo nic.
   *
   * **Bliźniak `restingIn` i to jest ta sama rzecz**: jedno i drugie znaczy, że
   * pionka nie ma na mapie. Stąd trasa liczy się regułą morską, kamera idzie za
   * statkiem, a klik w wodę przestaje być odmową.
   */
  private aboard: string | null = null;
  /** Pomiar trasy z serwera, dopóki obraz jeszcze nie dojechał — patrz `reckon`. */
  private travelled: { took: number; planned: number } | null = null;
  /** Jedno miejsce na zlecenia i wozy — patrz `HaulPanel`. */
  private haul?: HaulPanel;
  private nameTags?: NameTags;
  private sun?: SunLight;

  /** The world's clock, as it arrived in `welcome` and has ticked since. */
  private daylight?: Daylight;

  private compass?: Compass;

  /**
   * What can be built, what stands where, and the panel that offers it.
   *
   * The catalogue is the server's description fetched once; the field is what
   * is standing; the panel is the only way into build mode. All three are
   * optional because the game starts without them — a server too old to have
   * a catalogue still serves terrain, and losing the buildings is not a reason
   * to lose the world.
   */
  private catalogue?: Catalogue;
  /** Pobranie katalogu w locie — patrz `askCatalogue`. */
  private catalogueOnce?: Promise<Catalogue | undefined>;
  private buildings?: BuildingField;
  private panel?: BuildPanel;
  /**
   * Tryb testowy: wszystko za jedną sztukę i w pięć sekund.
   *
   * Stan **serwera**, nie tej karty — przychodzi w powitaniu i rozgłasza się
   * przy zmianie, jak niebo. Trzymany tutaj, bo ceny i wyszarzenia klient
   * rysuje sam i musi wiedzieć, wedle czego.
   */
  private godmode = false;
  /**
   * Pasek na dole i to, co się nad nim rozwija.
   *
   * Powstaje razem z panelami, które trzyma, więc dopiero po katalogu — a że
   * jeden panel jest otwarty naraz, jest też jedynym miejscem, które o tym wie.
   */
  private bar?: HudBar;
  private buildKind: BuildingKind | null = null;
  private buildMark?: HexHighlight;
  /**
   * Nakładki na polach skrzydeł — dziś dwie, przy stoczni. Osobne obiekty, a nie
   * jedna siatka na trzy pola: `HexHighlight` odtwarza powierzchnię, na której
   * leży, a trzy pola mają trzy różne powierzchnie.
   */
  private readonly buildWings: HexHighlight[] = [];
  private buildGhost?: BuildGhost;
  private progress?: BuildProgress;
  /** Chmurki z tym, co ruszyło z własnego budynku — patrz `YieldPops`. */
  private pops?: YieldPops;
  private tip?: BuildingTooltip;
  private menu?: BuildingMenu;
  /** Panel rozmowy przy prawej krawędzi. */
  private talk?: InteractionPanel;
  /**
   * Panel pola, na którym stoimy — patrz `SpotPanel`.
   *
   * Pozycje są kopią menu budynku i biorą się z `menuFor`, tej samej funkcji.
   */
  private spot?: SpotPanel;
  /** Magazyn: przekazywanie i wyciąganie, obok panelu pola. */
  private store?: StorePanel;
  /** Którego budynku dotyczy otwarty magazyn. */
  private storeOf?: string;
  /** Wysyłka do osady: panel boczny i budynek, którego dotyczy. */
  private ship?: SideSheet;
  private shipOf?: string;
  /**
   * Skąd wybrano pozycję menu: z panelu pola czy z menu pod prawym
   * przyciskiem. Od tego zależy, gdzie stanie panel boczny — patrz `SideSheet`.
   */
  private sideOrigin: 'menu' | 'spot' = 'menu';
  /** Gdzie stoi otwarty panel boczny (magazyn albo wysyłka). */
  private sidePlace: SidePlace = 'edge';
  /**
   * Panel pola ustępuje miejsca panelowi otwartemu z menu: tamten stoi przy
   * krawędzi i dotyczy być może innego budynku.
   */
  private spotYield = false;
  /**
   * Czy panel pola został zamknięty Escape'em, choć jest co pokazywać.
   *
   * Zerowane **wejściem na inne pole** i prawym przyciskiem na budynku: jedno
   * i drugie jest prośbą o to, żeby wrócił. Bez tej flagi Escape gasiłby panel
   * na jedną klatkę, bo odświeżanie zapaliłoby go z powrotem.
   */
  private spotShut = false;
  /** Ile minęło od ostatniego odświeżenia panelu pola, w sekundach. */
  private spotAge = 0;
  /**
   * Czy ktoś nas w tej chwili trzyma.
   *
   * **Brama po tej stronie jest uprzejmością, nie regułą** — regułą jest ta na
   * serwerze, która odmówi każdemu żądaniu. Ta istnieje, żeby kliknięcie
   * w zatrzymaniu nie rysowało trasy, po której nikt nie pójdzie.
   */
  private detained = false;
  /**
   * Czy idziemy za kimś.
   *
   * Trzymane tutaj tylko po to, żeby Escape wiedział, że ma co odwołać: sam
   * pościg żyje na serwerze i tam jest jego stan.
   */
  private chasing = false;
  /**
   * Co zrobić po dojściu na miejsce.
   *
   * Kliknięcie „idź i zbierz" na odległym budynku zamawia marsz i **zapamiętuje,
   * po co** — sam marsz jest środkiem, a nie prośbą. Trzymane po tej stronie,
   * bo serwer nie ma o tym pojęcia i nie musi mieć: zbiór jest zwykłym żądaniem,
   * które poleci, gdy będzie wolno. Zamknięcie karty w drodze po prostu kończy
   * sprawę, i to jest uczciwe.
   */
  private errand?: {
    /** Pole, przy którym trzeba stanąć — budynku albo placu pod niego. */
    at: Axial;
    what: 'harvest' | 'upgrade' | 'build' | 'road' | 'rest' | 'fell' | 'bridge' | 'ship' | 'ashore';
    /**
     * Oś: przy stawianiu mostu, a także przy budynku ze skrzydłami.
     *
     * Zapamiętana **z chwili kliknięcia**, a nie liczona po dojściu: kursor jest
     * po drodze gdzie indziej, a stocznia zamówiona wzdłuż brzegu miałaby wtedy
     * doki tam, gdzie akurat stoi mysz.
     */
    axis?: number;
    /** Czy po dojściu budowanie ma trwać dalej — patrz SHIFT przy kliknięciu. */
    chain?: boolean;
    /** Który budynek: przy zbiorze i rozbudowie. */
    building?: string;
    /** Co postawić: przy budowie. */
    kind?: string;
  };
  private audio?: AudioEngine;
  private clouds?: Clouds;
  /**
   * Deszcz. Gaśnie i zapala się tym samym przełącznikiem co chmury, bo jest
   * tym samym zjawiskiem oglądanym od spodu.
   */
  private rain?: Rain;
  /** Dym: nad placami budowy i nad ogniskami osad — patrz `Smoke`. */
  private smoke?: Smoke;
  /** Ogniska w osadach po zmroku — patrz `Hearths`. */
  private hearths?: Hearths;
  /**
   * Niebo tej klatki, policzone raz.
   *
   * Doba przesuwa się **wyłącznie** w `updateSky`, więc pytanie o `state()`
   * drugi raz w tej samej klatce dałoby tę samą odpowiedź — nie chodzi o rozjazd,
   * tylko o to, żeby pięć warstw (światło, mgła, chmury, dym, ogniska, tracer)
   * czytało **jedno** niebo. Poprzednio stał tu jeszcze wybór „debugowe słońce
   * czy zegar" i był powielony w czterech miejscach.
   *
   * Ustawiane w `updateSky`, które w klatce idzie pierwsze.
   */
  private skyNow?: SkyState;
  private weather?: Weather;
  /** Shared by the layer above and the shadow it casts, so they cannot drift. */
  private readonly cloudUniforms = createCloudUniforms();
  /**
   * The world clock, in seconds, started from what the server said.
   *
   * Advanced here rather than asked for again — the same arrangement the day
   * phase uses, and for the same reason: it is a pure function of a clock both
   * sides can read, so it needs saying once.
   */
  private worldClock = 0;
  /** Half the world's north-south extent, for placing the wind bands. */
  private worldHalfDepth = 1;
  private pause?: GameMenu;
  /** Kept for the credits, which name what is actually playing. */
  private sounds?: AudioManifest;
  /** Seconds since the ground under the ambience was last read. */
  private listened = 0;
  /**
   * What each of this player's **own** buildings holds and makes, by id.
   *
   * Only ever their own: the server sends this to one connection and puts none
   * of it in the public view of a building, so there is nothing here to leak by
   * accident. Held until the tooltip exists — the greeting can arrive before
   * the catalogue does, exactly as the purse can.
   */
  private estate = new Map<string, Holding>();
  /**
   * When the estate above was heard, by this client's clock.
   *
   * The stores in it fill from that moment on this side — see `madeSince`. One
   * timestamp for the whole listing, because it arrives as one message.
   */
  private estateAt = 0;
  private purse?: PurseBar;
  /** Held until the bar exists: the greeting can arrive before the fetch does. */
  private carried: { material: string; amount: number }[] = [];
  /** Nazwy i odmiana surowców, do tego, co menu i formularz wypisują. */
  private materials?: Materials;
  /**
   * The chunk the placed buildings were last fetched around, and how far.
   *
   * A chunk rather than a cell, because that is the unit the endpoint answers
   * in — see `refreshBuildings`.
   */
  private buildFocus?: { q: number; r: number; radius: number };
  /**
   * Whether the camera has been put at the height of the ground under the
   * player yet.
   *
   * It cannot be done at the start: the opening move begins before the terrain
   * has been fetched, because the terrain streams in around wherever the camera
   * is already looking. So it is done on the first delivery that answers — and
   * not at all if somebody has taken the camera by hand in the meantime, since
   * moving their view underneath them would be worse than a wrong height.
   */
  private seated = false;
  /**
   * The dictionaries the site rules are read against.
   *
   * Held rather than imported because they are fetched: a rule asked before
   * they arrive would answer from an empty vocabulary and refuse everything,
   * so build mode does not open until this exists.
   */
  private vocabulary?: Vocabulary;

  private route?: RouteLine;
  private chosen?: RouteLine;
  private preview?: RouteLine;
  private mark?: TargetMark;

  /**
   * The cell a first click picked, waiting for the second one that orders the
   * walk. Null when nothing is picked — which is also when the cursor draws
   * nothing, because a route under every mouse movement is a lot of line for a
   * question nobody asked.
   */
  private chosenAt: Axial | null = null;
  /**
   * Drogi, o których ten klient wie — maska krawędzi na pole.
   *
   * Trzymane obok mapy pól, a nie tylko w niej, bo chunki przyjeżdżają później
   * niż lista obszaru: pole, które dopiero dojechało, musi dostać swoją maskę,
   * a jedynym miejscem, które ją jeszcze pamięta, jest to.
   */
  private roads = new Map<number, { cell: RoadCell; readyAt: number }>();
  /**
   * Pola, z których wycięto las — lista obszaru i `treesFelled`, kładzione na
   * teren tak jak drogi, bo chunk z pamięci przeglądarki może ich nie znać.
   */
  private felled = new Map<number, Axial>();
  /**
   * Wycinka, która trwa: pole, termin na zegarze klienta i ile kłód przyniesie.
   *
   * Termin jest **zapowiedzią, nie rozstrzygnięciem** — kłody przynosi dopiero
   * `treesFelled` od serwera. Gdy nie przyjdzie w kilka sekund po terminie,
   * serwer wycinkę odwołał (inna akcja, zatrzymanie) i licznik gaśnie sam.
   */
  private felling?: { at: Axial; until: number; logs: number };
  /** Punkty modeli, jak przyszły z serwera — dla budynków i sztandarów. */
  private anchors: Anchors = {};
  /** Drzewce sztandaru, wczytywane raz. */
  private staff?: Promise<Staff>;
  /** Drzewa w trakcie wycinki — u nas i u każdego w zasięgu. */
  private fellingTrees?: FellingTrees;
  /**
   * Osada, w której ten gracz śpi, albo `null`.
   *
   * **Stan przychodzi z serwera, nie z kliknięcia.** Budzi też on sam — przy
   * pierwszej rzeczy wymagającej ciała na mapie — więc jedynym miejscem, które
   * wie, czy się śpi, jest wiadomość `rest`. Odpowiedź na własne wywołanie
   * ustawia to samo, żeby pionek zniknął, zanim socket zdąży odpowiedzieć.
   */
  private restingIn: string | null = null;
  /** Budzik na chwilę, w której najbliższa droga w budowie stanie się gotowa. */
  private roadClock?: ReturnType<typeof setTimeout>;
  /**
   * Tryb kładzenia drogi: `null` — wyłączony, pole — początek już wskazany.
   *
   * Dwa kliknięcia i jeden marsz: pierwsze wskazuje **początek** i prowadzi tam
   * postać, drugie — dokąd droga ma iść. Prowadzimy tylko na początek: marsz
   * wzdłuż całej trasy byłby przejściem przez pół mapy, zanim cokolwiek
   * powstanie.
   */
  private roadMode = false;
  private roadFrom: Axial | null = null;
  /** Tryb stawiania mostu — patrz „Mosty na wodzie” w `roads.md`. */
  private bridgeMode = false;
  /** Gdzie i w jakiej osi stanąłby most pod kursorem. */
  private bridgeAt: { cell: Axial; axis: number } | null = null;
  private bridgeModel?: BridgeModel;
  private bridgePreview?: THREE.Group;
  /** Budowa dróg i mostów: rusztowania, kurz, paski. */
  private roadWorks?: RoadWorks;
  /** Co się buduje w tej klatce — dla dymu i pasków. */
  private works: WorkSite[] = [];

  /**
   * Where a walk was ordered to, while it is still being walked.
   *
   * The X stays on it: setting off does not stop it being the destination, and
   * a mark that vanishes at the moment the walk starts takes away the one thing
   * on screen saying where the walk ends.
   */
  private walkingTo: Axial | null = null;

  /** Where the preview currently runs to, so it is redrawn only when it moves. */
  private previewTo: number | null = null;
  private status?: StatusBar;

  /** Where the server says this player is standing. */
  private standing: { q: number; r: number };

  /**
   * How far the pointer may travel between press and release and still count as
   * a click. Panning starts on the same button, so without this every drag that
   * ended over a cell would also order a walk there.
   */
  private static readonly CLICK_SLOP = 4;
  /** How often the ground under the ambience is read, in seconds. */
  private static readonly LISTEN_SECONDS = 0.5;
  private static readonly CLICK_MS = 400;
  private pressed?: { x: number; y: number; at: number };

  constructor(
    container: HTMLElement,
    private readonly session: Session,
    private readonly joined: Joined
  ) {
    this.container = container;
    this.standing = joined.position;
  }

  /**
   * Initialize game systems
   */
  async initialize(): Promise<void> {
    console.log('🎮 Initializing Uncharted Lands...');

    this.setupRenderer();
    this.setupScene();
    this.setupCamera();

    // Buffer size, canvas size and camera frustum all come from the window, so
    // they are set together — here for the first frame, and again on resize
    this.applyResolution();

    this.setupLights();

    // Controls before terrain, because the terrain streamer asks the camera
    // controller where it is looking in order to decide what to load. The
    // picker is handed the target list by reference and the streamer fills it
    // as chunks arrive, so nothing here depends on the ground existing yet.
    this.setupControls();

    // Before the terrain, not after: the streamer loads around wherever the
    // camera is looking, and that has to be the player's own cell or the first
    // neighbourhood fetched is ground nobody is standing on.
    const spawn = axialToWorld(this.joined.position.q, this.joined.position.r);
    this.cameraController.focusOn(spawn.x, spawn.z, true);

    // Arriving: the camera turns and pushes in to the view this player left
    // behind, or to the opening one on a first visit.
    //
    // **Not started here.** It waits behind the fog until the ground has
    // arrived, which buys two things: nobody watches the world assemble itself
    // chunk by chunk, and the camera already knows how high the player is
    // standing when the movement begins, so the opening has no correction in
    // the middle of it.
    this.arrival = this.joined.view ?? { yaw: this.cameraController.view.yaw, zoom: 2 };
    this.reportedView = { ...this.cameraController.view };

    await this.setupTerrain();
    this.setupPlayers();

    console.log('✅ Game initialized successfully');
  }

  /**
   * Setup Three.js WebGL renderer. Buffer size comes from applyResolution().
   */
  private setupRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({
      // Multisampling inside the low-resolution buffer, before the upscale.
      // A diagonal silhouette then steps through pixels of an in-between colour
      // instead of jumping, and the upscale enlarges that gradient rather than
      // a hard staircase. Cheap here: the terrain carries no textures, so the
      // only aliasing in the frame is on geometry edges.
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });

    // Enable shadows (optional, can disable for mobile)
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Tone mapping for better colors
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    // Set before the canvas is in the document, so the first paint the browser
    // does already has it hidden rather than fading it out afterwards.
    this.container.dataset.stage = this.shown;
    this.container.appendChild(this.renderer.domElement);
  }

  /**
   * Fit the render buffer, the canvas and the camera frustum to the window.
   *
   * The buffer is whatever fits the window at PIXEL_SCALE physical pixels per
   * game pixel, so the browser's nearest-neighbour upscale always works on a
   * whole-number ratio and every game pixel comes out the same size. What stays
   * constant across window sizes is how much world a pixel covers, not how much
   * world is on screen — see PIXELS_PER_UNIT.
   */
  private applyResolution(): void {
    const canvas = this.renderer.domElement;
    const dpr = window.devicePixelRatio || 1;

    // The canvas carries a CSS border, and the global box-sizing: border-box
    // counts it inside whatever width is set below — left alone, the image
    // would come out a few pixels short of a whole-number upscale. The widths
    // are read off the element rather than copied from the stylesheet, so the
    // two cannot drift apart; clientLeft/clientTop are the same pair HexPicker
    // uses to find the image inside the element's box.
    const borderX = 2 * canvas.clientLeft;
    const borderY = 2 * canvas.clientTop;

    // Physical pixels the image itself has to work with
    const availableX = (this.container.clientWidth - borderX) * dpr;
    const availableY = (this.container.clientHeight - borderY) * dpr;

    const width = Math.max(1, Math.floor(availableX / PIXEL_SCALE));
    const height = Math.max(1, Math.floor(availableY / PIXEL_SCALE));

    this.renderer.setSize(width, height, false);

    canvas.style.width = `${(width * PIXEL_SCALE) / dpr + borderX}px`;
    canvas.style.height = `${(height * PIXEL_SCALE) / dpr + borderY}px`;

    // Both extents follow the buffer, which is what holds a world unit at
    // PIXELS_PER_UNIT pixels whatever the window does
    this.camera.left = width / PIXELS_PER_UNIT / -2;
    this.camera.right = width / PIXELS_PER_UNIT / 2;
    this.camera.top = height / PIXELS_PER_UNIT / 2;
    this.camera.bottom = height / PIXELS_PER_UNIT / -2;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Setup Three.js scene
   */
  private setupScene(): void {
    this.scene = new THREE.Scene();

    // **Starts closed.** Colour and range are rewritten every frame from the
    // sky and the reveal, but the values a scene is built with are what any
    // frame drawn before the first update shows — and the old ones were sky
    // blue with the fog starting fifty units out, which is a bright and almost
    // unfogged world flashing up for a frame before the weather arrives.
    //
    // Built the way `updateSky` builds it at reveal zero: both planes at the
    // lens, a hair apart so the shader has a range to interpolate over.
    this.scene.background = new THREE.Color(0x000000);
    this.scene.fog = new THREE.Fog(0x000000, 0, 0.001);
  }

  /**
   * Setup orthographic camera (isometric view)
   */
  private setupCamera(): void {
    // The extents are placeholders — applyResolution() derives the real ones
    // from the buffer size, immediately after this and on every resize.
    // Position and orientation belong to CameraController, which derives them
    // from its focus point and yaw every frame.
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, CAMERA_NEAR, CAMERA_FAR);
    this.camera.zoom = 2;
  }

  /**
   * The sky: one key light for the sun or the moon, one hemisphere for the
   * ambient. Both are driven by the clock every frame — see `Daylight`.
   */
  private setupLights(): void {
    this.sun = new SunLight(this.scene);
  }

  /**
   * Move the sky on, and put the light over the ground the camera is on.
   *
   * The light follows the camera because a directional light shadows one box
   * and a world is thousands of units wide; what that box has to cover is the
   * frustum, which only this class can measure.
   */
  private updateSky(delta: number): void {
    if (!this.daylight || !this.sun) return;

    // **Debug nie trzyma już słońca.** Trzymał je pod pomiary — cień mierzony
    // dwa razy pod światłem, które się w międzyczasie przesunęło, nie jest
    // pomiarem — ale robił to **za plecami zegara świata**, czyli ten jeden
    // klient widział co innego niż wszyscy pozostali. Dziś dobę zatrzymuje się
    // z panelu (`/sky`), gdzie robi to serwer: stoi jedna doba dla całego
    // świata i widzą to wszyscy, którzy w nim są.
    this.daylight.advance(delta);
    const state = this.daylight.state();
    this.skyNow = state;

    const focus = this.cameraController.focusPoint;
    this.sun.update(state, focus, this.viewReach());

    // Fog is the edge of the loaded world, not a haze over the near ground.
    //
    // It used to be set from the frustum, which was wrong twice over. Fog in
    // three.js is measured **from the camera**, and an orthographic camera at
    // this pitch parks 34,6 units from the ground it looks at — so a near plane
    // of 0,85 of the frustum put the fog well inside the picture. The closer
    // the zoom, the smaller the frustum, and the thicker the fog: at maximum
    // zoom it covered everything.
    //
    // What it should mark is where the terrain stops. Chunks are streamed in
    // rings around the focus, so the last ring has a hard edge with nothing
    // behind it; the fog is placed on that edge and darkened, which turns the
    // end of the loaded world into distance rather than into a cliff.
    // Half a chunk past the middle of the last ring, because that is where the
    // ground actually ends: a chunk reaches its own radius beyond its centre.
    const edge = this.streamer
      ? (this.drawRadius() + 0.5) * this.streamer.chunkSpacing
      : this.viewReach();

    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.copy(this.edgeColor(state));
      // **The whole range is scaled, not just its tail.** Leaving the camera's
      // own distance outside the scaling puts the near plane exactly where the
      // ground under the focus is, so the middle of the frame stays clear while
      // everything around it is fog — a porthole, not weather. Scaled from zero
      // the fog stands at the lens and the scene is uniformly its colour.
      //
      // The two planes are also never allowed to meet: three's fog is a
      // smoothstep between them, and an empty range is a division by zero in
      // the shader rather than a very thick fog.
      const opened = smoothstep(this.reveal);
      const near = opened * (CAMERA_DISTANCE + edge * FOG_NEAR_EDGE);
      this.scene.fog.near = near;
      this.scene.fog.far = Math.max(
        near + 0.001,
        opened * (CAMERA_DISTANCE + edge * FOG_FAR_EDGE)
      );
    }
    if (this.scene.background instanceof THREE.Color) {
      // The same colour as the fog, so ground fading out and sky beyond it are
      // one thing rather than a dark rim against a bright horizon.
      this.scene.background.copy(this.edgeColor(state));
    }

    this.updateWater(delta, state);
  }

  /**
   * The sea: waves running, surf breaking, sky in the surface.
   *
   * Its own clock rather than the day's, because waves run at their own speed;
   * what it takes from the sky is the colour it reflects.
   *
   * **Detail fades as the camera pulls back.** A crest is a couple of pixels
   * across and at the widest zoom a hex is nine, so at some point the waves
   * stop being waves and become a shimmer on every water pixel in the frame.
   */
  private updateWater(delta: number, state: SkyState): void {
    const uniforms = this.waterMaterial?.userData.uniforms as
      | { uTime: { value: number }; uSky: { value: THREE.Color }; uDetail: { value: number } }
      | undefined;
    if (!uniforms) return;

    uniforms.uTime.value += delta;
    uniforms.uSky.value.copy(state.skyColor);
    uniforms.uDetail.value = THREE.MathUtils.clamp(
      (this.camera.zoom - WAVES_GONE) / (WAVES_FULL - WAVES_GONE),
      0,
      1
    );
  }

  /**
   * The colour the world fades into at the edge of what is loaded.
   *
   * Nearly black, with a little of the sky in it so that dusk and midnight are
   * not the same darkness. Dark rather than sky-coloured on purpose: past the
   * last ring there is no terrain, and what is being drawn is the fact that
   * nobody has looked there yet.
   */
  private edgeColor(state: SkyState): THREE.Color {
    return new THREE.Color(0x000000).lerp(state.fogColor, EDGE_SKY);
  }

  /**
   * How far the camera sees from its focus, in world units.
   *
   * The frustum is measured across the ground, so the vertical half is divided
   * by the sine of the fixed 35,264° pitch — on screen it is foreshortened, and
   * the shadow box and the fog both need the distance on the ground rather than
   * the one in the picture.
   */
  private viewReach(): number {
    const halfWidth = (this.camera.right - this.camera.left) / 2 / this.camera.zoom;
    const halfDepth =
      (this.camera.top - this.camera.bottom) / 2 / this.camera.zoom / Math.sin(CAMERA_PITCH);
    return Math.hypot(halfWidth, halfDepth);
  }

  /**
   * Connect to the world and stream in the terrain around the camera.
   */
  /**
   * Katalog budynków, pobrany **raz na dwie potrzeby**.
   *
   * Pytają o niego teren — bo katalog mówi, które pliki modeli wczytać — i pole
   * budynków, bo mówi, co wolno postawić i za ile. Trzymana jest **obietnica,
   * nie wartość**: to ona rozstrzyga, że żądanie poleci jedno, niezależnie od
   * tego, które z dwojga zdąży pierwsze. Dwa żądania byłyby dwiema odpowiedziami,
   * a te mogłyby się rozejść — bryły z jednej, ceny z drugiej.
   *
   * **Niepowodzenie nie zatrzymuje świata.** Bez katalogu nie ma budynków do
   * narysowania, więc brak modeli nie jest wtedy stratą: teren stoi, a pole
   * budynków nie powstaje — dokładnie tak, jak gdy to żądanie padało przedtem.
   */
  private askCatalogue(): Promise<Catalogue | undefined> {
    this.catalogueOnce ??= loadCatalogue(API).catch((error) => {
      console.warn('katalog budynków niedostępny:', error);
      return undefined;
    });
    return this.catalogueOnce;
  }

  private async setupTerrain(): Promise<void> {
    // Printed before the heavy work starts, so the hint is already on screen if
    // a large map locks the tab up
    console.log(
      '🔷 F3 — debug mode, then 1 hex outlines · 2 chunk borders · 3 see-through water\n' +
        '   4 path tracer · 5 deposit richness as circles, red poor to green rich\n' +
        '   6 impassable: red ground, orange cut off, white edges too steep to cross\n' +
        '   ?radius=N pins the drawn rings · ?overlays=off skips the debug meshes'
    );

    const source = new WorldSource(API);
    const info = await source.info();

    // The legend before the first chunk, so nothing is ever meshed with an
    // empty palette and left magenta until something happens to rebuild it.
    try {
      const biomes = await source.biomes();
      this.biomes = biomes;
      setBiomePalette(
        new Map(
          [...biomes].map(([, b]) => [
            b.id,
            { bare: b.color, wooded: b.wooded?.color, grass: b.grass },
          ])
        )
      );
      // Both readings, because the HUD names a cell the same way the map draws
      // it: a temperate cell with trees is a wood, not "temperate".
      this.biomeNames = new Map(
        [...biomes].map(([, b]) => [b.id, { bare: b.label, wooded: b.wooded?.label }])
      );

      // Broadleaf where the climate is warm — the rainy zone and the dry one,
      // which is where the rainforest and the savanna used to be named. Read
      // by key rather than by number, so a renumbering cannot silently put
      // pines in a jungle.
      const broadleaf = new Set(['BIO_RAINY', 'BIO_DRY']);
      this.broadleafBiomes = new Set(
        [...biomes].filter(([key]) => broadleaf.has(key)).map(([, b]) => b.id)
      );

      console.log(
        `🎨 słownik: ${biomes.size} biomów, liściaste w ${this.broadleafBiomes.size}`
      );
    } catch (error) {
      console.warn('legenda biomów niedostępna, teren pokoloruje wysokość:', error);
    }

    console.log(
      `🌍 world ${info.id}: ${info.chunkCount} chunks of ${info.cellsPerChunk} cells, ` +
        `elevation −${info.maxDepth}..${info.maxElevation}`
    );

    // One material for every chunk — nothing about it varies per chunk, so
    // sharing it keeps the count of GPU programs at one
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.9,
      metalness: 0,
    });

    this.waterMaterial = createWaterMaterial();
    const riverMaterial = createRiverMaterial();
    // Nie idzie do pętli niżej: znacznik zasobności nie jest rzeczą w świecie,
    // więc cień chmury nad nim nic nie znaczy, a `MeshBasicMaterial` i tak nie
    // ma czym go przyjąć.
    const depositMaterial = createDepositMaterial();
    const blockedMaterial = createBlockedMaterial();
    // Wstęga drogi leży na terenie i przyjmuje jego światło, więc idzie do
    // pętli z cieniem chmur — inaczej niż znacznik złoża, który jest rzeczą
    // o świecie, a nie w nim.
    const roadMaterial = createRoadMaterial();
    const digMaterial = createDigMaterial();
    const bridgeMaterial = createBridgeMaterial();

    // The cloud shadow goes on everything the sky can fall on. Wrapped rather
    // than assigned, because the water already has an `onBeforeCompile` of its
    // own and replacing it would quietly take its swell away.
    // Ziarno trawy tylko na terenie: woda, rzeka i droga nie mają darni, a most
    // stoi nad nią. Przed cieniem chmur, żeby chmura przyciemniała i trawę.
    applyGrassDetail(material);

    for (const lit of [material, this.waterMaterial, riverMaterial, roadMaterial, digMaterial, bridgeMaterial]) {
      applyCloudShadow(lit, this.cloudUniforms);
    }

    // How far it is to a pole, in world units, so the wind bands can be placed.
    // Read off the world's own shape: the lattice is `latticeRadius` chunks of
    // `chunkRadius` cells each way, and one row of cells is `√3 · HEX_SIZE`
    // deep. It is the client's own reading rather than the generator's climate
    // grid, so at a band's edge the two can disagree by a fraction of a percent
    // — the bands are a third of the way to the pole wide, and nothing there is
    // close enough to matter.
    const across = (2 * info.latticeRadius + 1) * (2 * info.chunkRadius + 1);
    this.worldHalfDepth = (across / 2) * Math.sqrt(3) * HEX_SIZE;

    this.weather = new Weather(info.id, info.wind);
    this.clouds = new Clouds(this.cloudUniforms);
    this.clouds.visible = cloudsWanted();
    this.scene.add(this.clouds.object);
    // Te same uniformy chmur, nie kopie: kropla ma padać z chmury, którą widać.
    this.rain = new Rain(this.cloudUniforms);
    this.rain.visible = cloudsWanted();
    this.scene.add(this.rain.object);
    // Both are reflective to the tracer. Remembered here because this is where
    // they are made — asking a mesh what it is by its name would be a second
    // source of truth about the same meshes.
    this.waterMaterials.add(this.waterMaterial);
    this.waterMaterials.add(riverMaterial);

    // Trees and rocks, loaded once for the whole session. A failure here costs
    // the props and nothing else: a world without trees is playable, and one
    // that refuses to start because a model would not parse is not.
    let props: Props | null = null;
    try {
      props = await loadProps();
      console.log(`🌲 modele wczytane: ${[...props.pines, ...props.rocks].map((m) => m.name).join(', ')}`);
    } catch (error) {
      console.warn('modele rekwizytów niedostępne, teren zostanie pusty:', error);
    }

    // Budynki, w tym samym miejscu i z tego samego powodu: **przed** zbudowaniem
    // świata, więc nic nie mignie zastępczą bryłą i nic nie trzeba podmieniać
    // w locie. Rodzaje bez pliku zostają przy swoich sylwetkach, a błąd
    // wczytania kosztuje jeden wygląd, nie wejście do gry.
    // Most, w tym samym miejscu i z tego samego powodu: przed zbudowaniem
    // świata, bo chunk pyta o niego przy meszowaniu. Brak pliku zostawia
    // przeprawy przy wstędze z pudełek.
    const bridgeModel = await loadBridgeModel();
    this.bridgeModel = bridgeModel ?? undefined;
    if (bridgeModel) {
      // Cień chmur, którego reszta modeli nie dostaje — i to nie jest
      // niekonsekwencja. Most jest jedyną rzeczą z pliku, która **zastąpiła**
      // coś, co ten cień już miało: bez tego przeprawa przestałaby ciemnieć pod
      // chmurą, po której dalej ciemnieje dochodząca do niej droga.
      for (const part of bridgeModel.parts) applyCloudShadow(part.material, this.cloudUniforms);
      console.log(`🌉 model mostu: ${bridgeModel.name}`);
    }

    // **Katalog przed modelami, bo to on mówi, które pliki wczytać.**
    const catalogue = await this.askCatalogue();

    // Punkty na bryłach jadą razem z modelami, bo są o nich: `adoptModels`
    // przelicza je tą samą skalą, którą wypieka w geometrię. Pobierane obok,
    // nie po kolei — jedno jest plikami, drugie jednym małym żądaniem.
    // Kompozycje mówią, które nazwy z katalogu są złożone z części, a punkty
    // części jadą do kompozycji — więc oba pytania idą przed wczytaniem brył.
    const [anchors, compositions] = await Promise.all([loadAnchors(API), loadCompositions(API)]);
    const models = await loadBuildingModels(catalogue, compositions, anchors);
    this.anchors = anchors;
    if (models.length > 0) {
      adoptModels(models, anchors);
      console.log(
        `🏚️ modele budynków: ${models
          .map(({ key, level }) => (level === undefined ? key : `${key} ${level}`))
          .join(', ')}`
      );
    }

    this.gridOverlay.visible = false;
    this.chunkBorders.visible = false;
    this.scene.add(this.gridOverlay, this.chunkBorders);

    this.streamer = new ChunkStreamer(
      source,
      info,
      this.scene,
      this.pickTargets,
      material,
      this.waterMaterial,
      riverMaterial,
      roadMaterial,
      bridgeMaterial,
      depositMaterial,
      blockedMaterial,
      this.gridOverlay,
      this.chunkBorders,
      overlaysEnabled(),
      props,
      bridgeModel,
      this.broadleafBiomes
    );

    this.world = this.streamer.world;
    this.streamer.digMaterial = digMaterial;
    if (props) {
      const streamer = this.streamer;
      this.fellingTrees = new FellingTrees(
        this.world,
        props,
        this.broadleafBiomes,
        (cell) => this.audio?.chop(this.hearing(cell)),
        (cell) => streamer.repaint([cell])
      );
      this.scene.add(this.fellingTrees.object);
      streamer.felling = (cell) => this.fellingTrees?.has(cell) ?? false;
    }
    this.highlight = new HexHighlight();
    this.scene.add(this.highlight.object);

    // The same overlay again, in build mode's colours. Not a pick target, for
    // the same reason the red one is not: it would shadow the cell it marks.
    this.buildMark = new HexHighlight();
    this.buildMark.setColor(BUILD_OK);
    this.scene.add(this.buildMark.object);

    // What the cursor is carrying: the building itself, see-through, standing
    // where it would stand. The overlay says which cell, this says what.
    this.buildGhost = new BuildGhost(this.world);
    this.scene.add(this.buildGhost.object);
    this.hud = new StatsHud(this.streamer.stats);

    // The first neighbourhood is awaited so that the opening frame has ground
    // in it rather than an empty sky
    const started = performance.now();
    const focus = this.focusCell();
    const radius = this.drawRadius();
    this.streamedFocus = { ...focus, radius };
    await this.streamer.update(focus, radius);
    console.log(
      `🗺️ first neighbourhood in ${(performance.now() - started).toFixed(0)}ms — ` +
        `${this.streamer.stats.chunks} chunks, ` +
        `${this.streamer.stats.triangles.toLocaleString('en-US')} tris`
    );
  }

  /**
   * Pawns, names, the route line, and the socket that keeps them true.
   *
   * After the terrain, because a pawn stands at a height read from the ground
   * it is on, and before anything is drawn, because the first frame should
   * already have the player in it.
   */
  private setupPlayers(): void {
    this.markers = new PlayerMarkers(this.world, this.joined.playerId);
    // Rycerz dojeżdża osobno; do tego czasu — albo gdy plik się nie wczyta —
    // pionki są stożkami.
    void loadPawnModel()
      .then((model) => this.markers?.useModel(model))
      .catch((error) => console.warn('model postaci:', error));
    // Sztandary: drzewce z pliku, płat z projektu gracza. Punkt, w którym rycerz
    // trzyma drzewce, jest punktem `banner` jego modelu.
    this.markers.useBanners(
      (player, version, height) => this.bannerFor(player, version, height),
      this.anchors['knight']?.banner?.[0]
    );
    this.convoys = new ConvoyMarkers(this.world);
    this.scene.add(this.convoys.object);
    this.ships = new ShipMarkers(this.world);
    this.scene.add(this.ships.object);
    // Bryła dojeżdża osobnym żądaniem; do tej chwili statek stoi zastępczym
    // kadłubem, jak budynek bez modelu stoi sylwetką.
    void this.ships.useModel();
    this.scene.add(this.markers.object);

    this.route = new RouteLine(this.world);
    this.scene.add(this.route.object);

    // The destination a first click picked, and — while one is picked — where
    // the cursor is pointing now. Both worked out locally, both the same kind
    // of line as the server's, so they read as the same sort of thing.
    this.chosen = new RouteLine(this.world, 'chosen');
    this.scene.add(this.chosen.object);

    this.preview = new RouteLine(this.world, 'hover');
    this.scene.add(this.preview.object);

    this.mark = new TargetMark(this.world);
    this.scene.add(this.mark.object);

    // What can be built. Fetched rather than known: the description lives in
    // one file on the server, and a copy of the prices here would be a second
    // source of truth for the same numbers.
    // The dictionary first: prices and the store bar are both spelled with it,
    // and a catalogue without one would show numbers with no units.
    //
    // Sam katalog przyjechał wyżej, razem z modelami — te wczytują się z niego,
    // więc musiał być pierwszy. Tutaj zostaje słownictwo, bez którego ceny nie
    // mają jednostek.
    void Promise.all([loadMaterials(API), loadObjects(API), this.askCatalogue()])
      .then(([materials, objects, catalogue]) => {
        // Bez katalogu nie ma czego budować ani czym tego opisać. Pole budynków
        // nie powstaje, a to znaczy „nic nie stoi" — tak samo, jak gdy żądanie
        // padło przed tą zmianą.
        if (!catalogue) return;
        this.vocabulary = { objects, biomes: this.biomes, catalogue };
        this.catalogue = catalogue;
        this.buildings = new BuildingField(this.world, catalogue, this.pickTargets);
        // Budynki noszą sztandar właściciela tą samą drogą, co pionki.
        this.buildings.useBanners((owner, version, height) => this.bannerFor(owner, version, height));
        // Statek nosi sztandar właściciela w punkcie `banner` swojej bryły —
        // tą samą drogą, co budynek, i tak samo milczy, gdy punktu nie ma.
        this.ships?.useBanners(
          (owner, version, height) => this.bannerFor(owner, version, height),
          this.anchors['ship-1']?.banner?.[0]
        );
        this.scene.add(this.buildings.object);

        // A building stands instead of what grew there, and the ground under it
        // is levelled — both are asked of this. Set once the field exists;
        // until then nothing is built and the answer is no.
        if (this.streamer) {
          this.streamer.built = (cell) => this.buildings?.has(cell) ?? false;
          this.streamer.lowered = (cell) => this.buildings?.lowered(cell);
          this.buildings.onGround = (cells) => this.streamer?.settle(cells);
        }

        // Bars over whatever is going up. Above the canvas, like the names.
        this.progress = new BuildProgress(
          this.container,
          this.renderer.domElement,
          this.buildings
        );

        // Co ruszyło z budynku, nad budynkiem. Słownik surowców, bo chmurka
        // niesie ikonę — więc tutaj, razem z dymkiem, a nie z resztą HUD-u.
        this.pops = new YieldPops(this.container, this.renderer.domElement, materials);

        // Dym nad budowami. Powstaje razem z polem budynków, bo z niego bierze
        // to, co się buduje — a bez pola nie ma czym dymić.
        this.smoke = new Smoke();
        this.scene.add(this.smoke.object);

        // Ogniska. Też z pola budynków i z tego samego powodu — a pula świateł
        // wchodzi do sceny **raz**, bo zmiana ich liczby przekompilowuje
        // wszystko, co jest oświetlone.
        this.hearths = new Hearths();
        this.scene.add(this.hearths.object);

        // What a building is, over the building. Needs the catalogue for its
        // name and the dictionary for what is in it, so it is made here rather
        // than with the rest of the HUD.
        this.tip = new BuildingTooltip(
          this.container,
          this.renderer.domElement,
          catalogue,
          materials,
          this.joined?.playerId ?? ''
        );

        // What can be done with one, at the cursor. Made beside the tooltip and
        // for the same reason: both need the catalogue to name things.
        this.menu = new BuildingMenu(this.container);

        this.materials = materials;
        this.purse = new PurseBar(this.container, materials);
        this.purse.set(this.carried);

        this.panel = new BuildPanel(catalogue, materials, (kind) => {
          this.buildKind = kind;
          // Picking a building and picking a destination are two different
          // answers to a click, so choosing one drops the other.
          if (kind) this.unpick();
          this.buildMark?.hide();
          this.buildGhost?.hide();
        },
        (on) => this.setRoadMode(on),
        (on) => this.setBridgeMode(on));

        // Tempo wozu jest liczbą serwera; pionek dostaje ją stąd, żeby przejście
        // trwało dokładnie tyle, ile trwa na tamtej stronie.
        this.convoys?.setPace(catalogue.haulage.pace);

        this.roadWorks = new RoadWorks(this.world, catalogue.roads.bridge.seconds);
        this.scene.add(this.roadWorks.object);

        this.haul = new HaulPanel({
          describe: (id) => this.describeBuilding(id),
          material: (key, amount) => countOf(materials, key, amount),
          onCancel: (shipment) => {
            void cancelShipment(this.session.token, shipment).then(() => this.refreshHaul());
          },
          onRefresh: () => this.refreshHaul(),
        });

        // **Pasek powstaje na końcu, bo składa się z tego, co przed chwilą
        // powstało.** Panele nie wiedzą, gdzie stoją — oddają swój element,
        // a pasek pilnuje, żeby otwarty był jeden naraz.
        this.bar = new HudBar(this.container, [
          { key: 'build', label: 'Budowanie', panel: this.panel.element },
          {
            key: 'trade',
            label: 'Handel',
            panel: this.haul.element,
            onOpen: () => this.haul?.opened(),
          },
        ]);

        // Whatever already stands where the player is standing.
        this.refreshBuildings();
      })
      .catch((error) => console.warn('słowniki niedostępne:', error));

    // The compass belongs to the player's HUD rather than to the scene: it says
    // which way the world is turned, and that is a thing about the picture.
    this.compass = new Compass(this.container);

    // Sound stands on its own, and that is the whole arrangement: it is loaded
    // in its own promise, it never throws, and a manifest that is missing or a
    // version ahead leaves a quiet game rather than taking anything with it.
    // The HUD once went dark because a catalogue one version ahead shared a
    // promise with everything else, and this is that lesson written down.
    this.audio = new AudioEngine();
    // What was set last time, applied before anything can be heard at the wrong
    // volume — the sliders live in the menu now, and the menu may never be
    // opened.
    const volumes = storedVolumes();
    for (const bus of ['music', 'ambience', 'effects'] as const) {
      this.audio.setVolume(bus, volumes[bus]);
    }
    void loadAudioManifest().then((manifest) => {
      if (!manifest) return;
      this.sounds = manifest;
      void this.audio?.load(manifest);
    });

    this.spot = new SpotPanel(this.container, () => {
      this.spotShut = true;
      this.spot?.hide();
    });
    this.ship = new SideSheet(this.container, () => this.closeShip());
    this.store = new StorePanel(this.container, {
      move: (tab, material, amount) => void this.moveGoods(tab, material, amount),
      close: () => this.closeStore(),
      switched: () => this.refreshStore(),
    });

    this.talk = new InteractionPanel(this.container, {
      onPart: () => this.connection?.part(),
      // Kwestia nie zmienia tu niczego od razu: panel przerysuje się z tego,
      // co odpowie serwer — bo to on rozstrzyga, czy wolno ją było wybrać,
      // i on przesuwa termin rozmowy.
      onSay: (action) => this.connection?.say(action),
    });

    this.pause = new GameMenu(this.container, {
      onVolume: (bus, value) => this.audio?.setVolume(bus, value),
      onClouds: (wanted) => {
        if (this.clouds) this.clouds.visible = wanted;
        if (this.rain) this.rain.visible = wanted;
      },
      onSnap: (wanted) => {
        this.cameraController.snapping = wanted;
      },
      credits: () => this.credits(),
      onLogout: () => void this.logOut(),
      // The camera listens on the window, so it has to be told; the mouse needs
      // no telling, because the overlay is in front of the canvas.
      onOpenChange: (open) => {
        this.cameraController.paused = open;
        // A building menu left open under the overlay would still be there on
        // the way back, pointing at a cell nobody is looking at any more.
        if (open) this.menu?.hide();
      },
    });

    this.status = new StatusBar(this.container);
    this.status.setPlayer(this.session.username, 1);
    this.status.setLink('łączę…', 'waiting');

    this.nameTags = new NameTags(
      this.container,
      this.renderer.domElement,
      this.markers
    );

    this.markers.add(this.joined.playerId, this.session.username, this.joined.position);

    this.connection = new Connection(this.session.token, {
      onWelcome: (welcome) => {
        // Everything is rebuilt from the welcome rather than merged into what
        // was there: after a reconnect the server's list is the truth, and a
        // pawn left over from before it dropped would be somebody who has
        // since walked away.
        this.markers?.clear();
        this.standing = welcome.position;
        // Tryb testowy przychodzi z powitaniem, bo po ponownym połączeniu to
        // serwer ma rację co do tego, ile rzeczy kosztują.
        this.godmode = welcome.godmode === true;
        // Płynięcie przeżywa połączenie, jak sen: wracający na pokład ma nie
        // zobaczyć własnego pionka stojącego na wodzie.
        this.aboard = (welcome as { aboard?: string }).aboard ?? null;
        this.panel?.setGodmode(this.godmode);
        // Odpoczynek przeżywa wylogowanie, więc powitanie może zastać nas
        // w łóżku — i wtedy własnego pionka nie ma czego stawiać.
        this.restingIn = welcome.resting ?? null;
        // **Pod pokładem pionka nie ma tak samo, jak pod dachem.** Powitanie
        // zastaje nas w rejsie (`aboard` przeżywa wylogowanie), a jego
        // `position` jest wtedy polem **statku** — postawiony z niego pionek
        // stał na wodzie i zostawał tam, gdy statek ruszał.
        if (!this.restingIn && !this.aboard) {
          this.markers?.add(
            welcome.playerId,
            welcome.username,
            welcome.position,
            welcome.slot
          );
        }
        welcome.players.forEach((player: Presence) => {
          this.markers?.add(player.playerId, player.username, player.position, player.slot);
          this.markers?.setBanner(player.playerId, player.banner);
        });
        // Własnego sztandaru powitanie nie niesie — pytamy o niego wprost.
        void loadMyBanner(this.session.token)
          .then(({ banner }) => this.markers?.setBanner(welcome.playerId, banner?.version))
          .catch(() => undefined);
        this.markers?.refresh();
        this.status?.setPlayer(welcome.username, welcome.players.length + 1);

        // The clock comes with the greeting and then runs locally. A reconnect
        // brings a fresh one, which is also how any drift is corrected.
        // Held as well as shown: a reconnect can beat the dictionary's fetch,
        // and the bar cannot be told anything before it exists.
        this.carried = welcome.purse ?? [];
        this.purse?.set(this.carried);
        // Same reasoning one level up, and the same reconnect rule: the
        // greeting is the truth about what is standing and what is in it.
        this.holdings(welcome.estate ?? []);

        if (welcome.daylight) {
          this.daylight = new Daylight(welcome.daylight);
          // The wall clock behind the phase. Weather moves over hours, so the
          // fraction of a three-hour day cannot carry it; a server that sends
          // none leaves the sky at whatever the clock here happens to say,
          // which is only wrong between two players minutes apart.
          this.worldClock = welcome.daylight.clock ?? Date.now() / 1000;
          this.weather?.pin(welcome.daylight.cover);
        }
        this.status?.setLink('połączono', 'live');
      },

      // An arrival is somebody coming into view, which is not the same as
      // somebody connecting — a player walking towards us arrives the same way.
      // The client does not need to tell the two apart, and the count on screen
      // is honestly "how many I can see", not how many are playing.
      onJoined: (player) => {
        this.markers?.add(player.playerId, player.username, player.position, player.slot);
        this.markers?.setBanner(player.playerId, player.banner);
        this.markers?.refresh();
        this.status?.setPlayer(this.session.username, this.countPlayers());
      },

      onLeft: (playerId, into) => {
        // **Najpierw wejście, potem zniknięcie** — także u patrzących. Kładący
        // się stoi obok osady, a serwer zdejmuje go z mapy od razu; bez tego
        // kroku postać gasła o pole przed drzwiami.
        if (into) this.markers?.moveTo(playerId, into, 0);
        this.markers?.remove(playerId);
        this.status?.setPlayer(this.session.username, this.countPlayers());
      },

      // Własny sen. Z zewnątrz to jest `playerLeft` i `playerJoined`; ta
      // wiadomość istnieje, bo budzi też serwer — sam, przy pierwszej rzeczy
      // wymagającej ciała na mapie.
      onRest: (building, slot) => this.settleRest(building, slot),

      onMoved: (playerId, position, slot) => {
        if (playerId === this.joined.playerId) {
          // Further than a step means somebody moved us — an admin, or the
          // world catching us up after a dropped socket. The camera goes along:
          // being put somewhere and left looking at where you used to be is the
          // one outcome nobody wants.
          const jumped = distance(this.standing, position) > 1;
          // A step is a step; being put somewhere by an administrator is not,
          // and a footstep for it would be a sound for something that did not
          // happen. The cadence comes from the server, so this needs no timer:
          // the message arrives when the foot lands.
          if (!jumped) this.audio?.step();
          this.standing = position;
          // Nowe pole, nowy panel: zgaszenie Escape'em dotyczyło tego, na
          // którym się wtedy stało. Odświeżenie od razu, bo pół sekundy zwłoki
          // przy wejściu na własny budynek widać jako panel, który się spóźnia.
          this.spotShut = false;
          this.refreshSpot();
          // Krok odkłada topór po tamtej stronie.
          this.felling = undefined;
          // Before anything else this step causes: arriving is the point of the
          // walk, and the rest of this branch is about the walk carrying on.
          this.finishErrand();
          this.route?.reached(position);

          // Standing somewhere else means a different ring is within reach, so
          // the marker under the cursor is answering about the old one. The
          // hover is invalidated rather than recomputed here: the next frame
          // does it anyway, and it is the frame that has the ray.
          if (this.buildKind) this.hovered = null;

          // Arrived: the walk is over and the X has nothing left to mark.
          if (
            this.walkingTo &&
            this.walkingTo.q === position.q &&
            this.walkingTo.r === position.r
          ) {
            this.walkingTo = null;
            // Unless a new destination has been picked on the way, in which
            // case the X is already marking that one instead.
            if (!this.chosenAt) this.mark?.hide();
            // Dopiero teraz jesteśmy u celu — panel pola, jeśli stoi tu nasz dom.
            this.refreshSpot();
          }

          // Both client lines start where we stand, so a step moves their near
          // end — including the one to a destination picked before we set off.
          // If the way there has gone, so has the pick: `choose` says so.
          if (this.chosenAt) this.choose(this.chosenAt);
          else this.updatePreview();

          if (jumped) {
            // Somewhere else entirely: the camera is put there rather than
            // scrolled, because scrolling across a continent would stream every
            // chunk on the way for a view nobody asked to see.
            const { x, z } = axialToWorld(position.q, position.r);
            this.cameraController.focusOn(x, z, true, this.groundAt(position));
            this.route?.clear();
            this.forget();
            this.status?.say('przeniesiono cię');
          }
          // Kamera **nie idzie już za wiadomością o kroku**: szłaby skokami, bo
          // wiadomości przychodzą polami. Idzie za bryłą, co klatkę — patrz
          // `followBody`.
        }

        this.markers?.moveTo(playerId, position, slot);
      },

      onRoute: (cells) => {
        this.route?.set(this.standing, cells);
        // **Trasa jest też torem, nie tylko rysunkiem.** Skoro serwer ją odesłał,
        // nie ma na co czekać: bryła sunie nią własnym zegarem, a wiadomości
        // o krokach są potwierdzeniem — patrz `Glide`.
        this.glideAlong(cells);
      },

      // Rozmowa: początek i koniec jedną drogą, bo taka przychodzi wiadomość.
      //
      // **Zatrzymanemu nie odwołuje się marszu.** Trasa jest na serwerze
      // wstrzymana, nie skasowana, a `callOff` wysłałoby `stop` — czyli
      // rozmowa kończyłaby się tym, że ktoś stoi tam, gdzie go zatrzymano,
      // zamiast iść dalej tam, gdzie szedł.
      onTalking: (talk) => {
        const started = !this.talk?.open;
        this.detained = !!talk.partner && !talk.mine;
        // **Rozmowa zatrzymuje bryłę, a serwer przestaje wtedy mówić o krokach.**
        // Sunięcie trasą trzeba więc zdjąć jawnie — inaczej obraz szedłby dalej
        // trasą, którą stoi. To jedyne miejsce poza odwołaniem marszu, w którym
        // ruch kończy się bez wiadomości o nim.
        if (talk.partner) this.stopGliding();
        if (talk.partner) this.chasing = false;
        this.talk?.show(talk);

        // **Zagadanie jest dojściem do celu i kończy marsz tak samo.** Serwer
        // przy dogonieniu wyrzuca pościg i trasę, więc linia prowadziłaby do
        // pola, na które nikt już nie idzie. Tylko po stronie zagadującego:
        // **zatrzymanemu trasa zostaje wstrzymana, a nie skasowana**, i po
        // rozmowie pójdzie nią dalej — skasowanie rysunku byłoby wtedy
        // kłamstwem o tym, co się zaraz stanie.
        if (talk.partner && talk.mine) {
          this.route?.clear();
          this.walkingTo = null;
          this.mark?.hide();
        }

        // Tylko na początku rozmowy: ta sama wiadomość przychodzi po każdej
        // kwestii, a linia stanu powtarzana co trzydzieści sekund mówiłaby
        // o czymś, co widać w panelu przez cały czas.
        if (talk.partner && !talk.mine && started) {
          this.status?.say(`${talk.partner.name ?? 'ktoś'} cię zatrzymał`);
        }
      },

      // Arrives for our own placement too, as a step does. `add` is keyed by
      // identity, so the copy that came back from the POST and the copy that
      // came over the socket are the same building.
      onPurse: (items) => {
        this.carried = items;
        this.purse?.set(items);
      },

      onEstate: (buildings) => this.holdings(buildings),

      // The same building, a rung higher. Replaced rather than added: the
      // silhouette stays, only what it says about itself changes.
      onRoads: (cells) => this.takeRoads(cells),

      onBanner: (playerId, banner, house) => {
        this.markers?.setBanner(playerId, banner);
        this.buildings?.setOwner(playerId, banner, house);
        this.updateTooltip();
      },

      onFellingStarted: (cell, by, from, seconds) => {
        if (this.fellingTrees?.start(cell, from, seconds)) this.streamer?.repaint([cell]);
        if (by === this.joined.playerId && !this.felling) {
          this.felling = { at: cell, until: performance.now() + seconds * 1000, logs: 0 };
        }
      },

      onFellingStopped: (cell, by) => {
        if (this.fellingTrees?.stop(cell)) this.streamer?.repaint([cell]);
        if (by === this.joined.playerId) this.felling = undefined;
      },

      onFelled: (cells, by, logs) => {
        // Przed wyzerowaniem lasu: drzewa, które już stoją osobno, mają się
        // przewrócić, a nie zniknąć razem z przerysowaną partią.
        for (const cell of cells) this.fellingTrees?.fell(cell);
        this.takeFelled(cells);
        if (by !== this.joined.playerId) return;
        this.felling = undefined;
        const at = cells[0];
        const ground = at ? this.groundAt(at) : undefined;
        if (at && ground !== undefined) {
          const { x, z } = axialToWorld(at.q, at.r);
          this.pops?.show(new THREE.Vector3(x, ground + HEX_SIZE, z), [{ material: 'MAT_LOGS', amount: logs }], 'gain');
        }
        this.status?.say('drzewa wycięte');
      },

      onShip: (ship, id) => {
        if (ship) this.ships?.set(ship);
        else if (id) this.ships?.remove(id);
      },
      /**
       * Pomiar rozjazdu, wypisany w jednym miejscu.
       *
       * Obraz idzie własnym zegarem i nie czeka na potwierdzenia, więc pytanie
       * „o ile te zegary się rozchodzą" nie ma innej odpowiedzi niż zmierzona.
       * Cztery liczby obok siebie: plan i wykonanie po każdej stronie.
       */
      /**
       * Pomiar rejsu i marszu: ile zajął serwerowi, ile obrazowi.
       *
       * **Odkładany, a nie wypisywany od razu.** Serwer kończy trasę wcześniej
       * niż obraz — z założenia o jeden krok, bo wchodzi na pole i dopiero potem
       * odczekuje jego koszt, a wygładzenie to przejście dopiero zaczyna.
       * Wypisany w tej chwili miałby **zawsze** puste miejsce po stronie obrazu,
       * a przy następnej trasie odczytałby liczbę z poprzedniej.
       */
      onTravelled: (took, planned) => {
        this.travelled = { took, planned };
        this.reckon();
      },

      onShipMoved: (id, at) => {
        this.ships?.moveTo(id, at);
        // Statek, którym płynę, niesie też mnie: moje pole jest jego polem,
        // bo od niego zależy, co widzę i skąd liczy się następna trasa.
        if (this.aboard === id) {
          this.standing = { q: at.q, r: at.r };
          // **Sprawa w drodze domyka się też na wodzie.** Pytana po każdym
          // kroku i o zasięg, nie o dojście do celu — tak samo jak przy
          // marszu: pod brzegiem wolno już zejść, choćby trasa prowadziła
          // jeszcze o pole dalej.
          this.finishErrand();
          this.route?.reached(at);
          // Kamera idzie za bryłą, nie za tą wiadomością — patrz `followBody`.

          // **Dopłynięcie kończy rejs dokładnie tak, jak dojście kończy marsz.**
          // Bez tego X zostawał na wodzie po tym, jak statek już przy nim stał:
          // pieszo domyka to `onMoved`, a kadłub chodzi własną wiadomością
          // i o tę samą rzecz trzeba go zapytać osobno.
          if (this.walkingTo && this.walkingTo.q === at.q && this.walkingTo.r === at.r) {
            this.walkingTo = null;
            if (!this.chosenAt) this.mark?.hide();
          }

          // Oba podglądy zaczynają się tam, gdzie jesteśmy, więc krok przesuwa
          // ich bliższy koniec — tak samo jak przy marszu.
          if (this.chosenAt) this.choose(this.chosenAt);
          else this.updatePreview();

        }
      },
      onConvoy: (id, at) => {
        if (at) this.convoys?.set(id, at);
        else this.convoys?.remove(id);
      },

      // Wóz zabrał towar z własnego zakładu. Minus, bo z magazynu ubyło —
      // towar dalej jest tego gracza, ale nie jest już tam, gdzie stoi napis.
      onShipped: (building, material, amount) => {
        this.popOver(building, [{ material, amount }], 'loss');
      },

      onBuildingChanged: (building) => {
        this.buildings?.replace(building);
        this.updateOwned();
        this.updateTooltip();
      },

      // Somebody in the panel moved the sky. Taken outright rather than eased
      // into: an administrator asking for dusk wants dusk, not a sunset that
      // arrives in a minute.
      // Tryb testowy: ceny i czasy zmieniają się dla wszystkich naraz, więc
      // przychodzi to wiadomością, a nie przy następnym wejściu do gry.
      onGodmode: (on) => {
        this.godmode = on;
        this.panel?.setGodmode(on);
        this.refreshSpot();
        this.status?.say(on ? 'tryb testowy włączony' : 'tryb testowy wyłączony');
      },

      onSky: (sky) => {
        this.daylight?.resync(sky);
        if (sky.clock !== undefined) this.worldClock = sky.clock;
        this.weather?.pin(sky.cover);
      },

      onBuildingPlaced: (building) => {
        if (this.buildings?.add(building)) this.streamer?.settle(this.buildings.ground([building]));
        this.updateOwned();
      },

      onWorldChanged: () => {
        // Every chunk in memory belongs to a map the server has stopped
        // serving, and so does the cell this player was standing on. Reloading
        // is the honest answer: there is nothing here worth keeping.
        this.status?.say('świat został podmieniony — wczytuję od nowa');
        this.status?.setLink('rozłączono', 'lost');
        window.setTimeout(() => window.location.reload(), 1500);
      },

      onError: (code, message) => {
        // A walk that was refused is an errand that will never arrive. Said
        // once, by the server's own words below, rather than twice.
        if (code === 'noWay' || code === 'tooFar') this.errand = undefined;
        // Odmowa trasy albo tłok, przez który nie da się przejść: marsz się nie
        // wydarzy, więc obraz nie ma czym sunąć.
        if (code === 'noWay' || code === 'tooFar' || code === 'noRoom') this.stopGliding();
        if (code === 'auth') {
          // The token is no good any more; keeping it would mean retrying a
          // refusal for as long as the tab is open.
          forget();
          window.setTimeout(() => window.location.reload(), 1200);
        }
        if (code === 'idle') {
          // Shown out of the world, but still logged in: the session is fine
          // and the way back is the player's page, which is where a reload
          // lands. The reason is left where that page will find it — after the
          // reload there is nothing left in memory to carry it.
          this.status?.setLink('rozłączono', 'lost');
          noteForPlayerPage(message);
          window.setTimeout(() => window.location.reload(), 1800);
        }
        this.status?.say(message);
      },

      onStatus: (state) => {
        if (state === 'live') this.status?.setLink('połączono', 'live');
        else if (state === 'closed') this.status?.setLink('rozłączono', 'lost');
        else this.status?.setLink(state === 'connecting' ? 'łączę…' : 'wznawiam…', 'waiting');
      },
    });

    this.connection.open();
  }

  /**
   * Tell the server where the camera settled, once it has.
   *
   * A second of stillness is the test. Reporting while it moves would send a
   * message per frame of a drag; reporting only on leaving would lose it every
   * time a tab is closed the way tabs are actually closed.
   */
  private reportView(deltaTime: number): void {
    const view = this.cameraController.view;

    const differs = (from?: { yaw: number; zoom: number }) =>
      !from || Math.abs(view.yaw - from.yaw) > 1e-4 || Math.abs(view.zoom - from.zoom) > 1e-4;

    // Still means unchanged since the previous frame, not unchanged since the
    // last report — the difference between the two is a message every frame of
    // a long drag.
    if (differs(this.seenView)) {
      this.seenView = { ...view };
      this.stillFor = 0;
      return;
    }

    if (!differs(this.reportedView)) return;

    this.stillFor += deltaTime;
    if (this.stillFor < 1) return;

    this.stillFor = 0;
    this.reportedView = { ...view };
    this.connection?.look(view.yaw, view.zoom);
  }

  /**
   * A cell as the debug lines want it: the one under the player, or the one
   * under the cursor.
   *
   * The ground may not be loaded — a step lands before its chunk does, and the
   * camera can be somewhere else entirely — so height and biome are allowed to
   * be missing while the coordinates are not. Under the cursor even the
   * coordinates can be missing, because the cursor is often over nothing.
   */
  /**
   * What to call this cell: the climate read together with what grows on it.
   *
   * The map draws it the same way, so the HUD saying "temperate" over a cell
   * drawn as woodland would be two answers to one question.
   */
  private biomeNameOf(cell: HexCellData): string {
    if (cell.biome === undefined) return `biom ${cell.biome}`;

    const name = this.biomeNames.get(cell.biome);
    if (!name) return `biom ${cell.biome}`;
    return cell.forest && name.wooded ? name.wooded : name.bare;
  }

  private describeCell(at: Axial | null): Cell | null {
    if (!at) return null;

    const cell = this.world.get(axialKey(at.q, at.r));

    return {
      q: at.q,
      r: at.r,
      elevation: cell?.elevation ?? null,
      biome:
        cell?.biome === undefined
          ? null
          : this.biomeNameOf(cell),
    };
  }

  private countPlayers(): number {
    // Własny pionek znika na czas odpoczynku, a gracz nie. Liczba mówi, ilu jest
    // w zasięgu, a nie ilu widać — inaczej samotny odpoczywający czytałby
    // „0 graczy" o świecie, w którym stoi.
    return [...(this.markers?.labels() ?? [])].length + (this.restingIn ? 1 : 0);
  }

  /** The cell the camera is looking at. */
  private focusCell(): { q: number; r: number } {
    const focus = this.cameraController.focusPoint;
    const { q, r } = worldToAxial(focus.x, focus.z);
    return roundAxial(q, r);
  }

  private drawRadius(): number {
    return requestedDrawRadius() ?? this.streamer!.drawRadiusFor(this.camera);
  }

  /**
   * Keep the loaded terrain in step with the camera.
   *
   * Called every frame but cheap when nothing moved: the streamer compares the
   * neighbourhood it wants against the one it has and returns immediately when
   * they match. Deliberately not awaited — a fetch in flight must not hold up
   * the frame that asked for it.
   */
  private updateTerrain(): void {
    if (!this.streamer) return;

    // **Przed wyjściem poniżej, nie za nim.** Przybliżenie zmienia się bez
    // ruszania punktu skupienia, a promień rysowania jest kwantowany do
    // pierścieni — więc kamera potrafi przejechać przez próg głazów, nie
    // zmieniając niczego, co ten wczesny powrót porównuje.
    this.streamer.setZoom(this.camera.zoom);

    const focus = this.focusCell();
    const radius = this.drawRadius();
    const last = this.streamedFocus;
    if (last && last.q === focus.q && last.r === focus.r && last.radius === radius) {
      return;
    }
    this.streamedFocus = { ...focus, radius };

    void this.streamer
      .update(focus, radius)
      .then(() => {
        this.hud?.setCounts(this.streamer!.stats);
        // The ground under the player is what the opening move needs to know
        // before it can begin, and it is also what says the world is ready.
        const ground = this.groundAt(this.standing);
        if (ground !== undefined) {
          if (!this.seated) {
            this.cameraController.seatAt(ground);
            this.seated = true;
          }
          // Separate from the seating on purpose: somebody grabbing the camera
          // takes the seating away — it is their view then — but it must not
          // also swallow the opening move and leave the fog standing.
          if (!this.worldReady) {
            this.worldReady = true;
            this.begin();
          }
        }

        // Ground that has just arrived is ground a pawn may have been standing
        // above, waiting for a height to sit on.
        this.markers?.refresh();
        this.convoys?.refresh();
        this.ships?.refresh();
        this.route?.refresh();
        this.chosen?.refresh();
        this.preview?.refresh();
        this.mark?.refresh();
        // Buildings come with the ground they stand on, so the fetch rides the
        // same trigger — and what is already held is re-seated, because a
        // building can have been waiting for its cell to arrive.
        this.refreshBuildings();
        this.buildings?.refresh();
        this.streamer?.settle(this.buildings?.staleGround() ?? []);
        // A cell arrives without its road: the listing and the chunks are two
        // streams and nothing says which lands first.
        this.applyRoads();
        // Teren z pamięci przeglądarki może pamiętać las sprzed wycinki.
        this.streamer?.repaint(this.applyFelled());
        this.buildGhost?.refresh();
      })
      .catch((error) => {
        // Forget the pass, so the next frame tries again rather than leaving a
        // hole in the map until the camera happens to move somewhere else
        this.streamedFocus = undefined;
        console.error('streaming chunków:', error);
      });
  }

  /**
   * Setup camera controls
   */
  private setupControls(): void {
    this.cameraController = new CameraController(
      this.camera,
      this.renderer.domElement
    );
    this.cameraController.snapping = snapWanted();

    // Reaching for the camera by hand ends the follow. The controller knows
    // which inputs count as reaching for it — a drag and WASD, not a spin or a
    // zoom — so the rule lives there and this only hears the outcome.
    this.cameraController.onUserPan = () => {
      this.following = false;
      // Their view now, not ours to adjust.
      this.seated = true;
    };

    this.picker = new HexPicker(
      this.renderer.domElement,
      this.camera,
      this.pickTargets
    );

    // A press and release in the same spot is an order to walk; anything with
    // travel in between was the camera being panned with the same button.
    const canvas = this.renderer.domElement;
    canvas.addEventListener('mousedown', this.onPressed);
    canvas.addEventListener('mouseup', this.onReleased);
    // Browsers keep an AudioContext suspended until the page has been touched,
    // and that is not an obstacle to work around: a game that starts talking
    // before anybody has clicked on it is why the rule exists. Both routes in
    // count, because the keyboard alone is enough to play.
    canvas.addEventListener('mousedown', this.onFirstGesture);
    window.addEventListener('keydown', this.onFirstGesture);

    // On window rather than the canvas, which never takes focus
    window.addEventListener('keydown', this.onKeyDown);
  }

  /** Where the right button went down, to tell a click from an orbit. */
  private pressedRight?: { x: number; y: number };

  private onFirstGesture = (): void => {
    void this.audio?.unlock();
  };

  private onPressed = (event: MouseEvent): void => {
    // Any press puts an open menu away, including the one that is about to open
    // another: a menu is answered by choosing from it or by looking elsewhere.
    this.menu?.hide();

    // The right button already orbits the camera, and the controller keeps it.
    // What is recorded here is only where it went down, so that a press which
    // turned into a drag can be told from one that did not.
    if (event.button === 2) {
      this.pressedRight = { x: event.clientX, y: event.clientY };
      return;
    }

    if (event.button !== 0 || event.altKey) return;
    this.pressed = { x: event.clientX, y: event.clientY, at: performance.now() };
  };

  private onReleased = (event: MouseEvent): void => {
    if (event.button === 2) {
      const spun = this.pressedRight;
      this.pressedRight = undefined;
      // Distance only, no deadline. The gesture this has to be told apart from
      // is orbiting, which is about travel; a slow right-click is still a
      // right-click, unlike a slow left one, which is a pan.
      if (spun && Math.hypot(event.clientX - spun.x, event.clientY - spun.y) <= Game.CLICK_SLOP) {
        this.openMenu(event);
      }
      return;
    }

    const pressed = this.pressed;
    this.pressed = undefined;

    if (!pressed || event.button !== 0) return;

    const travelled = Math.hypot(event.clientX - pressed.x, event.clientY - pressed.y);
    if (travelled > Game.CLICK_SLOP || performance.now() - pressed.at > Game.CLICK_MS) {
      return;
    }

    const cell = this.hoveredCell;
    if (!cell) return;

    // Zatrzymany nie działa. **Brama po tej stronie jest uprzejmością, nie
    // regułą** — regułą jest ta na serwerze, która odmówi każdemu żądaniu; ta
    // istnieje, żeby kliknięcie nie rysowało trasy, po której nikt nie pójdzie,
    // i nie stawiało ducha budynku, który zaraz zniknie.
    if (this.detained) {
      this.status?.say('ktoś cię zatrzymał — poczekaj');
      return;
    }

    // Laying a road takes the click before anything else does: the mode is the
    // deliberate half, and while it is on nothing else is what a click means.
    // **SHIFT znaczy „jeszcze raz".** Bez niego postawienie kończy akcję
    // i wraca do stanu, w którym klik znowu jest wyborem celu; z nim
    // budowanie trwa — kolejny budynek tego samego rodzaju, a przy drodze
    // **ciąg dalszy** od miejsca, w którym poprzedni odcinek się skończył.
    const chain = event.shiftKey;

    if (this.roadMode) {
      this.clickRoad(cell, chain);
      return;
    }

    if (this.bridgeMode) {
      this.clickBridge(chain);
      return;
    }

    // A building is picked: the click is a placement, not a destination. One
    // click, not two — the panel is already the deliberate half of it, and the
    // marker under the cursor has been saying yes or no the whole time.
    if (this.buildKind) {
      this.tryBuild(cell, chain);
      return;
    }

    // Two clicks, and the first one is not an order. It picks the cell and
    // draws the way there, so a walk across a continent can be looked at before
    // it is asked for — and so the cursor, which up to then draws nothing, has
    // something to compare against.
    const picked = this.chosenAt;
    if (!picked || picked.q !== cell.q || picked.r !== cell.r) {
      this.choose(cell);
      return;
    }

    // The same cell again: now it is an order — **także wtedy, gdy ktoś na nim
    // stoi**. Drugie kliknięcie znaczyło kiedyś „zagadaj", a uzasadnieniem było,
    // że na cudzym polu nie ma po co stawać; odkąd pole mieści sześcioro,
    // przestało to być prawdą, a gest, który raz znaczy marsz, a raz rozmowę,
    // zależnie od tego, kto gdzie stanął, odbierał jedyną drogę do wejścia
    // między ludzi. Zagadanie zostaje tam, gdzie widać, że jest: w menu pod
    // prawym przyciskiem.
    // Klik w łóżko, w którym się leży, nie jest rozkazem marszu.
    if (this.alreadyAsleepAt(cell)) {
      this.status?.say('już tu odpoczywasz');
      return;
    }

    // Wejście do własnej osady **nie** kładzie spać — odpoczynek jest pozycją
    // w menu i w panelu pola, który otworzy się po dojściu.
    this.orderWalk(cell);
  };

  /**
   * Panel pola: pokaż to, na czym stoimy, albo schowaj.
   *
   * **Rozmowa wygrywa o miejsce przy krawędzi.** Ma licznik, termin i drugą
   * osobę po drugiej stronie — jest stanem, który mija; budynek pod nogami nie
   * ucieka. Zatrzymany i tak nie zrobi z nim niczego, bo serwer odmawia
   * wszystkich akcji, więc panel pokazywałby wtedy same odmówione pozycje.
   *
   * **Śpiący widzi osadę, w której śpi**, a nie pole, na którym nie ma go na
   * mapie. To jest ta sama myśl, co przy wejściu pod dach: panel staje się
   * wtedy widokiem wnętrza, z „Wstań" na wierzchu.
   */
  private refreshSpot(): void {
    if (!this.spot) return;

    if (this.talk?.open || this.spotShut || this.spotYield) {
      this.spot.hide();
      return;
    }

    // **Tylko u celu.** Przejście przez własny budynek w drodze gdzie indziej
    // nie jest wejściem do niego: panel czeka, aż marsz się skończy — dojściem
    // albo zatrzymaniem. Pościg też jest marszem. Śpiący nigdzie nie idzie.
    if (!this.restingIn && (this.walkingTo || this.chasing)) {
      this.spot.hide();
      return;
    }

    const held = this.restingIn
      ? this.buildings?.find(this.restingIn)
      : this.buildings?.placedAt(this.standing);
    // Z czasem budowy liczonym teraz: panel odświeża się dwa razy na sekundę
    // i licznik ma iść razem z nim.
    const placed = held && this.buildings?.fresh(held.id);
    if (!placed) {
      this.spot.hide();
      return;
    }

    const kind = this.catalogue?.buildings.find((entry) => entry.id === placed.kind);
    // Nazwa osady, nie rodzaj — ta sama zasada, co w nagłówku menu: „wioska"
    // nad panelem Białogrodu byłoby opisem katalogu zamiast nazwy miejsca.
    // Ludność pod nazwą: osada jest przede wszystkim ludźmi. Brak liczby znaczy
    // „to nie jest osada", a nie „nikt tu nie mieszka" — patrz `people` w estate.
    const people = this.estate.get(placed.id)?.people;
    const note = [
      placed.house ? `Własność rodu ${placed.house}` : undefined,
      people === undefined ? undefined : `mieszkańcy: ${people}`,
    ].filter((line): line is string => line !== undefined);
    // Pozycje wybrane stąd otwierają panele boczne obok, a nie przy krawędzi.
    const entries = this.menuFor(placed).map((entry) =>
      entry.choose
        ? {
            ...entry,
            choose: () => {
              this.sideOrigin = 'spot';
              try {
                entry.choose?.();
              } finally {
                this.sideOrigin = 'menu';
              }
            },
          }
        : entry
    );
    this.spot.show(
      nameOf(kind, placed),
      entries,
      note.length > 0 ? note.join('\n') : undefined,
      this.factsOf(placed, kind),
      // Szczebel, który **stoi**, nie ten, który się buduje — jak bryła na mapie.
      kind?.levels[placed.level - 1]?.model ?? kind?.model
    );
  }

  /**
   * Co panel ma o tym budynku do powiedzenia poza pozycjami menu.
   *
   * **Tylko o własnym.** Cudzy nie ma nawet pozycji (`menuFor` oddaje dla niego
   * pustą listę), a jego stawka i magazyn są jego sprawą — tyle o nim mówi
   * dymek pod kursorem i tyle wolno wiedzieć.
   *
   * Liczby biorą się z `estate`, czyli stamtąd, skąd bierze je dymek i skąd
   * bierze je wyszarzenie „Rozbuduj". Trzecie miejsce liczące to samo byłoby
   * trzecim, które kiedyś pokaże coś innego.
   */
  private factsOf(placed: Placed, kind: BuildingKind | undefined): Fact[] {
    if (placed.owner !== (this.joined?.playerId ?? '')) return [];

    const facts: Fact[] = [];
    const level = kind?.levels[placed.level - 1];
    facts.push({ label: 'szczebel', value: level?.label ?? `${placed.level}` });

    if (placed.readyIn > 0) {
      facts.push({ label: 'buduje się', value: countdown(placed.readyIn) });
    }

    const holding = this.estate.get(placed.id);
    // `formOf` oddaje **samą formę słowa**, bez liczby — po to została kiedyś
    // wydzielona z `countOf`: „2,6 kłody na godzinę" potrzebuje odmiany do
    // liczby, której obok nie wypisuje.
    const named = (material: string, amount: number) =>
      this.materials ? formOf(this.materials, material, amount) : material;
    const icon = (material: string) =>
      this.materials ? iconOf(this.materials, material) : '';

    // Produkcja godzinna: to, co wychodzi na godzinę, surowiec po surowcu.
    // `perHour` liczy **sztuki zdobyczy**, a jedna sztuka bywa dwiema rzeczami
    // naraz — stąd mnożenie przez `each`, tak samo jak w dymku.
    if (holding?.perHour !== undefined && holding.makes?.length) {
      facts.push({ head: 'produkcja' });
      for (const made of holding.makes) {
        const hourly = holding.perHour * made.each;
        facts.push({
          label: `${icon(made.material)} ${named(made.material, hourly)}`,
          value: `${hourly.toFixed(1).replace('.', ',')}/h`,
        });
      }
      if (holding.base !== undefined) {
        facts.push({
          label: 'bazowo',
          value: `${holding.base.toFixed(1).replace('.', ',')}/h`,
        });
      }
    }

    // Modyfikatory tylko tam, gdzie cokolwiek zmieniają: budynek bez zasobności
    // i bez gruntu nie dostaje pustego nagłówka „modyfikatory: brak".
    if (holding?.modifiers?.length) {
      facts.push({ head: 'modyfikatory' });
      for (const modifier of holding.modifiers) {
        facts.push({ label: modifier.label, value: modifier.effect });
      }
    }

    // Magazyn: co w nim leży teraz. Do tej pory widać to było wyłącznie
    // w dymku pod kursorem — a stojąc na budynku, kursor jest gdzie indziej.
    const store = holding?.store?.filter((item) => item.amount > 0) ?? [];
    if (store.length > 0) {
      facts.push({ head: 'magazyn' });
      for (const item of store) {
        facts.push({
          label: `${icon(item.material)} ${named(item.material, item.amount)}`,
          value: `${item.amount}`,
        });
      }
    }

    return facts;
  }

  /**
   * Ile łóżek ma ten budynek na szczeblu, na którym stoi.
   *
   * Jedno pytanie, dwie odpowiedzi do wyciągnięcia: czy w menu ma być
   * „Odpocznij" i czy wejście na to pole ma kłaść spać. Dwa miejsca liczące to
   * osobno rozeszłyby się w dniu, w którym łóżka dostanie coś jeszcze.
   *
   * `levels` czyta się o jeden niżej niż przy rozbudowie: `beds` opisuje
   * **szczebel, na którym budynek stoi**, a tamta pyta o szczebel następny.
   */
  private bedsAt(placed: Placed): number {
    const kind = this.catalogue?.buildings.find((entry) => entry.id === placed.kind);
    return kind?.levels[placed.level - 1]?.beds ?? 0;
  }

  /**
   * Czy to kliknięcie trafiło w osadę, w której już się śpi.
   *
   * Wtedy nie ma po co nigdzie iść — a **marsz obudziłby**, bo dla serwera
   * każde `goTo` jest wyjściem z domu (patrz „Odpoczynek" w `buildings.md`).
   * Bez tego pytania klik we własne łóżko stawiałby śpiącego na nogi i zostawiał
   * przed drzwiami.
   */
  private alreadyAsleepAt(cell: Axial): boolean {
    if (!this.restingIn) return false;
    const placed = this.buildings?.placedAt(cell);
    return !!placed && placed.id === this.restingIn;
  }

  /**
   * Wyślij gracza w drogę.
   *
   * The destination goes to the server, which finds the way and walks it — what
   * was drawn here was a question, and the rules for what counts as a step live
   * on the other side of the wire.
   *
   * One method rather than two, because there are now two ways to set off: a
   * second click on a picked cell, and an errand that has to walk before it can
   * be done. Everything that follows from ordering a walk — the X, the dropped
   * pick, the camera coming back — has to follow from both.
   */
  private orderWalk(cell: Axial): void {
    // **Trasa jedzie razem z rozkazem** — ta sama, którą liczy podgląd i którą
    // za chwilę popłynie obraz. Serwer sprawdza każdy krok; gdy trasy nie ma —
    // bo cel leży poza wczytanym terenem — szuka jej sam, i wtedy różnica
    // między tym, co widać, a tym, czym się idzie, jest **jedyną**, jaka
    // zostaje.
    const drawn = findRoute(this.world, this.standing, cell, this.aboard ? 'sail' : 'walk');
    this.connection?.goTo(cell, drawn ?? undefined);
    // Nowa trasa odkłada topór po tamtej stronie; licznik gaśnie razem z nią.
    this.felling = undefined;

    // The pick is spent, but the X is not: it marks the destination, and the
    // destination is now the thing being walked to.
    this.chosenAt = null;
    this.chosen?.clear();
    this.preview?.clear();
    this.previewTo = null;
    this.walkingTo = cell;
    this.mark?.showAt(cell);

    // Ordering a walk is asking to watch it. The camera comes to the player
    // now rather than on their first step, so a click from across the map
    // scrolls back to them even if the way there turns out to be refused.
    this.following = true;
    const { x, z } = axialToWorld(this.standing.q, this.standing.r);
    this.cameraController.glideTo(x, z, this.groundAt(this.standing));
  }

  /**
   * What this player's own buildings hold and make, as the server last said.
   *
   * Replaced whole rather than merged, because that is what arrives: a listing
   * of everything they own. A building missing from it is one they no longer
   * own, and merging would keep showing a store that is not theirs.
   */
  private holdings(list: Holding[]): void {
    this.estate = new Map(list.map((holding) => [holding.id, holding]));
    this.estateAt = performance.now();
    // Whatever the cursor is on says the old numbers until it is told.
    this.updateTooltip();
  }

  /**
   * The box over whatever the cursor is on.
   *
   * Driven from the cell under the cursor, which is already computed once a
   * frame for the highlight — and which now answers with the building's own
   * cell when the cursor is over its silhouette, because the meshes are pick
   * targets. Pointing at a roof therefore means pointing at that building
   * rather than at the ground behind it.
   *
   * Silent in build mode: the cursor is carrying a ghost and saying where it
   * may go, and a box describing what is already there would be answering a
   * different question than the one being asked.
   */
  private updateTooltip(): void {
    if (!this.tip) return;

    const cell = this.buildKind ? null : this.hoveredCell;
    const found = cell ? this.buildings?.under(cell) : undefined;
    if (!found) {
      this.tip.hide();
      return;
    }

    this.tip.show(found.placed, found.anchor, this.estate.get(found.placed.id), this.estateAt);
  }

  /**
   * What can be done with the building under the cursor.
   *
   * Only over a building: right-clicking open ground opens nothing, because
   * there is nothing to list. Silent in build mode for the reason the tooltip
   * is — the cursor is carrying a ghost and answering a different question.
   */
  private openMenu(event: MouseEvent): void {
    if (!this.menu || this.buildKind) return;

    // Menu w zatrzymaniu byłoby listą pozycji, z których każda kończy się
    // odmową serwera — patrz ta sama brama w `onReleased`.
    if (this.detained) {
      this.menu.hide();
      this.status?.say('ktoś cię zatrzymał — poczekaj');
      return;
    }

    const cell = this.hoveredCell;
    const found = cell ? this.buildings?.under(cell) : undefined;
    if (!found) {
      // Nie ma budynku — może stoi tam ktoś. Menu jest jedno i to samo dla
      // budynku, pionka i wozu: prawy przycisk zawsze znaczy „co da się z tym
      // zrobić", a trzy różne odpowiedzi na jedno pytanie byłyby trzema
      // gestami do zapamiętania.
      if (cell && this.openShipMenu(event, cell)) return;
      if (cell && this.openWhoMenu(event, cell)) return;
      if (cell && this.openFellMenu(event, cell)) return;
      this.menu.hide();
      return;
    }

    const entries = this.menuFor(found.placed);
    if (entries.length === 0) {
      this.menu.hide();
      return;
    }

    // Pytanie o budynek, na którym się stoi, jest też prośbą o panel, który
    // wcześniej zgaszono Escape'em — ten sam gest przywraca go i otwiera menu.
    if (this.spotShut) {
      this.spotShut = false;
      this.refreshSpot();
    }

    const kind = this.catalogue?.buildings.find((entry) => entry.id === found.placed.kind);
    const room = this.container.getBoundingClientRect();
    this.menu.show(
      event.clientX - room.left,
      event.clientY - room.top,
      entries,
      // Osada nazywa się swoją nazwą, a nie rodzajem: nagłówek „wioska" nad
      // menu Białogrodu byłby opisem katalogu zamiast nazwy miejsca. Warownia
      // czyta się z tytułem szczebla — patrz `nameOf`.
      nameOf(kind, found.placed)
    );
  }

  /**
   * Menu nad pionkiem albo wozem. Odpowiada, czy było nad czym je otworzyć.
   *
   * **Jedyne wejście w rozmowę i w śledzenie.** Zagadanie było kiedyś także
   * drugim kliknięciem lewym w zajęte pole i to zostało wycofane: pole mieści
   * sześcioro, więc klik w nie musi znaczyć marsz, tak samo jak w każde inne.
   * Gest, który raz znaczy jedno, a raz drugie — zależnie od tego, kto gdzie
   * stanął — odbierał jedyną drogę do wejścia między ludzi.
   */
  private openWhoMenu(event: MouseEvent, cell: Axial): boolean {
    const target = this.whoAt(cell);
    if (!target || !this.menu) return false;

    const room = this.container.getBoundingClientRect();
    this.menu.show(
      event.clientX - room.left,
      event.clientY - room.top,
      [
        {
          label: 'Porozmawiaj',
          hint: this.detained ? 'ktoś cię zatrzymał' : 'dogonię go i zatrzymam na rozmowę',
          enabled: !this.detained,
          choose: () => this.setOffAfter(cell, true),
        },
        {
          label: 'Śledź',
          hint: this.detained ? 'ktoś cię zatrzymał' : 'pójdę za nim i nie odstąpię',
          enabled: !this.detained,
          choose: () => this.setOffAfter(cell, false),
        },
      ],
      target.name
    );
    return true;
  }

  /**
   * Kto stoi na tym polu: pionek przed wozem.
   *
   * Kolejność jest dowolna tylko z pozoru — na jednym polu może stać i człowiek,
   * i wóz, a wtedy pyta się o człowieka, bo z nim da się zrobić więcej.
   */
  private whoAt(cell: Axial): { mark: Mark; name: string } | null {
    // Z punktem pod kursorem, a nie samym polem: na polu może stać sześcioro,
    // a menu otwarte nad kimś innym niż wskazany jest gorsze niż brak menu.
    const who = this.markers?.whoIsAt(cell, this.picker?.lastPoint ?? undefined);
    if (who) {
      return { mark: { kind: 'player', id: who.id }, name: who.username };
    }

    const cart = this.convoys?.whoIsAt(cell);
    if (cart) {
      return { mark: { kind: 'convoy', id: cart }, name: 'wóz handlarza' };
    }

    return null;
  }

  /**
   * Rusz za kimś — samo podążanie albo z zamiarem zagadania.
   *
   * **Trasy tu nie ma i nie może być.** Celem jest ktoś, kto idzie, więc drogę
   * wyznacza serwer i wyznacza ją od nowa przy każdym jego kroku; klient
   * dostaje kolejne `route` i rysuje to, co przyszło — dokładnie tak, jak przy
   * marszu na pole.
   */
  private setOffAfter(cell: Axial, talk: boolean): boolean {
    const target = this.whoAt(cell);
    if (!target) return false;

    if (this.detained) {
      this.status?.say('ktoś cię zatrzymał — poczekaj');
      return true;
    }

    this.connection?.follow(target.mark, talk);
    this.chasing = true;
    // Wskazanie celu przestaje mieć sens: idziemy za kimś, a nie na pole.
    this.unpick();
    this.status?.say(
      talk ? `idę zagadać: ${target.name}` : `idę za: ${target.name}`
    );
    return true;
  }

  /**
   * The entries for one building.
   *
   * **Nothing for somebody else's.** The tooltip already says what it is and who
   * put it up, and that is everything an outsider is allowed to know — a menu
   * with no usable entry in it would be a menu that exists to say no.
   *
   * The split between a workshop and a camp is made on **whether the catalogue
   * gives the kind any output at all**, not on its key. The camp is the only one
   * without today, and a client that recognised it by name would need editing
   * the first time a second such building was added to a file it is supposed to
   * read rather than know.
   */
  private menuFor(placed: Placed): MenuEntry[] {
    if (placed.owner !== (this.joined?.playerId ?? '')) return [];

    const kind = this.catalogue?.buildings.find((entry) => entry.id === placed.kind);
    const near = inCollectReach(this.standing, placed);

    // Składane z niezależnych kawałków, a nie rozgałęziane na „warsztat albo
    // obóz". Ten podział raz już był i był zły: budynek bez produkcji dostawał
    // wyłącznie „Odpocznij" i nigdy nie docierał do rozbudowy — a obóz jest
    // dokładnie tym, który się rozbudowuje. Każda pozycja pyta teraz o własną
    // flagę: wydaje, przyjmuje, ma dokąd awansować.
    // **Plac budowy nie oferuje tego, czego serwer na nim odmówi**: zbioru,
    // rozbudowy, odpoczynku i zmiany nazwy (`reach` z `ready`). Ukryte, a nie
    // wyszarzone — ile zostało do końca, mówi licznik, a lista samych „budowa
    // jeszcze trwa" nie mówi nic więcej. Dosypać wolno, i tak napełnia się obóz
    // pod rozbudowę.
    const site = placed.readyIn > 0;

    const entries: MenuEntry[] = [
      ...(site ? [] : this.collecting(placed, kind, near)),
      ...(site ? [] : this.raising(placed, kind, near)),
      ...(site ? [] : this.shipbuilding(placed, kind, near)),
      ...this.stocking(placed, kind, near),
    ];

    // Odpoczynek należy do budynku z łóżkami, a nie do „tego, który niczego nie
    // wydaje" — tak było przedtem i było to zgadywanie jednej flagi po drugiej.
    // `beds` opisuje **szczebel, na którym budynek stoi**, więc `levels` czyta
    // się o jeden niżej niż przy rozbudowie, która pyta o szczebel następny.
    // Kto już śpi pod rusztowaniem (położył się przed rozbudową), może wstać.
    const beds = this.bedsAt(placed);
    if (beds > 0 && (!site || this.restingIn === placed.id)) {
      entries.push(
        this.restingIn === placed.id
          ? {
              label: 'Wstań',
              hint: 'wracasz na mapę',
              enabled: true,
              choose: () => void this.getUp(),
            }
          : {
              label: near ? 'Odpocznij' : 'Idź i odpocznij',
              hint: 'znikasz z mapy, dopóki czegoś nie zrobisz',
              enabled: true,
              choose: () =>
                near ? void this.lieDown(placed.id) : this.travelTo(placed, 'rest'),
            }
      );
    }

    // Wysyłka należy do zakładu, czyli do tego, który cokolwiek wydaje. Ten sam
    // podział, co przy zbiorze: `dispenses` mówi, czy jest co wywozić.
    if (kind?.dispenses) {
      entries.push({
        label: 'Wysyłaj do osady',
        hint: 'wóz rusza po uzbieraniu',
        enabled: near,
        choose: () => this.askShipment(placed),
      });
    }

    // Nazwa należy do osady, a osadą jest ten szczebel, który dostał nazwę od
    // serwera — klient nie zgaduje po kluczu ani po poziomie, tylko patrzy, czy
    // nazwa w ogóle jest.
    if (placed.name && !site) {
      entries.push(
        placed.renamed
          ? { label: 'Zmień nazwę', hint: 'już zmieniona', enabled: false }
          : {
              label: 'Zmień nazwę',
              hint: 'tylko raz',
              enabled: near,
              choose: () => this.askName(placed),
            }
      );
    }

    entries.push({ label: 'Rozbierz', hint: 'jeszcze nie działa', enabled: false });
    return entries;
  }

  /** Pozycja zbioru — tylko tam, skąd wolno cokolwiek wynieść. */
  private collecting(placed: Placed, kind: BuildingKind | undefined, near: boolean): MenuEntry[] {
    if (!kind?.dispenses) return [];

    const holding = this.estate.get(placed.id);
    const held = withMade(holding, madeSince(holding, this.estateAt)).reduce(
      (sum, item) => sum + item.amount,
      0
    );
    const building = placed.readyIn > 0;

    return [
      {
        // Distance stopped being a refusal and became a journey, so the label
        // says which of the two this click is going to be.
        label: near ? 'Zbierz surowce' : 'Idź i zbierz',
        // One reason, the one that is in the way. Two of them stacked would be a
        // sentence about the rules rather than about this building.
        hint: building
          ? 'budowa jeszcze trwa'
          : held === 0
            ? 'nic tu jeszcze nie ma'
            : near
              ? undefined
              : 'dojdę tam i zbiorę',
        enabled: !building && held > 0,
        choose: () => (near ? this.tryHarvest(placed.id) : this.travelTo(placed, 'harvest')),
      },
    ];
  }

  /**
   * Pozycje rozbudowy: sam awans i skrót „dosyp ile trzeba".
   *
   * Warunki czyta ta sama funkcja, którą serwer ma po swojej stronie —
   * wyszarzenie przycisku przed kliknięciem jest tym, po co ona tu jest, a nie
   * drugą regułą. Odpowiedź serwera i tak jest tą, która obowiązuje.
   */
  /**
   * „Zbuduj statek" — pozycja stoczni.
   *
   * **Czy budynek umie budować statki, mówi `berths` szczebla**, a nie jego
   * klucz: klient rozpoznający stocznię po nazwie trzeba by poprawić przy
   * pierwszej drugiej takiej. Czego to kosztuje, mówi blok `ships` katalogu —
   * jedna lista dla wszystkich stoczni, bo statek jest jeden.
   *
   * Płaci **magazyn stoczni**, więc brakujące surowce czyta się stamtąd, tą
   * samą drogą, co przy rozbudowie.
   */
  private shipbuilding(
    placed: Placed,
    kind: BuildingKind | undefined,
    near: boolean
  ): MenuEntry[] {
    const berths = kind?.levels[placed.level - 1]?.berths ?? 0;
    if (berths === 0 || !this.catalogue) return [];

    const recipe = this.catalogue.ships;
    const holding = this.estate.get(placed.id);
    const store = withMade(holding, madeSince(holding, this.estateAt));
    const held = (material: string) =>
      store.find((item) => item.material === material)?.amount ?? 0;
    const named = (material: string, amount: number) =>
      this.materials ? formOf(this.materials, material, amount) : material;

    // W trybie testowym wszystko kosztuje jedną sztukę — ta sama zasada, co
    // przy rozbudowie, i ta sama liczba, którą policzy serwer.
    const wanted = (amount: number) => (this.godmode ? Math.min(1, amount) : amount);
    const short = recipe.cost.filter((charge) => held(charge.material) < wanted(charge.amount));
    const why = short.length
      ? `brakuje: ${short
          .map((charge) => named(charge.material, wanted(charge.amount) - held(charge.material)))
          .join(', ')}`
      : undefined;

    return [
      {
        label: near ? 'Zbuduj statek' : 'Idź i zbuduj statek',
        hint:
          why ??
          recipe.cost.map((charge) => named(charge.material, wanted(charge.amount))).join(', '),
        enabled: !why,
        choose: () =>
          near ? void this.tryBuildShip(placed.id) : this.travelTo(placed, 'ship'),
      },
    ];
  }

  /**
   * Zamów statek. Serwer rozstrzyga — i to on wie, czy przy pomoście jest
   * miejsce.
   */
  private async tryBuildShip(building: string): Promise<void> {
    const answer = await buildShip(this.session.token, building);
    if ('code' in answer) {
      this.status?.say(answer.message);
      return;
    }

    // Postawiony kadłub przychodzi też socketem, do wszystkich w zasięgu —
    // `set` jest idempotentne, tak jak przy budynku.
    this.ships?.set(answer);
    this.status?.say('kadłub stoi na pochylni');
  }

  private raising(placed: Placed, kind: BuildingKind | undefined, near: boolean): MenuEntry[] {
    const next = kind?.levels[placed.level];
    if (!next) return [];

    const holding = this.estate.get(placed.id);
    const store = withMade(holding, madeSince(holding, this.estateAt));
    const held = (material: string) =>
      store.find((item) => item.material === material)?.amount ?? 0;
    const named = (material: string, amount: number) =>
      this.materials ? formOf(this.materials, material, amount) : material;

    // Mieszkańcy z posiadłości: to samo źródło, co magazyn i stawka, więc
    // wyszarzenie „Rozbuduj" i odmowa serwera mówią o tej samej liczbie.
    const people = holding?.people ?? 0;
    const why = unmet(next, held, named, people, this.godmode);
    const building = placed.readyIn > 0;
    const short = missingFor(next, held, this.godmode);
    const carried = (material: string) =>
      this.carried.find((item) => item.material === material)?.amount ?? 0;
    // Only offered when the purse can actually close the gap — a shortcut that
    // fails is worse than no shortcut.
    const canTopUp =
      (kind?.accepts ?? false) &&
      short.length > 0 &&
      short.every((need) => carried(need.material) >= need.amount);

    const entries: MenuEntry[] = [
      {
        label: near ? `Rozbuduj${next.label ? `: ${next.label}` : ''}` : 'Idź i rozbuduj',
        hint: building ? 'budowa jeszcze trwa' : why,
        enabled: !building && !why,
        choose: () => (near ? this.tryUpgrade(placed.id) : this.travelTo(placed, 'upgrade')),
      },
    ];

    if (canTopUp && near) {
      entries.push({
        label: 'Dosyp ile trzeba',
        hint: short.map((need) => named(need.material, need.amount)).join(', '),
        enabled: true,
        choose: () => void this.topUp(placed.id, short),
      });
    }

    return entries;
  }

  /** Pozycja otwierająca formularz przekazania — tylko tam, gdzie wolno wsypać. */
  private stocking(placed: Placed, kind: BuildingKind | undefined, near: boolean): MenuEntry[] {
    const entries: MenuEntry[] = [];
    if (kind?.accepts) {
      const anything = this.carried.some((item) => item.amount > 0);
      entries.push({
        label: 'Przekaż surowce…',
        hint: !near ? 'podejdź bliżej' : anything ? undefined : 'nie masz nic przy sobie',
        enabled: near && anything,
        choose: () => this.openStore(placed, 'give'),
      });
    }
    // Z każdego własnego budynku, także z osady — ale nie z placu budowy, i tę
    // decyzję podejmuje `menuFor`, zanim tu zajrzy.
    if (placed.readyIn <= 0) {
      const inside = this.storeItems(placed).some((item) => item.amount > 0);
      entries.push({
        label: 'Wyciągnij surowce…',
        hint: !near ? 'podejdź bliżej' : inside ? undefined : 'magazyn jest pusty',
        enabled: near && inside,
        choose: () => this.openStore(placed, 'take'),
      });
    }
    return entries;
  }

  /** Co leży w magazynie budynku teraz — z produkcją policzoną do tej chwili. */
  private storeItems(placed: Placed): { material: string; amount: number }[] {
    const holding = this.estate.get(placed.id);
    return withMade(holding, madeSince(holding, this.estateAt));
  }

  /** Otwórz magazyn tego budynku na podanej zakładce. */
  private openStore(placed: Placed, tab: StoreTab): void {
    if (!this.store) return;
    this.menu?.hide();
    this.closeShip();
    this.placeSide();
    this.storeOf = placed.id;
    this.store.select(tab);
    this.refreshStore();
  }

  private closeStore(): void {
    this.storeOf = undefined;
    this.store?.hide();
    this.releaseSpot();
  }

  /**
   * Gdzie stanie panel boczny, który właśnie się otwiera — z miejsca, z którego
   * wybrano pozycję. Z menu: przy krawędzi, a panel pola ustępuje.
   */
  private placeSide(): void {
    this.sidePlace = this.sideOrigin === 'spot' && this.spot?.open ? 'beside' : 'edge';
    if (this.sidePlace === 'edge' && this.spot?.open) {
      this.spotYield = true;
      this.spot.hide();
    }
  }

  /** Panel boczny zamknięty: panel pola wraca, jeśli ustępował. */
  private releaseSpot(): void {
    if (!this.spotYield || this.store?.open || this.ship?.open) return;
    this.spotYield = false;
    this.refreshSpot();
  }

  private openShip(placed: Placed, form: HTMLElement): void {
    if (!this.ship) return;
    this.menu?.hide();
    this.closeStore();
    this.placeSide();
    this.shipOf = placed.id;
    this.ship.show('Wysyłka do osady', form, this.sidePlace);
  }

  private closeShip(): void {
    this.shipOf = undefined;
    this.ship?.hide();
    this.releaseSpot();
  }

  /**
   * Magazyn dwa razy na sekundę, razem z panelem pola.
   *
   * Gaśnie sam, gdy przestaje mieć sens: budynek zniknął albo jest placem
   * budowy przy wyciąganiu, gracz odszedł dalej niż na sąsiednie pole, rozmawia
   * albo śpi.
   */
  private refreshStore(): void {
    // Wysyłka gaśnie z tych samych powodów, co magazyn.
    if (this.shipOf) {
      const shipped = this.buildings?.fresh(this.shipOf);
      if (!shipped || !inCollectReach(this.standing, shipped) || this.talk?.open || this.restingIn !== null) {
        this.closeShip();
      }
    }
    // Panel pola mógł zniknąć (odejście, rozmowa) — wtedy panel boczny idzie do krawędzi.
    const beside = this.sidePlace === 'beside' && !!this.spot?.open;
    this.ship?.place(beside ? 'beside' : 'edge');
    if (!this.store || !this.storeOf) return;
    const placed = this.buildings?.fresh(this.storeOf);
    const tab = this.store.current;
    const gone =
      !placed ||
      !inCollectReach(this.standing, placed) ||
      this.talk?.open ||
      this.restingIn !== null ||
      (tab === 'take' && placed.readyIn > 0);
    if (gone || !placed || !this.materials) {
      this.closeStore();
      return;
    }

    const materials = this.materials;
    const kind = this.catalogue?.buildings.find((entry) => entry.id === placed.kind);
    const source = tab === 'give' ? this.carried : this.storeItems(placed);
    const items = source.map((item) => ({
      material: item.material,
      amount: Math.floor(item.amount),
      label: `${iconOf(materials, item.material)} ${labelOf(materials, item.material)}`,
    }));
    this.store.show(nameOf(kind, placed), items, beside);
  }

  /** Przenieś surowiec w stronę, którą mówi zakładka. */
  private async moveGoods(tab: StoreTab, material: string, amount: number): Promise<void> {
    const building = this.storeOf;
    if (!building) return;

    if (tab === 'give') {
      await this.topUp(building, [{ material, amount }]);
      this.refreshStore();
      return;
    }

    const answer = await withdrawBuilding(API, this.session.token, building, material, amount).catch(
      () => ({ code: 'broken', message: 'nie udało się wyciągnąć' }) as const
    );
    if ('code' in answer) {
      this.status?.say(answer.message);
      return;
    }
    // Z budynku ubyło — minus nad nim, tak samo jak przy wozie.
    this.popOver(building, [{ material, amount: answer.moved }], 'loss');
    this.status?.say('wyciągnięte');
    this.refreshStore();
  }

  /** Podnieś budynek o szczebel. Serwer sprawdza wszystko od nowa. */
  private tryUpgrade(building: string): void {
    if (!this.session) return;

    void upgradeBuilding(API, this.session.token, building)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        // The broadcast brings the same thing to everybody nearby; this is so
        // the one who asked does not wait for their own message to come back.
        this.buildings?.replace(answer.building);
        this.updateOwned();
        this.updateTooltip();
        // „Ruszyła", a nie „rozbudowano": szczebel dochodzi dopiero z czasem,
        // a napis mówiący, że budynek już nim jest, kłóciłby się z rusztowaniem,
        // które właśnie stanęło nad nim.
        this.status?.say(
          answer.label ? `rozbudowa ruszyła: ${answer.label}` : 'rozbudowa ruszyła'
        );
      })
      .catch((error) => {
        console.error('rozbudowa:', error);
        this.status?.say('nie udało się rozbudować');
      });
  }

  /**
   * Przenieś surowce z sakwy do budynku.
   *
   * Po jednym na żądanie, sekwencyjnie: każde jest osobną transakcją po tamtej
   * stronie, a pierwsza odmowa kończy sprawę — dosypywanie dalej po tym, jak
   * czegoś zabrakło, zostawiłoby połowę przeniesioną bez słowa.
   */
  private async topUp(
    building: string,
    short: { material: string; amount: number }[]
  ): Promise<void> {
    if (!this.session) return;

    for (const need of short) {
      const answer = await stockBuilding(
        API,
        this.session.token,
        building,
        need.material,
        need.amount
      ).catch(() => ({ code: 'broken', message: 'nie udało się przekazać' }) as const);

      if ('code' in answer) {
        this.status?.say(answer.message);
        return;
      }
    }
    this.status?.say('przekazane');
  }

  /**
   * Formularz nazwy, w tym samym pudełku co przekazywanie surowców.
   *
   * Pole tekstowe, a nie `prompt()`: okno przeglądarki zabiera grze klawiaturę
   * i wygląda jak ostrzeżenie systemu, a to jest nazwanie własnej wioski.
   *
   * **Ostrzeżenie stoi na wierzchu**, bo zmiana jest jedna i nie ma jak jej
   * cofnąć — a menu, które o tym nie mówi, powiedziałoby to dopiero przy drugiej
   * próbie.
   */
  private askName(placed: Placed): void {
    if (!this.menu) return;

    const form = document.createElement('div');
    form.className = 'building-menu-form';

    const warning = document.createElement('div');
    warning.className = 'building-menu-hint';
    warning.textContent = 'nazwę można zmienić tylko raz';
    form.appendChild(warning);

    const row = document.createElement('div');
    row.className = 'building-menu-row';

    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'building-menu-input';
    field.value = placed.name ?? '';
    field.maxLength = NAME_LETTERS;
    field.setAttribute('aria-label', 'nazwa');

    const send = document.createElement('button');
    send.type = 'button';
    send.className = 'building-menu-send';
    send.textContent = 'nazwij';

    const submit = () => {
      const wanted = field.value.trim();
      if (!wanted || wanted === placed.name) {
        this.menu?.hide();
        return;
      }
      this.menu?.hide();
      this.rename(placed.id, wanted);
    };

    send.addEventListener('click', submit);
    field.addEventListener('keydown', (event) => {
      // The key belongs to the field while the field has it: Escape here closes
      // the form, not everything the player was in the middle of.
      event.stopPropagation();
      if (event.key === 'Enter') submit();
      if (event.key === 'Escape') this.menu?.hide();
    });

    row.append(field, send);
    form.appendChild(row);

    const at = this.buildings?.under({ q: placed.q, r: placed.r });
    const spot = at
      ? screenPlacer(this.container, this.renderer.domElement, this.camera)(at.anchor)
      : null;
    const room = this.container.getBoundingClientRect();
    this.menu.showPanel(
      spot ? spot.x : room.width / 2,
      spot ? spot.y : room.height / 2,
      form,
      'Nazwa osady'
    );

    field.focus();
    field.select();
  }

  private rename(building: string, name: string): void {
    void renameBuilding(API, this.session.token, building, name)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        this.buildings?.replace(answer);
        // Bez słowa „osada": warownia osadą nie jest, a nazwę zmienia tak samo.
        // Nazwa złożona, bo taka stanie nad budynkiem — kto zmienił nazwę, ma
        // zobaczyć to, co zobaczy reszta świata.
        const kind = this.catalogue?.buildings.find((entry) => entry.id === answer.kind);
        this.status?.say(`nazwa: ${nameOf(kind, answer)}`);
      })
      .catch((error) => {
        console.error('zmiana nazwy:', error);
        this.status?.say('nie udało się zmienić nazwy');
      });
  }

  /**
   * Idź tam i zbierz.
   *
   * **Cel marszu to nie zawsze pole budynku.** Siedlisko wolno postawić na polu,
   * przez które nie da się przejść — reguła miejsca wymaga tylko, żeby dało się
   * **przy nim** stanąć, i po to właśnie istnieje warunek `reachable`. Chata
   * w środku gąszczu jest więc zwykłym przypadkiem, a nie osobliwością: droga
   * idzie wtedy do pierwszego sąsiada, do którego droga jest.
   *
   * Trasa liczona jest tu, tą samą funkcją, którą klient rysuje podgląd — więc
   * „tam nie da się dojść" pada od razu, zamiast po marszu zakończonym niczym.
   * Serwer i tak sprawdzi to po swojemu; ta odpowiedź jest po to, żeby nie
   * wysyłać gracza w drogę, o której już wiadomo, że donikąd nie prowadzi.
   */
  private travelTo(placed: Placed, what: 'harvest' | 'upgrade' | 'rest' | 'ship'): void {
    const at: Axial = { q: placed.q, r: placed.r };
    const cells: Axial[] = [at, ...Array.from({ length: 6 }, (_, side) => neighbor(at, side))];
    const target = cells.find((cell) => findRoute(this.world, this.standing, cell));

    if (!target) {
      this.status?.say('tam nie da się dojść');
      return;
    }

    this.errand = { building: placed.id, at, what };
    this.orderWalk(target);
    this.status?.say(
      what === 'harvest'
        ? 'idę zebrać'
        : what === 'rest'
          ? 'idę odpocząć'
          : what === 'ship'
            ? 'idę zbudować statek'
            : 'idę rozbudować'
    );
  }

  /**
   * Idź tam i postaw.
   *
   * **Celem marszu jest sąsiad, nigdy samo pole.** Budynek nie może stanąć tam,
   * gdzie stoisz — reguła budowy to pierścień, a nie dysk — więc dojście na
   * plac budowy byłoby dojściem w jedyne miejsce, z którego nie da się budować.
   * Tym różni się od zbioru, gdzie wejście na budynek jest w porządku.
   *
   * Nic nie schodzi z sakwy po drodze: zapłatę pobiera dopiero postawienie po
   * tamtej stronie, więc marsz zatrzymany w połowie nic nie kosztuje.
   */
  private travelToBuild(at: Axial, kind: BuildingKind, chain = false, axis?: number): void {
    // Sąsiad, który nie jest zarazem polem tego budynku: doki stoczni są
    // sąsiadami jej środka, a stanięcie na doku jest staniem pod budynkiem.
    const spots = footprint(kind, at, axis);
    const around = Array.from({ length: 6 }, (_, side) => neighbor(at, side)).filter(
      (cell) => !spots.some((spot) => spot.q === cell.q && spot.r === cell.r)
    );
    const target = around.find((cell) => findRoute(this.world, this.standing, cell));

    if (!target) {
      this.status?.say('tam nie da się dojść');
      return;
    }

    this.errand = { at, what: 'build', kind: kind.key, chain, axis };
    this.orderWalk(target);
    this.status?.say(`idę postawić: ${kind.label}`);
  }

  /**
   * Dotarliśmy — zrób to, po co szliśmy.
   *
   * Pytane po **każdym własnym kroku** i o zasięg, a nie o dojście do celu:
   * trasa bywa prowadzona obok budynku, zanim dojdzie do pola, które wskazała,
   * a stojąc obok już wolno zbierać. Czekanie na cel byłoby wtedy kilkoma
   * krokami czekania na nic.
   */
  private finishErrand(): void {
    const errand = this.errand;
    if (!errand) return;

    // Building asks for the ring, everything else for the disc: a building
    // cannot go where you are standing, and nothing else has that reason.
    // A road starts **under** the walker: the ring is where a building goes and
    // the disc is what you can reach, but a road begins on the cell itself.
    const arrived =
      errand.what === 'build'
        ? inBuildReach(this.standing, errand.at)
        : errand.what === 'road'
          ? this.standing.q === errand.at.q && this.standing.r === errand.at.r
          : inCollectReach(this.standing, errand.at);
    if (!arrived) return;

    this.errand = undefined;
    // The walk was the means, so it ends with what it was for. Anything still
    // queued behind it would be walking to a place nobody is going any more.
    this.connection?.stopWalking();
    this.walkingTo = null;
    this.mark?.hide();
    // **Razem z trasą znika jej rysunek.** `reached` zdejmuje wyłącznie odcinki
    // już przebyte, więc po zatrzymaniu się w zasięgu zostawał ogon prowadzący
    // do pola, na które nikt już nie idzie — widoczny aż do następnego marszu,
    // bo dopiero nowa trasa zastępowała starą. Escape kasował go od początku
    // (`callOff`), a dojście do celu nie: ta sama rzecz w dwóch miejscach,
    // zrobiona tylko w jednym.
    this.route?.clear();
    if (errand.what === 'road') {
      // Arrived at the beginning. The mode may have been left in the meantime,
      // and then the walk was just a walk.
      if (this.roadMode) {
        this.roadFrom = { q: errand.at.q, r: errand.at.r };
        this.status?.say('jestem — wskaż, dokąd ma prowadzić');
      }
    } else if (errand.what === 'build') {
      // Straight back through the ordinary path, which checks the ground and
      // the reach again — by now we are standing somewhere else than when the
      // journey was ordered, and the answer could have changed with us.
      if (this.buildKind?.key === errand.kind) this.tryBuild(errand.at, errand.chain, errand.axis);
      else this.status?.say('budowa odwołana');
    } else if (errand.what === 'fell') {
      this.tryFell(errand.at);
    } else if (errand.what === 'bridge') {
      if (this.bridgeMode && errand.axis !== undefined) {
        this.placeBridge(errand.at, errand.axis, errand.chain);
      } else {
        this.status?.say('most odwołany');
      }
    } else if (errand.what === 'ashore') {
      // Dopłynęliśmy na wodę przy brzegu — reszta jest tym samym żądaniem,
      // które poleciałoby spod burty.
      void this.goAshore(errand.at);
    } else if (errand.building) {
      if (errand.what === 'harvest') this.tryHarvest(errand.building);
      else if (errand.what === 'rest') void this.lieDown(errand.building);
      else if (errand.what === 'ship') void this.tryBuildShip(errand.building);
      else this.tryUpgrade(errand.building);
    }
  }

  /**
   * Odwołaj wszystko, co jest w toku. Odpowiada, czy było co odwoływać.
   *
   * Jedno miejsce, w którym wypisane jest, co znaczy „nieważne": sprawa
   * w drodze, wybrany budynek, kładziona droga, wskazany cel i panel pola.
   * Escape woła to raz i nie pyta o kolejność — a że każda z tych rzeczy
   * potrafi być włączona razem z inną, kolejność i tak była wymysłem.
   *
   * **Marsz zostaje.** Zatrzymanie go to Spacja, która przy okazji wraca
   * kamerą do postaci; tu przerywana jest tylko ta jego część, która była
   * środkiem do czegoś — sprawa, po którą się szło.
   */
  private callOff(): boolean {
    let something = false;

    // Panel pola gaśnie i **zostaje zgaszony**, dopóki nie wejdzie się gdzieś
    // indziej albo nie poprosi o niego prawym przyciskiem. Bez zapamiętania
    // tego odświeżanie zapaliłoby go z powrotem w ciągu pół sekundy, czyli
    // Escape nie robiłby nic widocznego.
    // Magazyn gaśnie pierwszy i sam: jest najbardziej „w toku" z tego, co
    // stoi przy krawędzi — ktoś właśnie przenosi surowce.
    if (this.store?.open) {
      this.closeStore();
      return true;
    }
    if (this.ship?.open) {
      this.closeShip();
      return true;
    }

    if (this.spot?.open) {
      this.spotShut = true;
      this.spot.hide();
      something = true;
    }

    if (this.errand) {
      // The walk was the means, so it ends with what it was for.
      this.forgetErrand();
      this.connection?.stopWalking();
      this.route?.clear();
      this.walkingTo = null;
      this.mark?.hide();
      something = true;
    }

    if (this.felling) {
      this.putAxeDown();
      something = true;
    }

    if (this.roadMode) {
      this.panel?.paveMode(false);
      something = true;
    }

    if (this.bridgeMode) {
      this.panel?.bridgeMode(false);
      something = true;
    }

    if (this.buildKind) {
      this.panel?.pick(null);
      this.buildMark?.hide();
      this.buildGhost?.hide();
      something = true;
    }

    // A destination picked but never ordered. The camera stays where it is —
    // dropping a pick is not a reason to be moved anywhere, which is the one
    // thing that separates this from Space.
    if (this.chosenAt) {
      this.unpick();
      something = true;
    }

    // Rozmowa, którą prowadzimy. **Zatrzymany nie ma czego odwołać** — dla
    // niego Escape nie jest wyjściem, bo wyjścia nie ma; to jest cała treść
    // trzydziestu sekund.
    if (this.talk?.open && !this.detained) {
      this.connection?.part();
      something = true;
    }

    // Pościg. `stop` znaczy po tamtej stronie „przestań gonić i stań", bo
    // gonienie jest chodzeniem — a Escape nad kimś, za kim idziemy, znaczy
    // dokładnie to.
    if (this.chasing) {
      this.connection?.stopWalking();
      this.chasing = false;
      this.route?.clear();
      something = true;
    }

    // Rozwinięta zakładka paska na dole. Ostatnia na liście, bo jest najmniej
    // „w toku" ze wszystkiego tutaj — ale zamknąć ją Escape'em trzeba, inaczej
    // jedynym sposobem byłoby trafienie w ten sam przycisk.
    if (this.bar?.close()) something = true;

    return something;
  }

  /**
   * Menu nad lasem. Odpowiada, czy było nad czym je otworzyć.
   *
   * Ostatnie w kolejce: budynek i pionek wygrywają o prawy przycisk, bo stoją
   * **na** polu, a las jest samym polem.
   */
  /**
   * Menu statku i brzegu: „Wsiądź" na własny kadłub obok, „Zejdź na ląd"
   * z pokładu na sąsiednie pole.
   *
   * Jedno miejsce na oba, bo to jedno pytanie — **co da się zrobić z tym
   * polem** — zadane raz z brzegu i raz z pokładu.
   */
  private openShipMenu(event: MouseEvent, cell: Axial): boolean {
    if (!this.menu) return false;
    const room = this.container.getBoundingClientRect();
    const at = (entries: MenuEntry[], title: string) => {
      this.menu!.show(event.clientX - room.left, event.clientY - room.top, entries, title);
      return true;
    };

    // Z pokładu: brzeg, na który da się zejść. Reguła jest po tamtej stronie
    // drutu — tutaj wystarczy, że to ląd tuż przy burcie.
    if (this.aboard) {
      const data = this.world.get(axialKey(cell.q, cell.r));
      const dry = data && mirrorOf(data) === undefined;
      if (!dry) return false;

      // **Za daleko przestało być odmową, a stało się rejsem** — ta sama
      // zamiana, którą przeszedł zbiór i budowa. Klik w odległy brzeg zamawia
      // podpłynięcie i **zapamiętuje, po co**; zejście leci po dopłynięciu,
      // jako to samo żądanie, które poleciałoby spod burty.
      const near = hexDistance(this.standing, cell) === 1;
      return at(
        [
          {
            label: near ? 'Zejdź na ląd' : 'Popłyń i zejdź na ląd',
            hint: 'statek zostaje przy brzegu',
            enabled: true,
            choose: () => (near ? void this.goAshore(cell) : this.sailToShore(cell)),
          },
        ],
        'brzeg'
      );
    }

    const id = this.ships?.whoIsAt(cell);
    if (!id) return false;

    // Czy to mój i czy już pływa, rozstrzyga serwer — a menu nie proponuje
    // tego, o czym wie, że zostanie odmówione.
    const mine = this.ships?.ownerOf(id) === this.joined?.playerId;
    if (!mine) return false;

    return at(
      [
        {
          label: 'Wsiądź',
          hint: this.ships?.building(id) ? 'statek jeszcze się buduje' : 'popłyniesz nim',
          enabled: !this.ships?.building(id) && hexDistance(this.standing, cell) === 1,
          choose: () => void this.boardShip(id),
        },
      ],
      'statek'
    );
  }

  /** Wsiądź na własny statek stojący obok. */
  private async boardShip(ship: string): Promise<void> {
    const answer = await boardShip(this.session.token, ship);
    if ('code' in answer) {
      this.status?.say(answer.message);
      return;
    }
    this.aboard = ship;
    this.standing = { q: answer.q, r: answer.r };

    // **Własnego pionka zdejmuje klient, a nie wiadomość z serwera** — ta sama
    // droga, którą idzie kładzenie się spać (`settleRest`). Rozgłoszenie
    // `playerLeft` robi to u patrzących; u siebie robi się to tutaj, bo tutaj
    // jest odpowiedź, która mówi, że się udało.
    if (this.joined) {
      this.markers?.moveTo(this.joined.playerId, this.standing, 0);
      this.markers?.remove(this.joined.playerId);
    }
    this.callOff();
    this.status?.say('płyniesz — wskaż, dokąd');
  }

  /**
   * Podpłyń pod ten brzeg i zejdź.
   *
   * **Celem jest woda przy brzegu, nigdy sam brzeg** — statek na ląd nie
   * wpłynie. Trasy szuka ta sama funkcja, którą liczy się podgląd, więc „tam
   * nie dopłyniesz" pada od razu, zamiast po rejsie zakończonym niczym.
   */
  private sailToShore(cell: Axial): void {
    const around = Array.from({ length: 6 }, (_, side) => neighbor(cell, side));
    const target = around.find((water) => findRoute(this.world, this.standing, water, 'sail'));

    if (!target) {
      this.status?.say('tam nie dopłyniesz');
      return;
    }

    this.errand = { at: cell, what: 'ashore' };
    this.orderWalk(target);
    this.status?.say('płynę do brzegu');
  }

  /** Zejdź na wskazany brzeg. */
  private async goAshore(cell: Axial): Promise<void> {
    const answer = await leaveShip(this.session.token, cell);
    if ('code' in answer) {
      this.status?.say(answer.message);
      return;
    }
    // Statek został przy brzegu i nigdzie nie płynie — tor gaśnie razem
    // z rejsem, inaczej obraz sunąłby dalej trasą, którą kadłub stoi.
    // Pole, z którego się schodzi — zapamiętane, zanim statek przestanie być
    // nasz: pionek ma z niego **zejść**, a nie pojawić się na brzegu.
    const deck = this.aboard ? this.ships?.cellOf(this.aboard) : undefined;
    if (this.aboard) this.ships?.unfollow(this.aboard);
    this.aboard = null;
    this.standing = { q: answer.q, r: answer.r };

    // **Własnego pionka stawia klient**, tą samą drogą, którą wstaje się
    // z łóżka: tu jest odpowiedź mówiąca, że się udało, i pole, na którym się
    // stanęło. Rozgłoszenie `playerJoined` zostaje tym, czym jest — wiadomością
    // **dla patrzących**; własny pionek, który czeka na cudzą wiadomość, to
    // pionek, którego czasem nie ma.
    //
    // **Najpierw pokład, potem brzeg** — odwrotność wsiadania, i z tego samego
    // powodu: postać pojawiająca się od razu na brzegu nie schodzi ze statku,
    // tylko się na nim znajduje. Na wodzie stoi na lustrze, bo o wysokość pyta
    // się tą samą drogą, co wszędzie indziej.
    if (this.joined) {
      this.markers?.add(
        this.joined.playerId,
        this.session.username,
        deck ?? this.standing,
        0
      );
      if (deck) this.markers?.moveTo(this.joined.playerId, this.standing, answer.slot);
      this.markers?.refresh();
      this.status?.setPlayer(this.session.username, this.countPlayers());
    }
    this.callOff();
    this.status?.say('jesteś na brzegu');
  }

  private openFellMenu(event: MouseEvent, cell: Axial): boolean {
    if (!this.menu) return false;
    const rich = levelOf(this.world.get(axialKey(cell.q, cell.r))?.forest);
    if (rich === 0) return false;

    const seconds = rich * FELL_SECONDS_PER_LEVEL;
    const logs = rich * FELL_LOGS_PER_LEVEL;
    const icon = this.materials ? iconOf(this.materials, 'MAT_LOGS') : '';
    const room = this.container.getBoundingClientRect();
    this.menu.show(
      event.clientX - room.left,
      event.clientY - room.top,
      [
        {
          label: 'Zetnij drzewa',
          hint: `${seconds} s · +${logs} ${icon}`.trim(),
          enabled: !this.felling,
          choose: () => this.travelToFell(cell),
        },
      ],
      'las'
    );
    return true;
  }

  /**
   * Idź do lasu i zetnij go.
   *
   * Celem jest samo pole, jeśli da się na nie wejść, a inaczej sąsiad —
   * gęstwiny nie da się przejść, więc zwykle wycina się ją z brzegu.
   */
  private travelToFell(at: Axial): void {
    const cells: Axial[] = [at, ...Array.from({ length: 6 }, (_, side) => neighbor(at, side))];
    const target = cells.find((cell) => findRoute(this.world, this.standing, cell));
    if (!target) {
      this.status?.say('tam nie da się dojść');
      return;
    }
    this.errand = { at, what: 'fell' };
    this.orderWalk(target);
    this.status?.say('idę wyciąć drzewa');
  }

  private tryFell(at: Axial): void {
    void fellTrees(this.session.token, at)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        this.felling = {
          at,
          until: performance.now() + answer.seconds * 1000,
          logs: answer.logs,
        };
        this.tickFelling();
      })
      .catch(() => this.status?.say('serwer nie odpowiada'));
  }

  /**
   * Jak głośno słychać topór z tego pola: pełnym głosem obok, ciszej z dala,
   * wcale dalej niż `CHOP_HEARING` pól.
   */
  private hearing(cell: Axial): number {
    return Math.max(0, 1 - cellDistance(cell, this.standing) / CHOP_HEARING);
  }

  /** Sztandar gracza jako drzewce z płatem — albo `null`, gdy go nie ma. */
  private async bannerFor(player: string, version: number, height: number): Promise<THREE.Object3D | null> {
    // Drzewce wczytywane przy pierwszym sztandarze — pionki i budynki powstają
    // w różnej kolejności, a punkty modeli są już wtedy wczytane z terenem.
    this.staff ??= loadStaff(this.anchors);
    try {
      const [spec, staff, banner] = await Promise.all([
        loadBannerSpec(),
        this.staff,
        loadBanner(player, version),
      ]);
      if (!banner) return null;
      return raiseBanner(staff, bannerTexture(banner, spec), spec, height);
    } catch (error) {
      console.warn('sztandar:', error);
      return null;
    }
  }

  /** Odłóż topór — po obu stronach. */
  private putAxeDown(): void {
    this.felling = undefined;
    void stopFelling(this.session.token).catch(() => undefined);
    this.status?.say('wycinka przerwana');
  }

  /**
   * Licznik wycinki w linii stanu, dwa razy na sekundę.
   *
   * Po terminie czeka `FELL_GRACE` na `treesFelled`; gdy nie przyjdzie, serwer
   * wycinkę odwołał i licznik gaśnie bez słowa.
   */
  private tickFelling(): void {
    const felling = this.felling;
    if (!felling) return;
    const left = Math.ceil((felling.until - performance.now()) / 1000);
    if (left > 0) {
      this.status?.say(`wycinam drzewa… ${left} s`);
    } else if (-left * 1000 > FELL_GRACE) {
      this.felling = undefined;
    }
  }

  /** Odwołaj sprawę, ale nie zatrzymuj marszu — to robi ten, kto woła. */
  private forgetErrand(): void {
    if (!this.errand) return;
    this.errand = undefined;
    this.status?.say('odwołane');
  }

  /**
   * Empty one of your buildings into your purse.
   *
   * Nothing is changed here on the way out. The server settles what the building
   * has made, moves it, and then says both halves over the socket — the purse
   * and the estate — so the numbers on screen come from the same place they
   * always do rather than from a guess made while waiting.
   */
  private tryHarvest(building: string): void {
    if (!this.session) return;

    void harvestBuilding(API, this.session.token, building)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        const moved = answer.taken.reduce((sum, item) => sum + item.amount, 0);
        this.status?.say(`zebrano ${moved}`);
        // Chmurka bierze się stąd, a nie z wiadomości po drucie: odpowiedź na
        // to żądanie już niesie, co wyszło i z czego. Serwer musi mówić osobno
        // wyłącznie o tym, co dzieje się bez pytania — czyli o wozach.
        this.popOver(answer.building, answer.taken, 'gain');
      })
      .catch((error) => {
        console.error('zbiór:', error);
        this.status?.say('nie udało się zebrać');
      });
  }

  /**
   * Chmurka z tym, co ruszyło, nad budynkiem, z którego ruszyło.
   *
   * **Jedno miejsce na dwa zdarzenia**, bo zbiór i wysyłka wozu mówią to samo
   * zdanie o własnym majątku i różnią się wyłącznie znakiem. Milczy, gdy
   * budynku nie ma w polu: zakład mógł zostać za krawędzią wczytanego świata,
   * a wtedy nie ma nad czym rysować i nie jest to błąd.
   */
  private popOver(
    building: string,
    moved: { material: string; amount: number }[],
    sign: 'gain' | 'loss'
  ): void {
    const anchor = this.buildings?.anchorOf(building);
    if (anchor) this.pops?.show(anchor, moved, sign);
  }

  /**
   * What stands around here, fetched with the ground it stands on.
   *
   * Asked again whenever the loaded neighbourhood moves, and skipped when it
   * has not: the answer for a focus cell that has not changed is the answer
   * already held.
   *
   * Buildings are only ever added, never dropped, because nothing takes one
   * down yet. When something does, this is where the reconciling goes.
   */
  private refreshBuildings(): void {
    if (!this.buildings || !this.streamer) return;

    // Keyed on the **chunk**, not the cell under the camera. The endpoint
    // gathers whole rings of chunks, so its answer is identical for all 1027
    // cells of one chunk — asking per cell meant a request for every hex the
    // camera crossed while panning, for an answer already held.
    const origin = this.streamer.originFor(this.focusCell());
    const radius = Math.min(this.drawRadius(), 7);

    // Skipped when the reach has not grown, too. A narrower radius is a subset
    // of what is already held, so zooming in has nothing to ask for; a wider
    // one does, and used to go unnoticed until the focus happened to move.
    const last = this.buildFocus;
    if (last && last.q === origin.q && last.r === origin.r && radius <= last.radius) return;
    this.buildFocus = { ...origin, radius };

    // The roads of the same area, on the same trigger and with the same key:
    // they arrive with the ground exactly as the buildings do, and asking for
    // them on a second schedule would be a second answer about one place.
    void loadRoads(origin, radius)
      .then((cells) => this.takeRoads(cells))
      .catch((error) => console.warn('drogi obszaru:', error));

    // Wycięte pola tego samego obszaru, tym samym wyzwalaczem co drogi.
    void loadFelled(origin, radius)
      .then((cells) => this.takeFelled(cells))
      .catch((error) => console.warn('wycinka obszaru:', error));

    // Wozy obszaru na tym samym wyzwalaczu: pionek, który już jedzie, ma być
    // na mapie od wejścia, a nie dopiero od pierwszego kroku, który usłyszymy.
    void loadConvoys(origin, radius)
      .then((rolling) => {
        for (const convoy of rolling) {
          this.convoys?.set(convoy.id, { q: convoy.q, r: convoy.r });
        }
      })
      .catch((error) => console.warn('wozy obszaru:', error));

    // Statki obszaru na tym samym wyzwalaczu i z tego samego powodu: statek,
    // który już stoi przy pomoście, ma być na mapie od wejścia do gry.
    void loadShips(origin, radius)
      .then((afloat) => {
        for (const ship of afloat) this.ships?.set(ship);
      })
      .catch((error) => console.warn('statki obszaru:', error));

    void loadPlaced(API, origin, radius)
      .then((placed) => {
        // Only what was new: rebuilding a chunk for a building this client
        // already knew about would redo the work on every pass. All of them in
        // one call, so walking into a settlement rebuilds each chunk once.
        this.streamer?.settle(this.buildings?.ground(this.buildings.addAll(placed)) ?? []);
        this.updateOwned();
      })
      .catch((error) => {
        this.buildFocus = undefined;
        console.warn('budynki obszaru:', error);
      });
  }

  /**
   * What is built on a cell, in the object dictionary's words.
   *
   * The rules talk about objects, and a building is one — so a rule can say
   * "not beside a camp" without either building naming the other.
   */
  /**
   * How high the camera should look when it centres on a cell.
   *
   * The projection is oblique, so a focus left at sea level while somebody
   * stands twenty levels up puts them well above the middle of the frame —
   * which is what "centring goes to zero on the uplands" was.
   *
   * Undefined where the ground has not arrived: the callers then leave the
   * height where it was, which is a better guess than sea level and is what
   * they did before this existed.
   */
  /**
   * Move the fog out of the way, or give up waiting for the world.
   *
   * The timeout is not a nicety: the terrain can fail to arrive for reasons
   * this side cannot fix — no world on the server, a request that never comes
   * back — and a fog that never lifts is a black screen with nothing to read.
   * After it, the world is shown as it is, however little of it there is.
   */
  private advanceReveal(deltaTime: number): void {
    if (this.reveal >= 1) return;

    if (!this.worldReady) {
      this.waitingForWorld += deltaTime;
      if (this.waitingForWorld < REVEAL_TIMEOUT_SECONDS) return;

      console.warn('teren nie dojechał — odsłaniam, co jest');
      this.worldReady = true;
      this.begin();
    }

    this.reveal = Math.min(1, this.reveal + deltaTime / REVEAL_SECONDS);

    // The interface comes in once the fog is most of the way out — early
    // enough to be there when the world is, late enough not to be furniture
    // floating in weather.
    if (this.reveal >= HUD_AT_REVEAL) this.stage('ready');
  }

  /** Move to a later stage; never back to an earlier one. */
  private stage(shown: 'blank' | 'world' | 'ready'): void {
    if (this.shown === shown || this.shown === 'ready') return;

    this.shown = shown;
    this.container.dataset.stage = shown;

    // The loader goes when the world is genuinely visible behind it, which is
    // this stage and not the moment the renderer started: the fog is closed at
    // the start and a spinner taken down then leaves the player looking at
    // weather with nothing said about it. `ready` is the same threshold the HUD
    // waits for, so the interface arrives as the spinner leaves rather than
    // both changing at unrelated moments.
    //
    // It is reached even when the terrain never turns up: the eight-second
    // escape parts the fog regardless, so this cannot leave a spinner running
    // over a world that failed to arrive.
    if (shown === 'ready') hideLoader();
  }

  /**
   * The world is here: part the fog and start the opening move.
   *
   * Called once. The order matters — the camera is seated at the player's own
   * height before the movement starts, so the arrival is one continuous thing
   * rather than a turn with a vertical correction halfway through it.
   */
  private begin(): void {
    if (this.arrival) {
      this.cameraController.arriveAt(this.arrival.yaw, this.arrival.zoom);
      this.reportedView = { ...this.cameraController.view };
      this.arrival = undefined;
    }
  }

  private groundAt(cell: Axial): number | undefined {
    const data = this.world.get(axialKey(cell.q, cell.r));
    // Na moście — pokład: kamera i chmurki siadają tam, gdzie stoi postać.
    return data ? (deckY(data) ?? elevationToY(data.elevation)) : undefined;
  }

  /**
   * What already stands on a cell, as a placement rule needs to see it.
   *
   * More than the object it wears: a rule may ask which rung it is on and
   * whether it is this player's — a field grows beside a village, not beside a
   * camp, and beside its owner's other fields, not beside anybody's.
   */
  private standingAt(cell: Axial): Standing | undefined {
    const placed = this.buildings?.placedAt(cell);
    if (!placed || !this.catalogue) return undefined;

    const kind = this.catalogue.buildings.find((entry) => entry.id === placed.kind);
    if (!kind) return undefined;

    return {
      object: kind.object,
      key: kind.key,
      level: placed.level,
      mine: placed.owner === this.session.playerId,
    };
  }

  /**
   * Poprowadź własną bryłę trasą z serwera.
   *
   * **Czasy kroków liczy klient**, bo zna tę samą regułę, co serwer: pieszo
   * z gruntu, po wodzie jedną liczbą. To jest ten sam bliźniak, którym rysuje
   * się podgląd trasy — gdyby się rozjechał, bryła mijałaby pola wcześniej albo
   * później niż serwer i rozjazd byłoby widać jako skok przy potwierdzeniu.
   */
  private glideAlong(cells: Axial[]): void {
    if (this.aboard) {
      this.ships?.follow(this.aboard, cells);
      return;
    }
    if (!this.joined) return;
    this.markers?.follow(
      this.joined.playerId,
      cells,
      cells.map((cell) => crossingSeconds(this.world.get(axialKey(cell.q, cell.r))))
    );
  }

  /**
   * Kamera idzie za **bryłą**, nie za wiadomością o kroku.
   *
   * Wiadomości przychodzą polami, więc kamera prowadzona nimi szła skokami —
   * tym wyraźniej, im dłuższy krok. Bryła sunie własnym torem co klatkę i to za
   * nią kamera ma iść; `followTo` i tak dociąga ognisko swoją prędkością, więc
   * podawanie mu położenia częściej niczego nie przyspiesza, tylko wygładza.
   *
   * Tylko gdy kamera jest **przyklejona** (`following`): odsunięta ręką zostaje
   * tam, gdzie ją odsunięto, i to jest ta sama reguła, co dotąd.
   */
  private followBody(): void {
    if (!this.following || !this.joined) return;

    const spot = this.aboard
      ? this.ships?.spotOf(this.aboard)
      : this.markers?.spotOf(this.joined.playerId);
    if (!spot) return;

    // **Wysokość z pola, nie z bryły.** Bryła faluje na wodzie i stoi o pół
    // postaci nad ziemią; kamera podniesiona za nią kiwałaby się razem z nią.
    // Pionowo idzie więc za tym, co potwierdzone — zmienia się powoli i nikt
    // tego nie widzi jako skoku.
    this.cameraController.followTo(spot.x, spot.z, this.groundAt(this.standing));
  }

  /**
   * Zestaw pomiar serwera z pomiarem obrazu, gdy oba już są.
   *
   * Pytane z dwóch stron — przy wiadomości i w każdej klatce, dopóki jest na co
   * czekać — bo nie wiadomo, które przyjdzie później. Wypisuje raz i zapomina.
   *
   * **Opóźnienie, nie błąd.** Obraz z założenia idzie o krok za serwerem: tam
   * krok jest chwilą, tutaj przejściem, które trzeba pokazać. Rosnące
   * z długością trasy znaczyłoby, że czasy kroków po obu stronach się nie
   * zgadzają — i to jest jedyna rzecz, której ten pomiar szuka.
   */
  private reckon(): void {
    if (!this.travelled) return;

    const mine = this.aboard
      ? this.ships?.timing(this.aboard)
      : this.joined
        ? this.markers?.timing(this.joined.playerId)
        : undefined;
    if (!mine || mine.took === 0) return;

    const { took, planned } = this.travelled;
    this.travelled = null;

    const seconds = (value: number) => `${value.toFixed(2)} s`;
    const behind = mine.took - took;
    const what = this.aboard ? 'rejs' : 'trasa';
    console.info(
      `${what}: serwer ${seconds(took)} (plan ${seconds(planned)}), ` +
        `obraz ${seconds(mine.took)} (plan ${seconds(mine.planned)}), ` +
        `obraz z tyłu o ${seconds(behind)}`
    );
    if (this.debugMode) this.status?.say(`obraz z tyłu o ${seconds(behind)}`);
  }

  /** Przestań sunąć trasą — marsz się skończył albo został odwołany. */
  private stopGliding(): void {
    if (this.aboard) this.ships?.unfollow(this.aboard);
    if (this.joined) this.markers?.unfollow(this.joined.playerId);
  }

  /** How many of each kind this player has up, for the prices on the panel. */
  private updateOwned(): void {
    if (!this.panel || !this.catalogue || !this.buildings) return;

    // Two counts, because they answer two questions. How many stand decides
    // what the next one costs; how many count towards the limit may leave out
    // the upgraded ones — a camp raised to a village is still owned and still
    // makes the next hut dearer, but it is no longer a camp.
    const owned = new Map<string, number>();
    const counted = new Map<string, number>();
    for (const kind of this.catalogue.buildings) {
      const all = this.buildings.ownedBy(this.joined.playerId, kind.id);
      owned.set(kind.key, all);
      const below = kind.limit.kind === 'perPlayer' ? kind.limit.belowLevel : undefined;
      counted.set(
        kind.key,
        below === undefined
          ? all
          : this.buildings.ownedBelow(this.joined.playerId, kind.id, below)
      );
    }
    this.panel.setOwned(owned, counted);
  }

  /**
   * Try to put the picked building on the cell under the cursor.
   *
   * The client checks first only so that it can say why not without a round
   * trip; the server checks everything again and its answer is the one that
   * counts. A refusal it makes and this one does not — being unable to pay, or
   * somebody else having built there a moment ago — comes back as words too.
   */
  private tryBuild(cell: Axial, chain = false, along?: number): void {
    const kind = this.buildKind;
    if (!kind || !this.catalogue) return;

    if (!this.vocabulary) return;

    // Oś bierze się spod kursora, chyba że marsz przyniósł ją ze sobą — patrz
    // `errand.axis`. U budynku bez skrzydeł nie znaczy nic i nie jedzie.
    const axis = kind.wings ? (along ?? this.axisUnderCursor(cell)) : undefined;

    // The ground first, however far away it is: walking across the map to be
    // told the site is a cliff is the one outcome worth a check up front. The
    // server checks it again on arrival, and its answer is the one that counts.
    const site = checkSite(
      this.world,
      this.vocabulary,
      kind,
      cell,
      (at) => this.standingAt(at),
      axis
    );
    if (site !== true) {
      this.status?.say(hintFor(this.vocabulary, kind, site));
      return;
    }

    // Too far stopped being a refusal and became a journey — the same as with
    // collecting. **Nothing is paid for on the way**: the cost comes off in the
    // server's placement, so a walk that is stopped leaves the purse untouched.
    if (!inBuildReach(this.standing, cell, footprint(kind, cell, axis))) {
      this.travelToBuild(cell, kind, chain, axis);
      return;
    }

    void placeBuilding(API, this.session.token, kind.key, cell, axis)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        if (this.buildings?.add(answer)) this.streamer?.settle(this.buildings.ground([answer]));
        this.updateOwned();

        // **Postawienie kończy akcję.** Duch znika z kursora i klik znaczy znowu
        // to, co znaczył przedtem — wybór celu. Budowanie jest decyzją, a nie
        // trybem, w którym się zostaje: pierwsza wersja zostawiała wybór na
        // kursorze i następne kliknięcie stawiało kolejny budynek, o czym nic
        // nie uprzedzało.
        //
        // Wyjątkiem jest SHIFT, i tylko dopóki wolno postawić następny: budynek
        // z limitem na gracza po osiągnięciu go nie ma czego trzymać na kursorze
        // — wpis w panelu jest już wtedy wyszarzony.
        const again = chain && kind.limit.kind !== 'perPlayer';
        if (!again) this.panel?.pick(null);

        this.status?.say(
          again
            ? `${kind.label} postawiony — stawiam dalej`
            : `${kind.label} postawiony (SHIFT+klik stawia kolejny)`
        );
      })
      .catch((error) => {
        console.error('budowa:', error);
        this.status?.say('nie udało się postawić');
      });
  }

  /**
   * Pick a destination: draw the way there, and put an X on it.
   *
   * **Nothing is picked where there is no way to go.** The first version put
   * the X down first and let the route fail behind it, on the theory that the
   * server might know a way this client cannot see — which left a mark standing
   * on open water, and a second click on it ordering a walk that was refused
   * while the camera had already scrolled back to the player. Both were the
   * same mistake: marking a destination before knowing it is one.
   *
   * The client can be wrong about this — it only has the chunks it has loaded —
   * but only about somewhere it would have to walk off the loaded map to reach,
   * and the alternative is a mark that means nothing.
   */
  private choose(cell: Axial): boolean {
    // Picking somewhere else is picking somewhere else: the errand was a
    // destination too, and two of them cannot both be where we are going.
    this.forgetErrand();

    // Regułą tego, czym się właśnie porusza: pod pokładem pyta się o wodę,
    // a na brzegu o ląd. Serwer rozstrzyga tak samo — patrz `Passage`.
    const route = findRoute(this.world, this.standing, cell, this.aboard ? 'sail' : 'walk');
    if (!route) {
      this.unpick();
      this.status?.say(this.aboard ? 'tam nie dopłyniesz' : 'tam nie da się dojść');
      return false;
    }

    this.chosenAt = cell;
    this.mark?.showAt(cell);
    this.chosen?.set(this.standing, route);

    this.previewTo = null;
    this.updatePreview();
    return true;
  }

  /**
   * Drop the pick, but not the walk.
   *
   * A click on open water while already walking somewhere is a refused pick and
   * nothing else — the X belongs to the destination being walked to and stays
   * on it.
   */
  private unpick(): void {
    this.chosenAt = null;
    this.chosen?.clear();
    this.preview?.clear();
    this.previewTo = null;

    if (this.walkingTo) this.mark?.showAt(this.walkingTo);
    else this.mark?.hide();
  }

  /** Nothing is picked or being walked to any more: the lines and the X go too. */
  private forget(): void {
    this.chosenAt = null;
    this.walkingTo = null;
    this.chosen?.clear();
    this.mark?.hide();
    this.preview?.clear();
    this.previewTo = null;
  }

  /**
   * Resolve what the mouse is over, and show what a click there would do.
   *
   * Run once per frame rather than per mousemove event: the browser fires those
   * faster than frames, and a raycast against every chunk is not something to
   * repeat between two renders that show the same thing.
   *
   * **What it shows depends on the mode.** In play it is the route a click
   * would ask for; in debug it is the red overlay on the cell itself, which
   * answers a different question — which hex is this, exactly — and belongs
   * with the coordinates in the HUD.
   */
  private updateHover(): void {
    if (!this.picker || !this.highlight) return;

    // The renderer refreshes this at draw time, which is after the controller
    // has already moved the camera — without it the ray trails a frame behind
    this.camera.updateMatrixWorld();

    const hit = this.picker.pick();
    const key = hit ? axialKey(hit.q, hit.r) : null;
    // W trybie mostu liczy się też **krawędź** pod kursorem, a ta zmienia się
    // w obrębie jednego pola — więc to pytanie idzie przed skrótem niżej.
    if (this.bridgeMode) this.updateBridgeMark(hit ?? null);
    if (key === this.hovered) return;

    this.hovered = key;
    const cell = key === null ? undefined : this.world.get(key);

    // A hit can still land outside the map — the ray may clip the very edge of
    // a border cell and round outwards into a cell that was never generated
    this.hoveredCell = cell ? hit : null;

    if (cell && this.debugMode) {
      this.highlight.showAt(this.world, cell);
    } else {
      this.highlight.hide();
    }

    this.updateBuildMark(cell);
    this.updatePreview();
    this.updateTooltip();
  }

  /**
   * The route a click under the cursor would ask for, **while a destination is
   * picked** and not before.
   *
   * Drawing one under every mouse movement was the first version of this and it
   * was too much line: the map is for looking at, and a route redrawn on every
   * hex the cursor crosses turns the whole of it into an answer to a question
   * nobody asked. So the first click opens the conversation — from then on the
   * cursor offers alternatives to what was picked, and the second click on the
   * picked cell ends it by ordering the walk.
   *
   * Worked out here rather than asked for: a request per hex crossed would be
   * many a second, and the answer has to be on screen while the cursor is still
   * moving. The rules it walks by are the twins of the server's, in
   * `Movement.ts` — so what is drawn is what will be asked for, and the server
   * still has the last word: its own answer replaces these lines on the order.
   *
   * Hidden in debug mode, where the red overlay has the cursor instead.
   */
  /**
   * Say what a click would do while a building is picked.
   *
   * Green where it would go up, red where it would not, and the reason in
   * words on the panel — the same four conditions the server will check,
   * read from the catalogue it sent.
   *
   * Out of build mode this draws nothing, which is what the cursor did before
   * buildings existed: the map is for looking at.
   */
  private updateBuildMark(cell: HexCellData | undefined): void {
    if (!this.buildKind || !this.buildMark || !this.vocabulary) {
      this.buildMark?.hide();
      this.buildGhost?.hide();
      this.showWings([]);
      return;
    }

    if (!cell) {
      this.buildMark.hide();
      this.buildGhost?.hide();
      this.showWings([]);
      this.panel?.setHint(hintFor(this.vocabulary, this.buildKind, 'far'));
      return;
    }

    const axis = this.buildKind.wings ? this.axisUnderCursor(cell) : undefined;
    const spots = footprint(this.buildKind, cell, axis);

    // The ground is asked about wherever the cursor is, near or far. Distance
    // is no longer a refusal — a click on a good site across the valley walks
    // there and puts it up — so a red marker on ground that will take the
    // building would be the cursor saying no to something that works.
    const site = checkSite(
      this.world,
      this.vocabulary,
      this.buildKind,
      cell,
      (at) => this.standingAt(at),
      axis
    );
    const allowed = site === true;
    const near = inBuildReach(this.standing, cell, spots);

    this.buildMark.setColor(allowed ? BUILD_OK : BUILD_NO);
    this.buildMark.showAt(this.world, cell);
    // Skrzydła dostają własne nakładki w tym samym kolorze: „które pole" ma dla
    // stoczni trzy odpowiedzi naraz, a oś wybiera się kursorem, więc bez nich
    // nie widać, wzdłuż czego budynek stanie.
    this.showWings(spots.slice(1), allowed);
    // Ten sam obrót, którym stanie postawiony budynek: oś spod kursora i woda,
    // ku której zwróci się front. Serwer policzy to po swojemu i jego odpowiedź
    // jest tą zapisaną — ta jest zapowiedzią.
    this.buildGhost?.showAt(
      this.buildKind.key,
      cell,
      allowed,
      axis === undefined ? 0 : turnFor(axis, waterFacing(this.world, cell, axis))
    );
    this.panel?.setHint(
      allowed
        ? near
          ? `postaw: ${this.buildKind.label}`
          : `dojdę tam i postawię: ${this.buildKind.label}`
        : hintFor(this.vocabulary, this.buildKind, site as Refusal)
    );
  }

  /**
   * Wejście i wyjście z trybu kładzenia drogi.
   *
   * Wyłączenie sprząta po sobie tak samo, jak robi to tryb debug: podgląd
   * trasy gaśnie, a wskazany początek przestaje być wskazany — inaczej można by
   * zostawić stan, w którym klik znaczy coś, czego nic już nie zapowiada.
   */
  setRoadMode(on: boolean): void {
    this.roadMode = on;
    this.roadFrom = null;
    this.preview?.clear();
    this.previewTo = null;

    if (on) {
      // One picked thing at a time: a building under the cursor and a road
      // being drawn would both want the next click.
      this.panel?.pick(null);
      this.buildKind = null;
      this.buildGhost?.hide();
      this.status?.say('wskaż początek drogi');
    } else {
      this.forgetErrand();
    }
  }

  /** Wejście i wyjście z trybu mostu. */
  setBridgeMode(on: boolean): void {
    this.bridgeMode = on;
    this.bridgeAt = null;
    if (this.bridgePreview) this.bridgePreview.visible = false;
    this.panel?.setHint(null);
    if (on) {
      this.buildKind = null;
      this.buildGhost?.hide();
      this.buildMark?.hide();
      this.status?.say('wskaż wodę — most stanie w poprzek najbliższej krawędzi');
    } else if (this.errand?.what === 'bridge') {
      this.forgetErrand();
    }
  }

  /**
   * Nakładki na polach skrzydeł — tyle, ile ich akurat trzeba.
   *
   * Powstają leniwie i zostają: budynek ze skrzydłami jest jeden, a nakładka to
   * sześć trójkątów. Pusta lista chowa wszystkie, więc wyjście z trybu budowania
   * nie musi o nich wiedzieć nic ponad to, co już wie o `buildMark`.
   */
  private showWings(cells: Axial[], allowed = true): void {
    while (this.buildWings.length < cells.length) {
      const wing = new HexHighlight();
      this.scene.add(wing.object);
      this.buildWings.push(wing);
    }

    this.buildWings.forEach((wing, index) => {
      const at = cells[index];
      const data = at ? this.world.get(axialKey(at.q, at.r)) : undefined;
      if (!data) {
        wing.hide();
        return;
      }
      wing.setColor(allowed ? BUILD_OK : BUILD_NO);
      wing.showAt(this.world, data);
    });
  }

  /**
   * Oś mostu pod kursorem: **najbliższa krawędź pola** wskazuje, w poprzek
   * której most ma stanąć — krawędź `e` i przeciwległa `e + 3` to jedna oś.
   *
   * **Tej samej funkcji pyta budynek ze skrzydłami**, i jest to ta sama rzecz:
   * linia przez pole ma trzy położenia, a wskazuje się je krawędzią. Stocznia
   * kładzie wzdłuż niej doki, most — przęsło.
   */
  private axisUnderCursor(cell: Axial): number {
    const point = this.picker?.lastPoint;
    if (!point) return 0;
    const centre = axialToWorld(cell.q, cell.r);
    const angle = Math.atan2(point.z - centre.z, point.x - centre.x);
    // Środek krawędzi `i` leży pod kątem −(i + ½)·60°, jak narożniki w `CORNERS`.
    let best = 0;
    let nearest = Infinity;
    for (let edge = 0; edge < 6; edge++) {
      const at = (-(edge + 0.5) * Math.PI) / 3;
      const gap = Math.abs(Math.atan2(Math.sin(angle - at), Math.cos(angle - at)));
      if (gap < nearest) {
        nearest = gap;
        best = edge;
      }
    }
    return best % 3;
  }

  /**
   * Czy tu stanie most — bliźniak `roads::bridge_site`, żeby podgląd mówił to
   * samo, co powie serwer. Odpowiada powodem odmowy albo niczym.
   */
  private bridgeRefusal(cell: Axial, axis: number): string | null {
    const water = this.world.get(axialKey(cell.q, cell.r));
    if (!water) return 'tego miejsca jeszcze nie widać';
    if (!isWater(water)) return 'most stawia się na wodzie';
    if (water.road) return 'tu już stoi most';
    const mirror = waterLevel(water) ?? 0;
    for (const edge of [axis, axis + 3]) {
      const at = neighbor(cell, edge);
      const bank = this.world.get(axialKey(at.q, at.r));
      if (!bank || isWater(bank) || !habitable(bank)) {
        return 'most musi łączyć ląd z lądem przez jedno pole wody';
      }
      if (Math.abs(bank.elevation - mirror) >= MAX_STEP) return 'brzeg jest za wysoko nad wodą';
    }
    return null;
  }

  /** Podgląd mostu pod kursorem, zielony albo czerwony. */
  private updateBridgeMark(cell: Axial | null): void {
    if (!cell) {
      this.bridgeAt = null;
      if (this.bridgePreview) this.bridgePreview.visible = false;
      return;
    }
    const axis = this.axisUnderCursor(cell);
    const same =
      this.bridgeAt &&
      this.bridgeAt.cell.q === cell.q &&
      this.bridgeAt.cell.r === cell.r &&
      this.bridgeAt.axis === axis;
    if (same) return;
    this.bridgeAt = { cell: { q: cell.q, r: cell.r }, axis };

    const refusal = this.bridgeRefusal(cell, axis);
    this.panel?.setHint(refusal);
    this.showBridgePreview(cell, axis, refusal === null);
  }

  private showBridgePreview(cell: Axial, axis: number, allowed: boolean): void {
    const model = this.bridgeModel;
    const data = this.world.get(axialKey(cell.q, cell.r));
    if (!model || !data) return;

    if (!this.bridgePreview) {
      this.bridgePreview = new THREE.Group();
      this.bridgePreview.name = 'bridge-preview';
      for (const part of model.parts) {
        const material = new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
        });
        const mesh = new THREE.Mesh(part.geometry, material);
        mesh.matrixAutoUpdate = false;
        this.bridgePreview.add(mesh);
      }
      this.scene.add(this.bridgePreview);
    }

    const { x, z } = axialToWorld(cell.q, cell.r);
    const middle = (edge: number) => ({
      x: (CORNERS[edge].x + CORNERS[edge + 1].x) / 2,
      z: (CORNERS[edge].z + CORNERS[edge + 1].z) / 2,
    });
    const y = elevationToY(isWater(data) ? (waterLevel(data) ?? 0) : data.elevation);
    const matrix = spanMatrix(
      { x, y, z, a: middle(axis), b: middle(axis + 3), simple: true },
      model
    );
    if (!matrix) return;

    for (const child of this.bridgePreview.children) {
      const mesh = child as THREE.Mesh;
      mesh.matrix.copy(matrix);
      (mesh.material as THREE.MeshBasicMaterial).color.setHex(allowed ? BUILD_OK : BUILD_NO);
    }
    this.bridgePreview.visible = true;
  }

  /**
   * Klik w trybie mostu: postaw tam, gdzie pokazuje podgląd — z brzegu, a gdy
   * gracz stoi daleko, najpierw prowadzi go na brzeg.
   */
  private clickBridge(chain = false): void {
    const at = this.bridgeAt;
    if (!at) return;
    const refusal = this.bridgeRefusal(at.cell, at.axis);
    if (refusal) {
      this.status?.say(refusal);
      return;
    }

    if (hexDistance(this.standing, at.cell) <= 1) {
      this.placeBridge(at.cell, at.axis, chain);
      return;
    }

    // Najbliższy brzeg, na który da się dojść — któryś z końców osi.
    const banks = [neighbor(at.cell, at.axis), neighbor(at.cell, at.axis + 3)];
    const target = banks.find((bank) => findRoute(this.world, this.standing, bank));
    if (!target) {
      this.status?.say('do tego brzegu nie da się dojść');
      return;
    }
    this.errand = { at: at.cell, what: 'bridge', axis: at.axis, chain };
    this.orderWalk(target);
    this.status?.say('idę postawić most');
  }

  private placeBridge(cell: Axial, axis: number, chain = false): void {
    this.status?.say('stawiam most…');
    void buildBridge(this.session.token, cell, axis)
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }
        this.takeRoads(answer.cells);
        this.status?.say('most się buduje');
        // Bez SHIFT tryb się kończy, jak po postawieniu budynku.
        if (!chain) this.panel?.bridgeMode(false);
        else this.bridgeAt = null;
      })
      .catch(() => this.status?.say('nie udało się postawić mostu'));
  }

  /**
   * Klik w trybie drogi: najpierw początek, potem koniec.
   *
   * Początek daleko od gracza zamawia marsz — i **tylko na początek**. Reszta
   * trasy powstaje bez chodzenia po niej: droga jest zamówieniem, a nie
   * wędrówką z taczką.
   */
  private clickRoad(cell: Axial, chain = false): void {
    if (!this.roadFrom) {
      if (cell.q === this.standing.q && cell.r === this.standing.r) {
        this.roadFrom = { q: cell.q, r: cell.r };
        this.status?.say('teraz wskaż, dokąd ma prowadzić');
        return;
      }

      if (!findRoute(this.world, this.standing, cell)) {
        this.status?.say('tam nie da się dojść');
        return;
      }

      this.errand = { at: { q: cell.q, r: cell.r }, what: 'road' };
      this.orderWalk(cell);
      this.status?.say('idę na początek drogi');
      return;
    }

    this.orderRoad(cell, chain);
  }

  /**
   * Zamów drogę od początku odcinka do wskazanego pola.
   *
   * **Początkiem jest `roadFrom`, nie pole pod stopami.** Przy pierwszym
   * odcinku to jedno i to samo, przy SHIFT-cie już nie: ciąg dalszy zaczyna się
   * tam, gdzie skończył się poprzedni, a postać zostaje na miejscu. To ta sama
   * zmienna, z której liczy podgląd pod kursorem — liczenie zamówienia od
   * czegoś innego znaczyło kładzenie drogi innej niż narysowana.
   */
  private orderRoad(to: Axial, chain = false): void {
    if (!this.catalogue) return;

    const from = this.roadFrom;
    if (!from) return;

    const route = findRoute(this.world, from, to, 'road');
    if (!route) {
      this.status?.say('tamtędy droga nie pobiegnie');
      return;
    }

    this.status?.say('kładę drogę…');
    // **The route travels, not just its end.** The server used to search for it
    // again, and over flat ground — where a dozen ways are equally fast — its
    // answer was a different dozen than the one drawn under the cursor. Two
    // searches cannot be made to agree on which of several equal routes to
    // pick; what they can agree on is whether a route is legal, so the player
    // picks and the server checks.
    // Pierwsze pole jedzie w trasie: serwer nie dokleja już pozycji gracza,
    // bo przy ciągu dalszym stoi ona gdzie indziej niż początek odcinka.
    void buildRoad(this.session.token, [from, ...route])
      .then((answer) => {
        if ('code' in answer) {
          this.status?.say(answer.message);
          return;
        }

        this.takeRoads(answer.cells);

        const paid = `${answer.paid} ${
          this.materials ? formOf(this.materials, answer.material, answer.paid) : answer.material
        }`;

        this.preview?.clear();
        this.previewTo = null;

        if (chain) {
          // **Ciąg dalszy zaczyna się tam, gdzie skończył się odcinek.** O to
          // chodzi w trzymaniu SHIFT-a przy drodze: sieć rysuje się kolejnymi
          // kliknięciami, a nie wskazywaniem początku od nowa przy każdym z nich.
          // Postać zostaje na miejscu — prowadzimy ją tylko na **pierwszy**
          // początek, a nie wzdłuż budowanej trasy.
          this.roadFrom = { q: to.q, r: to.r };
          this.status?.say(`droga na ${answer.cells.length} pól — ${paid}; ciągnę dalej`);
          return;
        }

        // Bez SHIFT-a zamówienie kończy tryb: klik znaczy znowu to, co znaczył
        // przedtem.
        this.panel?.paveMode(false);
        this.status?.say(
          `droga na ${answer.cells.length} pól — ${paid} (SHIFT+klik ciągnie dalej)`
        );
      })
      .catch((error) => {
        console.error('droga:', error);
        this.status?.say('nie udało się położyć drogi');
      });
  }

  /**
   * Połóż się spać w swojej osadzie.
   *
   * Pole osady przychodzi w odpowiedzi, bo kładąc się, gracz **przechodzi** na
   * nie: zasięg pozwala działać z sąsiedniego, a „śpię w Białogrodzie, stojąc
   * na łące obok" nie jest faktem, który chcemy mieć prawdziwy.
   */
  private async lieDown(building: string): Promise<void> {
    // Wszystko, co trwa, kończy się tu samo: idziemy spać, a nie w drogę.
    this.forgetErrand();

    const answer = await rest(this.session.token, building).catch((error) => {
      console.error('odpoczynek:', error);
      return { code: 'broken', message: 'nie udało się odpocząć' };
    });

    if ('code' in answer) {
      this.status?.say(answer.message);
      return;
    }

    this.standing = answer.position;
    this.settleRest(answer.building);
  }

  /** Wstań. Budzi też każda akcja poza osadą — to jest ta jawna droga. */
  private async getUp(): Promise<void> {
    const refused = await wake(this.session.token).catch((error) => {
      console.error('wstawanie:', error);
      return { code: 'broken', message: 'nie udało się wstać' };
    });

    if (refused) this.status?.say(refused.message);
    // Nic więcej: stan przychodzi wiadomością `rest`, tak samo jak wtedy, gdy
    // budzi serwer. Dwie drogi do jednego stanu to dwie drogi do rozjechania go.
  }

  /**
   * Przyjmij stan odpoczynku, skądkolwiek przyszedł.
   *
   * Jedno miejsce dla własnego wywołania i dla wiadomości serwera, bo obie
   * mówią o tym samym — a pionek znikający dwiema drogami znikałby kiedyś
   * połową z nich.
   */
  private settleRest(building: string | null, slot = 0): void {
    if (this.restingIn === building) return;
    this.restingIn = building;

    // Sen i przebudzenie zmieniają to, co panel ma pokazywać — wnętrze osady
    // albo pole pod nogami — a jedno i drugie jest widoczną zmianą, więc nie
    // czeka na najbliższe odświeżenie. Zgaszenie Escape'em przestaje przy tym
    // obowiązywać: wejście pod dach jest wejściem gdzie indziej.
    this.spotShut = false;

    if (building) {
      // Marsz kończy się razem z wejściem pod dach — serwer i tak wyrzucił
      // trasę, kładąc nas do łóżka, a linia na ziemi zostałaby po niczym.
      this.connection?.stopWalking();
      this.walkingTo = null;
      this.mark?.hide();
      // **Najpierw wejście, potem zniknięcie.** Kładąc się, gracz przechodzi na
      // pole osady (serwer oddaje je w odpowiedzi), a dopiero stamtąd schodzi
      // z mapy. Bez tego kroku pionek gasł tam, gdzie stał — czyli o pole przed
      // osadą, do której właśnie wchodził. `remove` dokańcza to przejście
      // i zdejmuje pionka w klatce, w której stanął; przy zaśnięciu z tego
      // samego pola nie ma czego dokańczać i gaśnie od razu.
      //
      // **Pole osady bierze się z samej osady, nie z `standing`.** Wiadomość
      // `rest` przychodzi socketem przed odpowiedzią HTTP, która niesie nową
      // pozycję — `standing` jest wtedy jeszcze polem przed drzwiami.
      const home = this.buildings?.find(building);
      if (home) this.standing = { q: home.q, r: home.r };
      this.markers?.moveTo(this.joined.playerId, this.standing, slot);
      // Znika i pionek, i nazwa nad nim: etykiety jadą za znacznikami.
      this.markers?.remove(this.joined.playerId);
      this.status?.setPlayer(this.session.username, this.countPlayers());
      this.status?.say('odpoczywasz — kliknij cokolwiek poza osadą, żeby wstać');
      this.refreshSpot();
      return;
    }

    this.markers?.add(this.joined.playerId, this.session.username, this.standing, slot);
    this.markers?.refresh();
    this.status?.setPlayer(this.session.username, this.countPlayers());
    this.status?.say('wstałeś');
    this.refreshSpot();
  }

  /**
   * Zapamiętaj wycięte pola i przerysuj te, które coś jeszcze pokazywały.
   */
  private takeFelled(cells: Axial[]): void {
    if (cells.length === 0) return;
    for (const cell of cells) this.felled.set(axialKey(cell.q, cell.r), cell);
    this.streamer?.repaint(this.applyFelled());
  }

  /**
   * Wyzeruj las na wyciętych polach, które są w pamięci. Oddaje pola, na
   * których las jeszcze był — tylko te trzeba przerysować.
   *
   * Zero w mapie pól wystarcza wszystkim czytającym po tej stronie: rekwizytom,
   * kolorowi gruntu, regule kroku (`Movement.ts`) i regułom miejsca
   * (`Site.ts`) — tak samo jak po stronie serwera wystarcza nakładka w magazynie
   * świata.
   */
  private applyFelled(): Axial[] {
    const changed: Axial[] = [];
    for (const [key, cell] of this.felled) {
      const data = this.world.get(key);
      if (!data?.forest) continue;
      data.forest = 0;
      changed.push(cell);
    }
    return changed;
  }

  /**
   * Wpisz drogi do mapy pól i przemesz to, co je niesie.
   *
   * `settle` robi obie rzeczy naraz i jest tym samym wywołaniem, którego używa
   * postawiony budynek: podnosi flagę wypłaszczenia i przebudowuje dotknięte
   * chunki. Wypłaszczenie jest tu tym samym faktem co pod budynkiem — pole
   * z drogą siada na własnej wysokości, więc wstęga nie wije się po zboczu
   * uśrednionym z sąsiadów.
   */
  private takeRoads(cells: RoadCell[]): void {
    if (cells.length === 0) return;

    const now = performance.now();
    for (const cell of cells) {
      // A moment on this client's clock rather than the countdown as sent: the
      // countdown was true when the server said it, and this map outlives that
      // by minutes. The same arrangement the building sites use.
      this.roads.set(axialKey(cell.q, cell.r), {
        cell,
        readyAt: now + cell.readyIn * 1000,
      });
    }

    this.applyRoads();
    this.streamer?.settle(cells.map((cell) => ({ q: cell.q, r: cell.r })));
  }

  /**
   * Przepisz maski dróg do pól, które są w pamięci.
   *
   * Wołane też po każdym dojechaniu terenu, bo pole przyjeżdża bez maski:
   * lista obszaru i chunki są dwoma strumieniami i nic nie mówi, który dojedzie
   * pierwszy.
   */
  private applyRoads(): Axial[] {
    const now = performance.now();
    let soonest = Infinity;
    const flipped: Axial[] = [];
    for (const [key, road] of this.roads) {
      // Kiedy najbliższa z budowanych dojrzeje. Zbierane przed odczytem pola,
      // bo droga poza wczytanym terenem też ma swój termin — a budzik ma być
      // jeden dla wszystkich, nie jeden dla widocznych.
      if (road.readyAt > now && road.readyAt < soonest) soonest = road.readyAt;

      const data = this.world.get(key);
      if (!data) continue;
      data.road = road.cell.mask;
      // Laid but not yet carrying anybody. Read by the route preview, so a road
      // still being built does not draw itself into a walk that would not use
      // it — the server would not either.
      const pending = road.readyAt > now || undefined;
      if (pending !== data.roadPending) flipped.push(road.cell);
      data.roadPending = pending;
      data.levelled = true;
    }

    this.armRoadClock(soonest - now);
    return flipped;
  }

  /**
   * Przypomnij sobie o drogach w budowie, gdy najbliższa stanie się gotowa.
   *
   * **`roadPending` jest migawką, a fakt, z którego się bierze, jest momentem.**
   * Bez tego przypomnienia droga położona w tej sesji zostaje dla liczenia trasy
   * lasem na zawsze: flaga zapada raz, przy wpisaniu maski do pól, i nic jej nie
   * odświeża, dopóki nie dojedzie na nowo teren pod nią albo nie powstanie
   * następna droga. Objaw jest mylący, bo wstęgę widać od razu — zapłacone jest
   * od razu — więc trasa omija drogę, która na ekranie jest gotowa.
   *
   * Jeden budzik na najbliższy termin, a nie odczyt zegara w `crossingSeconds`:
   * to jest do sześciu odczytów na węzeł przy sześciu tysiącach węzłów, na każde
   * drgnięcie kursora, za fakt zmieniający się raz na kilka minut.
   */
  private armRoadClock(delay: number): void {
    if (this.roadClock !== undefined) {
      clearTimeout(this.roadClock);
      this.roadClock = undefined;
    }
    if (!Number.isFinite(delay)) return;

    // Milisekunda zapasu, żeby budzik nie trafił w tę samą chwilę co termin
    // i nie zastał drogi wciąż niegotowej: przezbroiłby się wtedy na zero
    // i kręcił w kółko.
    this.roadClock = setTimeout(() => {
      this.roadClock = undefined;
      // Wykop, który właśnie stał się drogą, ma się przerysować drogą.
      this.streamer?.repaint(this.applyRoads());
      // Linia pod kursorem jest przeliczana dopiero wtedy, gdy cel się zmieni,
      // a tu zmieniła się arytmetyka pod nią. Bez tego droga zaczyna się liczyć
      // w chwili, w której gracz ruszy myszką, a nie w chwili, w której stała
      // się gotowa.
      this.previewTo = null;
    }, Math.max(0, delay) + 1);
  }

  /**
   * Jak nazwać budynek, gdy zna się tylko jego identyfikator.
   *
   * Nazwa osady wygrywa z rodzajem — Białogród jest wioską, ale nazywa się
   * Białogród — a budynek, o którym ten klient jeszcze nie słyszał, dostaje
   * kreskę zamiast pustego miejsca: panel ma być czytelny także wtedy, gdy cel
   * stoi poza wczytanym obszarem.
   */
  private describeBuilding(id: string): string {
    const placed = this.buildings?.find(id);
    if (!placed) return '—';

    const kind = this.catalogue?.buildings.find((entry) => entry.id === placed.kind);
    return nameOf(kind, placed);
  }

  /** Przeczytaj zlecenia i wozy, i podaj je panelowi. */
  private refreshHaul(): void {
    if (!this.haul) return;

    void loadBoard(this.session.token)
      .then((board) => {
        if ('code' in board) {
          this.status?.say((board as { message: string }).message);
          return;
        }
        this.haul?.set(board);
      })
      .catch((error) => console.warn('transporty:', error));
  }

  /**
   * Formularz zlecenia: dokąd, czego i od ilu.
   *
   * Cel jest listą **własnych osad**, bo tyle dziś umie serwer przyjąć od tego
   * formularza — sam endpoint przyjmuje dowolny budynek, który przyjmuje
   * surowce, i to jest miejsce, przez które wejdzie handel z innymi graczami.
   */
  private askShipment(placed: Placed): void {
    if (!this.menu || !this.catalogue || !this.materials) return;

    const kind = this.catalogue.buildings.find((entry) => entry.id === placed.kind);
    const makes = [
      ...new Set(
        (kind?.levels ?? []).flatMap((level) =>
          (level.output?.yields ?? []).map((yielded) => yielded.material)
        )
      ),
    ];

    const settlements = (this.buildings?.ownedAll(this.joined.playerId) ?? []).filter(
      (building) => building.name && building.id !== placed.id
    );

    const form = document.createElement('div');
    form.className = 'building-menu-form';

    if (makes.length === 0 || settlements.length === 0) {
      const why = document.createElement('div');
      why.className = 'building-menu-hint';
      why.textContent =
        makes.length === 0
          ? 'ten budynek niczego nie produkuje'
          : 'nie masz osady, do której można wysyłać — rozbuduj obóz do wioski';
      form.appendChild(why);
      this.openShip(placed, form);
      return;
    }

    const target = document.createElement('select');
    target.className = 'building-menu-input';
    for (const settlement of settlements) {
      const option = document.createElement('option');
      option.value = settlement.id;
      option.textContent = settlement.name ?? '—';
      target.appendChild(option);
    }

    const material = document.createElement('select');
    material.className = 'building-menu-input';
    for (const key of makes) {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = formOf(this.materials, key, 2);
      material.appendChild(option);
    }

    const threshold = document.createElement('input');
    threshold.type = 'number';
    threshold.className = 'building-menu-amount';
    threshold.min = '1';
    threshold.value = '20';
    threshold.title = 'od ilu wóz rusza';

    // Ile wieźć: cały zapas albo tyle, ile wpisano. Dwa pola, bo to dwa różne
    // zdania — „ruszaj przy pięćdziesięciu" i „wieź po dwadzieścia" da się
    // wtedy powiedzieć naraz.
    const how = document.createElement('select');
    how.className = 'building-menu-input';
    for (const [value, label] of [
      ['all', 'cały zapas'],
      ['some', 'po tyle:'],
    ] as const) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      how.appendChild(option);
    }

    const load = document.createElement('input');
    load.type = 'number';
    load.className = 'building-menu-amount';
    load.min = '1';
    load.value = '20';
    load.hidden = true;
    load.title = 'ile wóz zabiera';

    how.addEventListener('change', () => {
      load.hidden = how.value !== 'some';
    });

    const send = document.createElement('button');
    send.type = 'button';
    send.className = 'building-menu-send';
    send.textContent = 'ustaw';
    send.addEventListener('click', () => {
      const atLeast = Math.max(1, Math.floor(Number(threshold.value) || 0));
      const amount =
        how.value === 'some' ? Math.max(1, Math.floor(Number(load.value) || 0)) : null;
      this.closeShip();
      void orderShipment(this.session.token, {
        source: placed.id,
        target: target.value,
        material: material.value,
        atLeast,
        amount,
      }).then((answer) => {
        if (answer && 'code' in answer) {
          this.status?.say((answer as { message: string }).message);
          return;
        }
        this.status?.say('zlecenie ustawione');
        this.refreshHaul();
      });
    });

    const hint = document.createElement('div');
    hint.className = 'building-menu-hint';
    hint.textContent =
      'wóz rusza, gdy uzbiera się tyle — a jeśli już jest, rusza od razu';

    // W kolumnie, pole pod polem, każde z podpisem: panel boczny jest wąski,
    // a rząd pięciu kontrolek bez nazw dało się czytać tylko z dymków.
    const field = (label: string, ...controls: HTMLElement[]): HTMLElement => {
      const wrap = document.createElement('label');
      wrap.className = 'ship-field';
      const name = document.createElement('span');
      name.className = 'ship-label';
      name.textContent = label;
      wrap.append(name, ...controls);
      return wrap;
    };

    form.classList.add('ship-form');
    form.append(
      field('dokąd', target),
      field('co', material),
      field('wóz rusza, gdy uzbiera się', threshold),
      field('ile wieźć', how, load),
      send,
      hint
    );
    this.openShip(placed, form);
  }

  private updatePreview(): void {
    if (!this.preview) return;

    const target = this.hoveredCell;
    const key = target ? axialKey(target.q, target.r) : null;
    const standing = axialKey(this.standing.q, this.standing.r);
    const picked = this.chosenAt ? axialKey(this.chosenAt.q, this.chosenAt.r) : null;

    // In road mode the cursor draws the road that would be laid, from the cell
    // its beginning is on. The same line, because it is the same question —
    // which way would this go — and two lines would be two answers.
    if (this.roadMode && this.roadFrom) {
      if (!target || key === this.previewTo) return;
      this.previewTo = key;

      const way = findRoute(this.world, this.roadFrom, target, 'road');
      if (way) this.preview.set(this.roadFrom, way);
      else this.preview.clear();
      return;
    }

    // Nothing picked, nothing hovered, our own cell, or the cell already picked
    // — in every one of those the cursor has nothing to add.
    if (!target || !picked || this.debugMode || key === standing || key === picked) {
      if (this.previewTo !== null) {
        this.preview.clear();
        this.previewTo = null;
      }
      return;
    }

    if (key === this.previewTo) return;
    this.previewTo = key;

    const route = findRoute(this.world, this.standing, target);
    if (route) {
      this.preview.set(this.standing, route);
    } else {
      // No way there — from here, with what is loaded. Nothing is drawn rather
      // than something wrong; the click still goes to the server, which knows
      // the whole map and answers `noWay` if that is the truth.
      this.preview.clear();
    }
  }

  /**
   * Key bindings.
   *
   * F3 is the only key that works outside debug mode; everything else lives on
   * the number row and answers only while it is on. The letters are left alone
   * deliberately — they belong to the game, and a debug overlay that responds
   * to a key the player will one day press for something else is a trap laid
   * for later.
   */
  private onKeyDown = (event: KeyboardEvent): void => {
    // Escape means "never mind", and it means it about **everything at once**.
    //
    // It used to unwind one thing per press, innermost first, and the order was
    // written down as a feature. In the hand it was not one: a player with a
    // building on the cursor, a road half drawn and a destination picked had to
    // guess how many times to press a key whose whole point is not having to
    // think. Now one press clears the lot, and the menu opens only when there
    // was nothing to clear — so the key still answers, whatever the game is in
    // the middle of.
    //
    // What it does **not** touch is a walk already under way: that is Space,
    // which also brings the camera back to the character. Stopping is a
    // different intention from calling something off, and one key for both
    // would take the camera somewhere on a press meant to drop a menu.
    if (event.key === 'Escape') {
      event.preventDefault();

      // The pause menu keeps its own back step, and it is the only exception:
      // inside it Escape walks out of Settings into the main column, which is
      // navigation rather than an action, and the only way back up.
      if (this.pause?.escape()) return;

      // Everything else goes together, the open building menu included — it is
      // a thing on the screen waiting for an answer, and "never mind" is one.
      let something = false;
      if (this.menu?.open) {
        this.menu.hide();
        something = true;
      }
      if (this.callOff()) something = true;

      // Nothing was in the way, so the key does the other thing it means.
      if (!something) this.pause?.toggle();
      return;
    }

    // Everything below is the game's, and the game is behind the menu.
    if (this.pause?.open) return;

    // Space attends to the character: stop walking, and look at them. Two
    // things under one key because they are one instinct — the moment you want
    // to stop is the moment you want to see where you stopped.
    if (event.code === 'Space') {
      event.preventDefault();

      // Stop where you are. The server owns the route, so this is a request
      // like any other — the walking stops when it says it has stopped, and the
      // drawn route is cleared here because there is no longer one to walk.
      this.forgetErrand();
      this.connection?.stopWalking();
      this.route?.clear();
      this.stopGliding();
      if (this.felling) this.putAxeDown();

      // A destination picked but never ordered goes with it: the key means
      // "never mind", and half of never mind is not leaving an X on the map.
      this.forget();

      // And look at them, which is what this key did before stopping was a
      // thing it could do. Both are the same instinct: attend to my character.
      const at = this.markers?.cellOf(this.joined.playerId) ?? this.standing;
      const { x, z } = axialToWorld(at.q, at.r);
      this.cameraController.glideTo(x, z, this.groundAt(at));
      return;
    }

    if (event.key === 'F3') {
      // Firefox puts find-again here; the browser does not need to hear it
      event.preventDefault();
      this.setDebugMode(!this.debugMode);
      return;
    }

    if (!this.debugMode) return;

    switch (event.key) {
      case '1':
        return this.toggleOverlay(this.gridOverlay, 'Hex outlines');
      case '2':
        return this.toggleOverlay(this.chunkBorders, 'Chunk borders');
      case '3':
        return this.setWaterSeeThrough(!this.waterSeeThrough);
      case '4':
        void this.togglePathTracer();
        return;
      case '5':
        return this.setDepositView(!this.depositView);
      case '6':
        return this.setBlockedView(!this.blockedView);
    }
  };

  /**
   * Zasobność pól kołami zamiast rekwizytów.
   *
   * Las rysuje się jako drzewa, a kamień jako głazy, i **po ich liczbie da się
   * odczytać poziom** — tylko trzeba je w tym celu policzyć, pole po polu. Ten
   * widok odpowiada na to samo pytanie jednym spojrzeniem: czerwone ubogie,
   * zielone bogate. Modele na ten czas znikają, bo inaczej odpowiedź czytałoby
   * się przez rzecz, którą zastępuje.
   *
   * Ryby widać **wyłącznie tutaj**, i to nie jest wyjątek, tylko ta sama
   * zasada: ławica nie ma modelu, który mogłaby zastąpić, więc poza tym widokiem
   * nie ma czego pokazywać.
   */
  private setDepositView(shown: boolean): void {
    const objects = this.vocabulary?.objects ?? null;

    // The dictionary says which layers are deposits at all, so without it there
    // is nothing to draw. The switch stays **off** rather than on-with-nothing:
    // a flag that says yes over an empty screen would need the key pressed
    // twice to get anywhere.
    if (shown && !objects) {
      this.status?.say('słownik obiektów jeszcze nie dojechał');
      return;
    }

    this.depositView = shown;
    this.streamer?.showDeposits(shown, objects);

    console.log(`🎨 Zasobność złóż ${shown ? 'on' : 'off'}`);
  }

  /**
   * Po czym nie da się chodzić, na mapie.
   *
   * **Dwa kolory, bo to dwa różne „nie".** Czerwony: pole odmawia samo z siebie
   * — woda, gąszcz, głazowisko. Pomarańczowy: pole jest do stania, ale **nie ma
   * jak do niego wejść**, bo każdy z sześciu sąsiadów leży za wysoko albo za
   * nisko. Drugi z nich jest tym, który zmienia się przy każdym pokręceniu
   * progiem kroku — i po to ta nakładka głównie jest.
   */
  private setBlockedView(shown: boolean): void {
    this.blockedView = shown;
    this.streamer?.showBlocked(shown);

    // Budynki i wozy też schodzą z widoku: sylwetka stojąca na polu zasłania
    // dokładnie to pole, o które ta nakładka pyta. Rekwizyty chowa streamer,
    // bo to on nimi zarządza.
    if (this.buildings) this.buildings.object.visible = !shown;
    if (this.convoys) this.convoys.object.visible = !shown;
    console.log(
      `🚧 Nieprzechodność ${shown ? 'on' : 'off'} — czerwone: grunt odmawia, ` +
        `pomarańczowe: odcięte progiem kroku, białe linie: krawędź nie do przejścia`
    );
  }

  /**
   * Debug mode: the counters on screen and the number row live.
   *
   * Switching it off puts back what it drew — overlays hidden, sea solid.
   * Otherwise the view could be left in a state whose only way out is a key
   * that no longer answers.
   */
  private setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    this.hud?.setVisible(enabled);

    // The overlay and the routes share the cursor, and only one of them has it
    // at a time.
    this.hovered = null;
    this.forget();
    this.highlight?.hide();

    if (!enabled) {
      this.gridOverlay.visible = false;
      this.chunkBorders.visible = false;
      this.setWaterSeeThrough(false);
      this.setDepositView(false);
      this.setBlockedView(false);
      // Left on, it would be a picture of the world with no way back to the
      // world: the key that turns it off is one debug mode owns.
      this.stopPathTracer();
    }

    console.log(
      `🔷 Debug ${enabled ? 'on' : 'off'} — ` +
        `1 hex outlines · 2 chunk borders · 3 see-through water · 4 path tracing · ` +
        `5 deposit richness · 6 impassable`
    );
  }

  /**
   * Switch the path traced view on or off.
   *
   * Rebuilds the hierarchies every time it is switched on, because the world it
   * saw last time has almost certainly streamed since. That is the expensive
   * part and it is reported in the console, being the number that says whether
   * this is usable at the current draw radius.
   */
  private async togglePathTracer(): Promise<void> {
    if (this.tracing) {
      this.stopPathTracer();
      return;
    }

    this.tracer ??= new PathTracer(this.container);
    if (!(await this.tracer.open())) return;

    // Nothing of the interface belongs in the picture: the overlays are lines
    // drawn over the world, and a path tracer that traced them would be tracing
    // the debug view rather than the world it is meant to show.
    const overlays = [this.gridOverlay.visible, this.chunkBorders.visible];
    this.gridOverlay.visible = false;
    this.chunkBorders.visible = false;

    this.tracer.capture(this.scene, (mesh) =>
      this.waterMaterials.has(mesh.material as THREE.Material)
    );

    this.gridOverlay.visible = overlays[0];
    this.chunkBorders.visible = overlays[1];

    this.tracing = true;
    this.tracer.show();

    const stats = this.tracer.stats;
    console.log(
      `🔷 Path tracing on — ${stats.triangles} trójkątów w ${stats.geometries} ` +
        `geometriach, ${stats.instances} instancji, BVH w ${stats.buildMs.toFixed(0)} ms, ` +
        `${stats.width}×${stats.height}`
    );
  }

  private stopPathTracer(): void {
    if (!this.tracing) return;
    this.tracing = false;
    this.tracer?.hide();
    console.log('🔷 Path tracing off');
  }

  private toggleOverlay(group: THREE.Group, label: string): void {
    group.visible = !group.visible;
    console.log(`🔷 ${label}: ${group.visible ? 'on' : 'off'}`);
  }

  /**
   * Let the sea be looked through, to read the shape of the seabed under it.
   *
   * The water stays in the picking targets either way. The ray answering with
   * the surface rather than the bottom is not an accident of the water being
   * opaque — it is what keeps the answer under the cursor, since a seabed two
   * levels down sits nearly three cells away in screen terms at this camera
   * angle.
   *
   * depthWrite goes off with it so the stretches of sea do not occlude each
   * other; the terrain is drawn before them regardless, being opaque.
   */
  private setWaterSeeThrough(seeThrough: boolean): void {
    if (!this.waterMaterial || seeThrough === this.waterSeeThrough) return;

    this.waterSeeThrough = seeThrough;
    this.waterMaterial.transparent = seeThrough;
    this.waterMaterial.depthWrite = !seeThrough;
    this.waterMaterial.opacity = seeThrough ? WATER_SEE_THROUGH_OPACITY : 1;
    this.waterMaterial.needsUpdate = true;

    console.log(`🔷 Water: ${seeThrough ? 'see-through' : 'solid'}`);
  }

  /**
   * Start game loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastFrameTime = performance.now();
    this.gameLoop();

    console.log('🚀 Game loop started');
  }

  /**
   * Stop game loop
   */
  stop(): void {
    this.isRunning = false;
  }

  /**
   * Main game loop
   */
  private gameLoop = (): void => {
    if (!this.isRunning) return;

    requestAnimationFrame(this.gameLoop);

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
    this.lastFrameTime = currentTime;

    this.update(deltaTime);
    this.render();
  };

  /**
   * Update game state
   */
  private update(deltaTime: number): void {
    this.advanceReveal(deltaTime);
    this.updateSky(deltaTime);
    // The heading the camera is at now, not the one it is settling towards:
    // a compass that showed the target would swing before the world does.
    this.compass?.update(this.cameraController.heading);
    // Licznik rozmowy idzie własnym zegarem, z terminu — jak pasek budowy.
    this.talk?.tick();
    // Panel pola dwa razy na sekundę, nie co klatkę: „Zbierz 12 🪵" rośnie
    // z czasem produkcji, ale nie szybciej niż raz na sekundę, a `menuFor`
    // składa przy każdym pytaniu kilka tablic. Panel sam pilnuje, żeby nie
    // przemalowywać się bez zmiany treści.
    this.spotAge += deltaTime;
    if (this.spotAge >= 0.5) {
      this.spotAge = 0;
      this.refreshSpot();
      this.refreshStore();
      this.tickFelling();
    }
    // Update camera controls
    this.cameraController.update(deltaTime);

    this.updateTerrain();
    this.updateHover();
    this.markers?.update(deltaTime);
    this.convoys?.update(deltaTime);
    this.ships?.update(deltaTime);
    this.followBody();
    // Obraz kończy trasę po serwerze, więc zestawienie dopina się tutaj —
    // i tylko wtedy, gdy jest na co czekać.
    if (this.travelled) this.reckon();
    this.fellingTrees?.update(deltaTime);
    bannerClock.value += deltaTime;
    this.reportView(deltaTime);
    this.hud?.setCells(this.describeCell(this.standing), this.describeCell(this.hoveredCell));

    if (this.daylight && this.skyNow) {
      // „Światło stoi" znaczy teraz to, co mówi **serwer**: dobę zatrzymuje
      // panel, nie ten klient. Wcześniej ta lampka zapalała się od trybu debug,
      // który trzymał słońce wyłącznie u siebie.
      this.hud?.setSky(
        this.daylight.clock,
        this.skyNow.altitude,
        this.skyNow.night,
        this.daylight.held
      );
    }

    this.worldClock += deltaTime;
    this.listenAround(deltaTime);

    this.updateClouds(deltaTime);
    this.updateSmoke(deltaTime);
    // Bez nieba nie ma pory dnia, a bez pory dnia ogień nie wie, czy się palić.
    if (this.hearths && this.skyNow) {
      this.hearths.update(
        () => this.buildings?.standingAll() ?? [],
        this.skyNow,
        this.cameraController.focusPoint,
        deltaTime
      );
    }

    this.hud?.setView(this.camera.zoom, this.viewReach());

    // Update other game systems here
    // e.g., animations, particles, AI, etc.
  }

  /**
   * Dym: nad tym, co się buduje, i nad tym, co pali ogień.
   *
   * Zbiór budów bierze się stąd, skąd paski postępu i rusztowania — z pola
   * budynków — więc trzy rzeczy nad jednym placem mówią o tej samej budowie.
   * Ogniska idą z tej samej listy i przez **ten sam filtr, co światło nocą**
   * (`LIT`): budynek, który świeci, jest dokładnie tym, który dymi.
   *
   * **Plac budowy nie dymi dwa razy.** Ma swój, gęstszy słup, a `site` wyklucza
   * go z ognisk — to ta sama flaga, którą czyta ogień.
   *
   * Nic tu nie kosztuje, gdy nie ma ani jednego z dwojga: puste listy gaszą
   * obiekt.
   */
  private updateSmoke(deltaTime: number): void {
    if (!this.smoke || !this.skyNow) return;

    const hearths = (this.buildings?.standingAll() ?? [])
      .filter((building) => !building.site && LIT.has(building.key))
      .map((building) => ({
        id: building.id,
        base: building.position,
        // Kominy z modelu, ile ich ustawiono. Pusto znaczy „bryła bez punktów"
        // i wtedy dym idzie znad środka pola, tak jak szedł zawsze.
        vents: building.smoke,
      }));

    this.works = this.roadWorks?.update(this.roads, performance.now()) ?? [];
    this.smoke.update(
      [...(this.buildings?.raising() ?? []), ...this.works],
      hearths,
      this.skyNow,
      this.reveal,
      deltaTime
    );
  }

  /**
   * Chmury i ich cień, o jedną klatkę dalej.
   *
   * Liczone z tego samego stanu nieba, którym jadą światła — cień chmury jest
   * cieniem, więc gaśnie razem ze słońcem i nie ma go w nocy. Przy wyłączonych
   * chmurach uniform cienia siada na zerze i gałąź w shaderze terenu wypada,
   * więc wyłączenie naprawdę nic nie kosztuje.
   */
  private updateClouds(deltaTime: number): void {
    if (!this.clouds || !this.weather || !this.daylight) return;

    const sky = this.skyNow ?? this.daylight.state();

    const focus = this.cameraController.focusPoint;
    const weather = this.weather.update(
      deltaTime,
      this.worldClock,
      focus.z,
      this.worldHalfDepth
    );
    this.clouds.update(weather, sky, focus, this.reveal, this.camera.zoom, this.camera);
    // Zasięg widoku, nie przybliżenie: pudło deszczu ma obejmować to, co widać,
    // a to zależy też od rozmiaru okna. Ta sama miara, którą stawia się mgłę.
    this.rain?.update(weather, sky, focus, this.reveal, this.viewReach(), deltaTime);
  }

  /**
   * Kto zrobił to, co słychać.
   *
   * Read out of the audio manifest rather than kept in a second list, because
   * the manifest is where provenance already lives — beside the file it is
   * about, which is what stops the two from drifting. A credits screen written
   * by hand is one that is right on the day it is written.
   *
   * Names come from the dictionaries where there are any: `OBJ_FOREST` is "las"
   * on this screen for the same reason it is "las" in a refusal.
   */
  private credits(): Credit[] {
    if (!this.sounds) return [];

    const named = (key: string): string => {
      if (this.vocabulary) {
        const object = this.vocabulary.objects.objects[key];
        if (object) return object.label;
        const biome = this.vocabulary.biomes.get(key);
        if (biome) return biome.label;
      }
      return key;
    };

    return [
      ...this.sounds.music.map((entry) => ({ what: 'muzyka', ...entry })),
      ...this.sounds.ambience.map((entry) => ({ what: named(entry.key), ...entry })),
      ...this.sounds.steps.map((entry) => ({ what: 'kroki', ...entry })),
    ];
  }

  /**
   * Wyjście z gry.
   *
   * The token goes first and the page is reloaded after, rather than this
   * client being taken apart by hand: `main.ts` starts by looking for a stored
   * session, so a reload with none is exactly the login screen, assembled by
   * the code that already knows how. Even a server that never hears about the
   * logout leaves this browser without a token.
   */
  private async logOut(): Promise<void> {
    await logOut(this.session);
    location.reload();
  }

  /**
   * Ile lasu i morza jest wokół, i jak głośno mają być.
   *
   * On a slow beat rather than on every step, and that is not laziness: terrain
   * streams in after the player is already standing on it, so a reading taken
   * only when they move would leave a wood silent until they walked. Half a
   * second is far below the time the beds take to glide to a new level, so
   * nothing is heard to happen in steps.
   *
   * Silent until the vocabulary has arrived: without the dictionaries there is
   * no answer to what a wood is, and guessing one here is the second definition
   * this whole arrangement exists to avoid.
   */
  private listenAround(deltaTime: number): void {
    if (!this.audio || !this.vocabulary) return;

    this.listened += deltaTime;
    if (this.listened < Game.LISTEN_SECONDS) return;
    this.listened = 0;

    for (const key of this.audio.ambienceKeys) {
      const radius = this.audio.radiusOf(key);
      this.audio.setAmbience(
        key,
        nearness(this.world, this.vocabulary, this.standing, key, radius)
      );
    }

    // Plac budowy: najbliższy, czyjkolwiek, liniowo ciszej z odległością.
    const reach = this.audio.constructionRadius;
    if (reach > 0) {
      let nearest = Infinity;
      for (const site of this.buildings?.sites() ?? []) {
        nearest = Math.min(nearest, cellDistance(site, this.standing));
      }
      this.audio.setConstruction(Math.max(0, 1 - nearest / reach));
    }
  }

  /**
   * Render scene
   */
  private render(): void {
    this.renderer.render(this.scene, this.camera);

    // Over the top, from its own device. The raster frame is still drawn
    // underneath: it costs a millisecond, it keeps the picking and the overlays
    // working, and it is what is there the instant this is switched off.
    if (this.tracing && this.skyNow) {
      this.tracer?.render(this.camera, this.skyNow);
    }

    // There is now a frame to look at, and behind the closed fog it is a dark
    // rectangle. Shown after the draw rather than before it, so nothing is
    // revealed until there is genuinely something in the buffer.
    if (this.shown === 'blank') this.stage('world');

    // Budowy, którym upłynął czas: bryła podmienia się z własnego zegara tej
    // strony, nie z wiadomości. Przed nakładkami, bo to one wieszają się na
    // wysokości, która przez tę podmianę właśnie się zmieniła.
    this.buildings?.tick();

    this.hud?.update(this.renderer, this.picker?.lastPickMs ?? 0);
    // After the render, because a tag follows a pawn that the render has just
    // put in its final place for this frame.
    // Własny podpis nad statkiem: pionka nie ma na mapie, bo jest pod pokładem,
    // a podpis ma iść za tym, co widać.
    const afloat = this.aboard ? this.ships?.spotOf(this.aboard) : undefined;
    this.nameTags?.update(
      this.camera,
      afloat && this.joined
        ? [
            {
              playerId: this.joined.playerId,
              username: this.session.username,
              position: afloat,
            },
          ]
        : []
    );
    // Paski nad mostami w budowie — obok pasków budynków, tym samym wyglądem.
    this.progress?.update(this.camera, [
      ...this.works.filter((site) => site.bridge),
      // Kadłub na pochylni dostaje pasek tym samym kanałem, co most: budowa
      // spoza pola budynków, z tym samym kształtem odpowiedzi.
      ...(this.ships?.raising() ?? []),
    ]);
    this.pops?.update(this.camera);
    this.tip?.update(this.camera);
  }

  /**
   * Handle window resize. The buffer is sized from the window, so a resize
   * changes the resolution, not just how the picture is stretched.
   */
  onResize(): void {
    this.applyResolution();
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.stop();

    if (this.roadClock !== undefined) clearTimeout(this.roadClock);

    window.removeEventListener('keydown', this.onKeyDown);
    this.renderer.domElement.removeEventListener('mousedown', this.onPressed);
    this.renderer.domElement.removeEventListener('mouseup', this.onReleased);

    this.connection?.close();
    this.nameTags?.dispose();
    this.fellingTrees?.dispose();
    this.status?.dispose();
    this.markers?.dispose();
    this.route?.dispose();
    this.chosen?.dispose();
    this.preview?.dispose();
    this.mark?.dispose();
    this.hud?.dispose();
    this.picker?.dispose();
    this.highlight?.dispose();
    this.compass?.dispose();
    this.panel?.dispose();
    this.haul?.dispose();
    this.bar?.dispose();
    this.convoys?.dispose();
    this.ships?.dispose();
    this.talk?.dispose();
    this.spot?.dispose();
    this.store?.dispose();
    this.ship?.dispose();
    this.purse?.dispose();
    this.progress?.dispose();
    this.pops?.dispose();
    this.tip?.dispose();
    this.menu?.dispose();
    this.pause?.dispose();
    this.clouds?.dispose();
    this.rain?.dispose();
    this.smoke?.dispose();
    this.roadWorks?.dispose();
    this.hearths?.dispose();
    this.audio?.dispose();
    this.tracer?.dispose();
    this.buildGhost?.dispose();
    this.buildings?.dispose();
    this.sun?.dispose();
    this.cameraController.dispose();

    // Dispose geometries, materials, textures
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach(mat => mat.dispose());
        } else {
          object.material.dispose();
        }
      }
    });

    this.renderer.dispose();
    console.log('🧹 Game resources cleaned up');
  }
}
